import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { MeshBVH } from 'three-mesh-bvh';
import { Evaluator, Brush, SUBTRACTION } from 'three-bvh-csg';
import {
  AmbientLight,
  DirectionalLight,
  MeshPhongMaterial,
  Mesh,
  Box3,
  Vector3,
  DoubleSide,
  Color,
  TextureLoader,
  Plane,
  Raycaster,
  Vector2,
  CatmullRomCurve3,
  TubeGeometry,
  Quaternion,
  Matrix3,
  Matrix4,
  BufferGeometry,
  BufferAttribute,
  Float32BufferAttribute,
  CircleGeometry,
  Triangle,
} from 'three';

// Lazy-load Google's Manifold (https://github.com/elalish/manifold) — proven
// 3D-printing-grade boolean library that guarantees manifold output. We load
// the WASM module on demand (first hole cut) and cache it for subsequent runs.
let manifoldModulePromise = null;
const getManifoldModule = () => {
  if (!manifoldModulePromise) {
    manifoldModulePromise = import('manifold-3d').then(async (m) => {
      const Module = m.default || m;
      const wasm = await Module();
      wasm.setup();
      return wasm;
    });
  }
  return manifoldModulePromise;
};

// ============================================================
// Compute cross-section of mesh with an arbitrary plane.
// Returns array of Vector3 points forming the intersection contour.
// ============================================================
function computeCrossSection(model, plane) {
  const points = [];

  model.traverse((child) => {
    if (!child.isMesh || !child.geometry) return;
    const geo = child.geometry;
    const posAttr = geo.attributes.position;
    const index = geo.index;
    if (!posAttr) return;

    const wm = child.matrixWorld;
    const triCount = index ? index.count / 3 : posAttr.count / 3;
    const vA = new Vector3(), vB = new Vector3(), vC = new Vector3();

    for (let i = 0; i < triCount; i++) {
      let a, b, c;
      if (index) { a = index.getX(i*3); b = index.getX(i*3+1); c = index.getX(i*3+2); }
      else { a = i*3; b = i*3+1; c = i*3+2; }

      vA.fromBufferAttribute(posAttr, a).applyMatrix4(wm);
      vB.fromBufferAttribute(posAttr, b).applyMatrix4(wm);
      vC.fromBufferAttribute(posAttr, c).applyMatrix4(wm);

      const dA = plane.distanceToPoint(vA);
      const dB = plane.distanceToPoint(vB);
      const dC = plane.distanceToPoint(vC);

      const intersections = [];
      if (dA * dB < 0) intersections.push(new Vector3().lerpVectors(vA, vB, dA / (dA - dB)));
      if (dB * dC < 0) intersections.push(new Vector3().lerpVectors(vB, vC, dB / (dB - dC)));
      if (dC * dA < 0) intersections.push(new Vector3().lerpVectors(vC, vA, dC / (dC - dA)));
      if (Math.abs(dA) < 0.001) intersections.push(vA.clone());
      if (Math.abs(dB) < 0.001) intersections.push(vB.clone());
      if (Math.abs(dC) < 0.001) intersections.push(vC.clone());

      if (intersections.length >= 2) {
        points.push(intersections[0], intersections[1]);
      }
    }
  });

  return orderCrossSectionPoints(points);
}

// Order segment pairs into continuous loops, return the longest
function orderCrossSectionPoints(pairs) {
  if (pairs.length < 4) return [];

  const segments = [];
  for (let i = 0; i < pairs.length - 1; i += 2) {
    segments.push([pairs[i], pairs[i + 1]]);
  }
  if (segments.length === 0) return [];

  let totalLen = 0;
  for (const [a, b] of segments) totalLen += a.distanceTo(b);
  const threshold = (totalLen / segments.length) * 2.0;

  const used = new Set();
  const loops = [];

  for (let startIdx = 0; startIdx < segments.length; startIdx++) {
    if (used.has(startIdx)) continue;
    const loop = [segments[startIdx][0], segments[startIdx][1]];
    const loopUsed = new Set([startIdx]);

    // Grow forward
    let changed = true;
    while (changed) {
      changed = false;
      let bestDist = threshold, bestIdx = -1, bestReverse = false;
      const last = loop[loop.length - 1];
      for (let i = 0; i < segments.length; i++) {
        if (loopUsed.has(i)) continue;
        const dA = last.distanceTo(segments[i][0]);
        const dB = last.distanceTo(segments[i][1]);
        if (dA < bestDist) { bestDist = dA; bestIdx = i; bestReverse = false; }
        if (dB < bestDist) { bestDist = dB; bestIdx = i; bestReverse = true; }
      }
      if (bestIdx >= 0) {
        loop.push(bestReverse ? segments[bestIdx][0] : segments[bestIdx][1]);
        loopUsed.add(bestIdx);
        changed = true;
      }
    }
    // Grow backward
    changed = true;
    while (changed) {
      changed = false;
      let bestDist = threshold, bestIdx = -1, bestReverse = false;
      const first = loop[0];
      for (let i = 0; i < segments.length; i++) {
        if (loopUsed.has(i)) continue;
        const dA = first.distanceTo(segments[i][1]);
        const dB = first.distanceTo(segments[i][0]);
        if (dA < bestDist) { bestDist = dA; bestIdx = i; bestReverse = false; }
        if (dB < bestDist) { bestDist = dB; bestIdx = i; bestReverse = true; }
      }
      if (bestIdx >= 0) {
        loop.unshift(bestReverse ? segments[bestIdx][1] : segments[bestIdx][0]);
        loopUsed.add(bestIdx);
        changed = true;
      }
    }

    for (const idx of loopUsed) used.add(idx);
    if (loop.length >= 3) loops.push(loop);
  }

  if (loops.length === 0) return [];
  // Default: return longest loop
  loops.sort((a, b) => b.length - a.length);
  return loops[0];
}

// Find the loop that is closest to a specific point
function findLoopNearPoint(model, plane, targetPointInput) {
  const targetPoint = targetPointInput instanceof Vector3 ? targetPointInput : new Vector3(targetPointInput.x, targetPointInput.y, targetPointInput.z);
  const points = [];

  model.traverse((child) => {
    if (!child.isMesh || !child.geometry) return;
    const geo = child.geometry;
    const posAttr = geo.attributes.position;
    const index = geo.index;
    if (!posAttr) return;

    const wm = child.matrixWorld;
    const triCount = index ? index.count / 3 : posAttr.count / 3;
    const vA = new Vector3(), vB = new Vector3(), vC = new Vector3();

    for (let i = 0; i < triCount; i++) {
      let a, b, c;
      if (index) { a = index.getX(i*3); b = index.getX(i*3+1); c = index.getX(i*3+2); }
      else { a = i*3; b = i*3+1; c = i*3+2; }

      vA.fromBufferAttribute(posAttr, a).applyMatrix4(wm);
      vB.fromBufferAttribute(posAttr, b).applyMatrix4(wm);
      vC.fromBufferAttribute(posAttr, c).applyMatrix4(wm);

      const dA = plane.distanceToPoint(vA);
      const dB = plane.distanceToPoint(vB);
      const dC = plane.distanceToPoint(vC);

      const intersections = [];
      if (dA * dB < 0) intersections.push(new Vector3().lerpVectors(vA, vB, dA / (dA - dB)));
      if (dB * dC < 0) intersections.push(new Vector3().lerpVectors(vB, vC, dB / (dB - dC)));
      if (dC * dA < 0) intersections.push(new Vector3().lerpVectors(vC, vA, dC / (dC - dA)));
      if (Math.abs(dA) < 0.001) intersections.push(vA.clone());
      if (Math.abs(dB) < 0.001) intersections.push(vB.clone());
      if (Math.abs(dC) < 0.001) intersections.push(vC.clone());

      if (intersections.length >= 2) {
        points.push(intersections[0], intersections[1]);
      }
    }
  });

  if (points.length < 4) return [];

  // Build all loops
  const segments = [];
  for (let i = 0; i < points.length - 1; i += 2) segments.push([points[i], points[i + 1]]);
  if (segments.length === 0) return [];

  let totalLen = 0;
  for (const [a, b] of segments) totalLen += a.distanceTo(b);
  const threshold = (totalLen / segments.length) * 2.0;

  const used = new Set();
  const loops = [];

  for (let startIdx = 0; startIdx < segments.length; startIdx++) {
    if (used.has(startIdx)) continue;
    const loop = [segments[startIdx][0], segments[startIdx][1]];
    const loopUsed = new Set([startIdx]);

    let changed = true;
    while (changed) {
      changed = false;
      let bestDist = threshold, bestIdx = -1, bestReverse = false;
      const last = loop[loop.length - 1];
      for (let i = 0; i < segments.length; i++) {
        if (loopUsed.has(i)) continue;
        const dA = last.distanceTo(segments[i][0]);
        const dB = last.distanceTo(segments[i][1]);
        if (dA < bestDist) { bestDist = dA; bestIdx = i; bestReverse = false; }
        if (dB < bestDist) { bestDist = dB; bestIdx = i; bestReverse = true; }
      }
      if (bestIdx >= 0) {
        loop.push(bestReverse ? segments[bestIdx][0] : segments[bestIdx][1]);
        loopUsed.add(bestIdx);
        changed = true;
      }
    }
    changed = true;
    while (changed) {
      changed = false;
      let bestDist = threshold, bestIdx = -1, bestReverse = false;
      const first = loop[0];
      for (let i = 0; i < segments.length; i++) {
        if (loopUsed.has(i)) continue;
        const dA = first.distanceTo(segments[i][1]);
        const dB = first.distanceTo(segments[i][0]);
        if (dA < bestDist) { bestDist = dA; bestIdx = i; bestReverse = false; }
        if (dB < bestDist) { bestDist = dB; bestIdx = i; bestReverse = true; }
      }
      if (bestIdx >= 0) {
        loop.unshift(bestReverse ? segments[bestIdx][1] : segments[bestIdx][0]);
        loopUsed.add(bestIdx);
        changed = true;
      }
    }
    for (const idx of loopUsed) used.add(idx);
    if (loop.length >= 3) loops.push(loop);
  }

  if (loops.length === 0) return [];

  // Project targetPoint onto the plane to get 2D coords for containment test
  const planeNormal = plane.normal;

  // Build a local 2D coordinate system on the plane
  let uAxis = new Vector3(1, 0, 0);
  if (Math.abs(planeNormal.dot(uAxis)) > 0.9) uAxis = new Vector3(0, 1, 0);
  uAxis.cross(planeNormal).normalize();
  const vAxis = new Vector3().crossVectors(planeNormal, uAxis).normalize();

  // Project target point to 2D
  const targetProjected = plane.projectPoint(targetPoint, new Vector3());
  const target2D = { x: targetProjected.dot(uAxis), y: targetProjected.dot(vAxis) };

  // For each loop: check if target is "inside" the loop (winding number test in 2D)
  // and compute path length
  const containingLoops = [];
  const allLoopsWithDist = [];

  for (const loop of loops) {
    // Project loop to 2D
    const pts2D = loop.map(pt => {
      const proj = plane.projectPoint(pt, new Vector3());
      return { x: proj.dot(uAxis), y: proj.dot(vAxis) };
    });

    // Winding number test
    let winding = 0;
    for (let i = 0; i < pts2D.length; i++) {
      const a = pts2D[i];
      const b = pts2D[(i + 1) % pts2D.length];
      if (a.y <= target2D.y) {
        if (b.y > target2D.y) {
          const cross = (b.x - a.x) * (target2D.y - a.y) - (target2D.x - a.x) * (b.y - a.y);
          if (cross > 0) winding++;
        }
      } else {
        if (b.y <= target2D.y) {
          const cross = (b.x - a.x) * (target2D.y - a.y) - (target2D.x - a.x) * (b.y - a.y);
          if (cross < 0) winding--;
        }
      }
    }

    const len = pathLength(loop);

    if (winding !== 0) {
      containingLoops.push({ loop, len });
    }

    // Also track distance for fallback
    let minDist = Infinity;
    for (const pt of loop) {
      const d = pt.distanceTo(targetPoint);
      if (d < minDist) minDist = d;
    }
    allLoopsWithDist.push({ loop, len, minDist });
  }

  // Prefer: smallest loop that CONTAINS the point
  if (containingLoops.length > 0) {
    containingLoops.sort((a, b) => a.len - b.len);
    return containingLoops[0].loop;
  }

  // Fallback: closest loop to point, then smallest
  allLoopsWithDist.sort((a, b) => a.minDist - b.minDist);
  // Among the closest ones (within 2x of best distance), pick smallest
  const closestDist = allLoopsWithDist[0].minDist;
  const nearLoops = allLoopsWithDist.filter(l => l.minDist < closestDist * 2);
  nearLoops.sort((a, b) => a.len - b.len);
  return nearLoops[0].loop;
}

// Compute total path length of a point array
function pathLength(pts) {
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += pts[i].distanceTo(pts[i - 1]);
  if (pts.length > 2) len += pts[pts.length - 1].distanceTo(pts[0]);
  return len;
}

// Create a plane from a point and a normal vector
// Accepts both Vector3 and plain {x,y,z} objects
function makePlane(point, normal) {
  const p = point instanceof Vector3 ? point : new Vector3(point.x, point.y, point.z);
  const n = normal instanceof Vector3 ? normal.clone().normalize() : new Vector3(normal.x, normal.y, normal.z).normalize();
  return new Plane(n, -n.dot(p));
}

// Create a tilted plane at Y position
function makeCutPlane(y, tiltX, tiltZ, center) {
  const normal = new Vector3(0, 1, 0);
  if (tiltX) {
    const c = Math.cos(tiltX), s = Math.sin(tiltX);
    const ny = normal.y * c - normal.z * s;
    normal.z = normal.y * s + normal.z * c;
    normal.y = ny;
  }
  if (tiltZ) {
    const c = Math.cos(tiltZ), s = Math.sin(tiltZ);
    const nx = normal.x * c - normal.y * s;
    normal.y = normal.x * s + normal.y * c;
    normal.x = nx;
  }
  normal.normalize();
  return makePlane(new Vector3(center.x, y, center.z), normal);
}

// Find the plane orientation through clickPoint that gives the smallest
// cross-section loop CONTAINING the clickPoint.
// All tested planes go through clickPoint. The loop nearest to clickPoint is chosen.
function findMinCircumferencePlane(model, clickPoint, initialNormal, searchRange = 0.5, steps = 4) {
  let bestNormal = initialNormal.clone();
  let bestLength = Infinity;

  for (let tx = -searchRange; tx <= searchRange; tx += searchRange / steps) {
    for (let tz = -searchRange; tz <= searchRange; tz += searchRange / steps) {
      const n = initialNormal.clone();
      if (tx !== 0) {
        const c = Math.cos(tx), s = Math.sin(tx);
        const ny = n.y * c - n.z * s;
        n.z = n.y * s + n.z * c;
        n.y = ny;
      }
      if (tz !== 0) {
        const c = Math.cos(tz), s = Math.sin(tz);
        const nx = n.x * c - n.y * s;
        n.y = n.x * s + n.y * c;
        n.x = nx;
      }
      n.normalize();

      // Plane always goes through the exact clickPoint
      const plane = makePlane(clickPoint, n);
      // Find the loop closest to the click point
      const loop = findLoopNearPoint(model, plane, clickPoint);
      if (loop.length < 3) continue;

      const len = pathLength(loop);
      if (len < bestLength) {
        bestLength = len;
        bestNormal = n.clone();
      }
    }
  }

  return { normal: bestNormal, circumference: bestLength };
}

// Adjust a point+normal by offset and tilt (for slider fine-tuning after click placement)
function adjustPointNormal(point, normal, offsetY, tiltX, tiltZ) {
  const p = point instanceof Vector3 ? point.clone() : new Vector3(point.x, point.y, point.z);
  let nx = normal.x != null ? normal.x : 0;
  let ny = normal.y != null ? normal.y : 1;
  let nz = normal.z != null ? normal.z : 0;

  if (offsetY) p.y += offsetY;

  if (tiltX) {
    const c = Math.cos(tiltX), s = Math.sin(tiltX);
    const newY = ny * c - nz * s;
    nz = ny * s + nz * c;
    ny = newY;
  }
  if (tiltZ) {
    const c = Math.cos(tiltZ), s = Math.sin(tiltZ);
    const newX = nx * c - ny * s;
    ny = nx * s + ny * c;
    nx = newX;
  }

  const len = Math.sqrt(nx*nx + ny*ny + nz*nz) || 1;
  return {
    point: p,
    normal: new Vector3(nx/len, ny/len, nz/len),
  };
}

// ============================================================
// CrossSectionRing — renders thick tube along intersection contour
// Accepts either (planeY + tiltX/tiltZ + center) OR (planePoint + planeNormal)
// ============================================================
const CrossSectionRing = React.memo(({ model, planeY, tiltX, tiltZ, center, planePoint, planeNormal, color, tubeRadius }) => {
  const meshData = useMemo(() => {
    if (!model) return null;

    let pts;
    if (planePoint && planeNormal) {
      // Use findLoopNearPoint to get the loop closest to the click point
      const plane = makePlane(planePoint, planeNormal);
      pts = findLoopNearPoint(model, plane, planePoint);
    } else if (planeY != null && center) {
      const plane = makeCutPlane(planeY, tiltX || 0, tiltZ || 0, center);
      pts = computeCrossSection(model, plane);
    } else {
      return null;
    }

    if (!pts || pts.length < 3) return null;

    pts.push(pts[0]);
    const curve = new CatmullRomCurve3(pts, true, 'catmullrom', 0.5);
    const radius = tubeRadius || 1.0;
    return new TubeGeometry(curve, Math.max(pts.length * 2, 64), radius, 8, true);
  }, [model, planeY, tiltX, tiltZ, center, planePoint, planeNormal, tubeRadius]);

  if (!meshData) return null;

  return (
    <mesh>
      <primitive object={meshData} attach="geometry" />
      <meshBasicMaterial color={color} transparent opacity={0.85} depthWrite={false} />
    </mesh>
  );
});
CrossSectionRing.displayName = 'CrossSectionRing';

// ============================================================
// MarkerTube — renders a polyline as a thin tube that follows the orthese
// outer surface (points are pushed outward along the radial-from-axis
// direction by `offsetDist`). Uses standard depth testing (no `depthTest=false`)
// so the tube is naturally hidden by the orthese mesh when viewed from the
// opposite side. polygonOffset prevents Z-fighting where the tube sits
// directly on the orthese skin.
// ============================================================
const MarkerTube = React.memo(({ points, radius, color, closed = false, opacity = 0.95, offsetCenter, offsetDir, offsetDist, renderOrder = 1500 }) => {
  const geo = useMemo(() => {
    if (!points || points.length < 2 || !offsetCenter || !offsetDir) return null;
    const pts = [];
    for (const p of points) {
      const v = new Vector3(p.x, p.y, p.z);
      const local = new Vector3().subVectors(v, offsetCenter);
      const proj = local.dot(offsetDir);
      const radial = local.sub(offsetDir.clone().multiplyScalar(proj));
      if (radial.length() > 0.01) {
        v.addScaledVector(radial.normalize(), offsetDist);
      }
      pts.push(v);
    }
    // Auto-detect a closed loop: if first ≈ last, drop the duplicate and
    // close the CatmullRomCurve3 properly (avoids a kink at the wrap point).
    let useClosed = closed;
    if (pts.length >= 3) {
      const first = pts[0];
      const last = pts[pts.length - 1];
      if (first.distanceToSquared(last) < 1e-4) {
        pts.pop();
        useClosed = true;
      }
    }
    if (pts.length < 2) return null;
    const curve = new CatmullRomCurve3(pts, useClosed, 'catmullrom', 0.5);
    const seg = Math.max(pts.length * 2, 32);
    return new TubeGeometry(curve, seg, radius, 6, useClosed);
  }, [points, radius, closed, offsetCenter, offsetDir, offsetDist]);
  if (!geo) return null;
  return (
    <mesh renderOrder={renderOrder}>
      <primitive object={geo} attach="geometry" />
      <meshBasicMaterial
        color={color}
        transparent={opacity < 1}
        opacity={opacity}
        depthWrite={false}
        polygonOffset
        polygonOffsetFactor={-2}
        polygonOffsetUnits={-2}
      />
    </mesh>
  );
});
MarkerTube.displayName = 'MarkerTube';

// ============================================================
// MarkerDot — small sphere placed on the orthese outer surface (point
// is pushed outward along the radial direction). Same depth-test behavior
// as MarkerTube — visible only on the side facing the camera.
// ============================================================
const MarkerDot = React.memo(({ point, radius, color, offsetCenter, offsetDir, offsetDist, renderOrder = 1500 }) => {
  const pos = useMemo(() => {
    if (!point || !offsetCenter || !offsetDir) return null;
    const v = new Vector3(point.x, point.y, point.z);
    const local = new Vector3().subVectors(v, offsetCenter);
    const proj = local.dot(offsetDir);
    const radial = local.sub(offsetDir.clone().multiplyScalar(proj));
    if (radial.length() > 0.01) {
      v.addScaledVector(radial.normalize(), offsetDist);
    }
    return v;
  }, [point, offsetCenter, offsetDir, offsetDist]);
  if (!pos) return null;
  return (
    <mesh position={[pos.x, pos.y, pos.z]} renderOrder={renderOrder}>
      <sphereGeometry args={[radius, 12, 12]} />
      <meshBasicMaterial color={color} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
    </mesh>
  );
});
MarkerDot.displayName = 'MarkerDot';

// ============================================================
// ThumbRingOptimized — auto-tilts the thumb normal to find smallest circumference
// ============================================================
const ThumbRingOptimized = React.memo(({ model, thumbPoint, thumbNormal, tubeRadius }) => {
  const optimized = useMemo(() => {
    if (!model || !thumbPoint || !thumbNormal) return null;
    const pt = thumbPoint instanceof Vector3 ? thumbPoint : new Vector3(thumbPoint.x, thumbPoint.y, thumbPoint.z);
    const n = thumbNormal instanceof Vector3 ? thumbNormal.clone() : new Vector3(thumbNormal.x, thumbNormal.y, thumbNormal.z);
    n.normalize();
    // Search tilt angles up to ~45° around the thumb axis normal for minimum circumference
    const result = findMinCircumferencePlane(model, pt, n, 0.6, 5);
    return { point: pt, normal: result.normal };
  }, [model, thumbPoint?.x, thumbPoint?.y, thumbPoint?.z, thumbNormal?.x, thumbNormal?.y, thumbNormal?.z]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!optimized) return null;
  return <CrossSectionRing model={model} planePoint={optimized.point} planeNormal={optimized.normal} color="#22c55e" tubeRadius={tubeRadius} />;
});
ThumbRingOptimized.displayName = 'ThumbRingOptimized';

// ============================================================
// CutPlaneVisual — semi-transparent tilted plane
// ============================================================
const CutPlaneVisual = React.memo(({ y, tiltX, tiltZ, center, size, color }) => {
  const rotation = useMemo(() => [-Math.PI / 2 + (tiltX || 0), 0, tiltZ || 0], [tiltX, tiltZ]);
  return (
    <mesh position={[center.x, y, center.z]} rotation={rotation}>
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial color={color} transparent opacity={0.1} side={DoubleSide} depthWrite={false} />
    </mesh>
  );
});
CutPlaneVisual.displayName = 'CutPlaneVisual';

// ============================================================
// ShellPreview — offset surface between cut planes (orthosis shell)
// ============================================================
// Offset each shared vertex along its corrected normal.
// Faces stay connected because vertices are shared.
// Uses BFS winding fix for consistent normals.
function buildOffsetGeo(model, offset, flipNormals) {
  const meshes = [];
  model.traverse((child) => { if (child.isMesh && child.geometry) meshes.push(child); });

  // Collect world-space positions + face indices
  const pos = []; // flat [x,y,z,...]
  const faces = []; // [a,b,c, a,b,c, ...]
  let vBase = 0;

  for (const child of meshes) {
    const geo = child.geometry;
    if (!geo.attributes.position) continue;
    const pa = geo.attributes.position;
    const idx = geo.index;
    const wm = child.matrixWorld;
    const v = new Vector3();
    for (let i = 0; i < pa.count; i++) {
      v.fromBufferAttribute(pa, i).applyMatrix4(wm);
      pos.push(v.x, v.y, v.z);
    }
    const tc = idx ? idx.count / 3 : pa.count / 3;
    for (let t = 0; t < tc; t++) {
      faces.push(
        (idx ? idx.getX(t*3) : t*3) + vBase,
        (idx ? idx.getX(t*3+1) : t*3+1) + vBase,
        (idx ? idx.getX(t*3+2) : t*3+2) + vBase
      );
    }
    vBase += pa.count;
  }

  const rawNv = pos.length / 3;
  const rawNf = faces.length / 3;
  if (rawNv < 3 || rawNf < 1) return null;

  // --- Vertex Welding: merge duplicate vertices at same position ---
  // OBJ files often have duplicate vertices (different UVs/normals per face corner).
  // We must merge them so shared points get a single offset.
  const WELD_PRECISION = 1000; // round to 0.001
  const posKey = (i) => {
    const j = i * 3;
    return `${Math.round(pos[j]*WELD_PRECISION)}_${Math.round(pos[j+1]*WELD_PRECISION)}_${Math.round(pos[j+2]*WELD_PRECISION)}`;
  };

  const keyToNewIdx = new Map();
  const oldToNew = new Int32Array(rawNv);
  const weldedPos = [];
  let weldedCount = 0;

  for (let i = 0; i < rawNv; i++) {
    const key = posKey(i);
    if (keyToNewIdx.has(key)) {
      oldToNew[i] = keyToNewIdx.get(key);
    } else {
      const ni = weldedCount++;
      keyToNewIdx.set(key, ni);
      oldToNew[i] = ni;
      weldedPos.push(pos[i*3], pos[i*3+1], pos[i*3+2]);
    }
  }

  // Remap face indices to welded vertices
  for (let i = 0; i < faces.length; i++) {
    faces[i] = oldToNew[faces[i]];
  }

  // Use welded data
  const nv = weldedCount;
  const nf = rawNf;
  const wpos = weldedPos; // flat array [x,y,z,...]

  // Replace pos references with welded positions for the rest of the function
  const pos_w = new Float32Array(wpos);

  // --- BFS Winding Fix ---
  const edgeKey = (a, b) => a < b ? (a * nv + b) : (b * nv + a);
  const edgeMap = new Map(); // edgeKey → [faceIdx, ...]
  for (let f = 0; f < nf; f++) {
    const i = f * 3;
    const a = faces[i], b = faces[i+1], c = faces[i+2];
    for (const [e0, e1] of [[a,b],[b,c],[c,a]]) {
      const k = edgeKey(e0, e1);
      if (!edgeMap.has(k)) edgeMap.set(k, []);
      edgeMap.get(k).push(f);
    }
  }

  const visited = new Uint8Array(nf);
  visited[0] = 1;
  const queue = [0];

  while (queue.length > 0) {
    const fi = queue.shift();
    const i = fi * 3;
    const fa = faces[i], fb = faces[i+1], fc = faces[i+2];
    const edges = [[fa,fb],[fb,fc],[fc,fa]];

    for (const [e0, e1] of edges) {
      const k = edgeKey(e0, e1);
      const nbrs = edgeMap.get(k);
      if (!nbrs) continue;
      for (const ni of nbrs) {
        if (visited[ni]) continue;
        visited[ni] = 1;

        // Check if neighbor has same edge direction (needs flip)
        const nj = ni * 3;
        const na = faces[nj], nb = faces[nj+1], nc = faces[nj+2];
        // Find the shared edge in neighbor
        const nEdges = [[na,nb],[nb,nc],[nc,na]];
        for (const [ne0, ne1] of nEdges) {
          if ((ne0 === e0 && ne1 === e1) || (ne0 === e1 && ne1 === e0)) {
            if (ne0 === e0 && ne1 === e1) {
              // Same direction → flip neighbor
              faces[nj] = na; faces[nj+1] = nc; faces[nj+2] = nb;
            }
            break;
          }
        }
        queue.push(ni);
      }
    }
  }

  // --- Compute vertex normals from fixed winding ---
  const vnorm = new Float32Array(nv * 3);
  const vA = new Vector3(), vB = new Vector3(), vC = new Vector3();
  const e1 = new Vector3(), e2 = new Vector3(), fn = new Vector3();

  for (let f = 0; f < nf; f++) {
    const i = f * 3;
    const a = faces[i], b = faces[i+1], c = faces[i+2];
    vA.set(pos_w[a*3], pos_w[a*3+1], pos_w[a*3+2]);
    vB.set(pos_w[b*3], pos_w[b*3+1], pos_w[b*3+2]);
    vC.set(pos_w[c*3], pos_w[c*3+1], pos_w[c*3+2]);
    e1.subVectors(vB, vA);
    e2.subVectors(vC, vA);
    fn.crossVectors(e1, e2); // area-weighted, not normalized
    vnorm[a*3] += fn.x; vnorm[a*3+1] += fn.y; vnorm[a*3+2] += fn.z;
    vnorm[b*3] += fn.x; vnorm[b*3+1] += fn.y; vnorm[b*3+2] += fn.z;
    vnorm[c*3] += fn.x; vnorm[c*3+1] += fn.y; vnorm[c*3+2] += fn.z;
  }

  // Normalize
  for (let i = 0; i < nv; i++) {
    const j = i * 3;
    const len = Math.sqrt(vnorm[j]**2 + vnorm[j+1]**2 + vnorm[j+2]**2);
    if (len > 1e-7) { vnorm[j] /= len; vnorm[j+1] /= len; vnorm[j+2] /= len; }
  }

  // Ensure outward: majority dot(pos-center, normal) > 0
  const box = new Box3();
  for (let i = 0; i < nv; i++) box.expandByPoint(new Vector3(pos_w[i*3], pos_w[i*3+1], pos_w[i*3+2]));
  const ctr = new Vector3(); box.getCenter(ctr);
  let outCnt = 0;
  const ss = Math.max(1, Math.floor(nv / 500));
  for (let i = 0; i < nv; i += ss) {
    const j = i * 3;
    if ((pos_w[j]-ctr.x)*vnorm[j] + (pos_w[j+1]-ctr.y)*vnorm[j+1] + (pos_w[j+2]-ctr.z)*vnorm[j+2] > 0) outCnt++;
  }
  const shouldFlip = outCnt < Math.floor(nv / ss / 2);
  if (shouldFlip) {
    for (let i = 0; i < vnorm.length; i++) vnorm[i] = -vnorm[i];
  }

  // --- Offset vertices along their normal ---
  const outPos = new Float32Array(nv * 3);
  for (let i = 0; i < nv; i++) {
    const j = i * 3;
    outPos[j] = pos_w[j] + vnorm[j] * offset;
    outPos[j+1] = pos_w[j+1] + vnorm[j+1] * offset;
    outPos[j+2] = pos_w[j+2] + vnorm[j+2] * offset;
  }

  // --- Build indices (flip for inner face) ---
  const idxOut = [];
  for (let f = 0; f < nf; f++) {
    const i = f * 3;
    if (flipNormals !== shouldFlip) {
      idxOut.push(faces[i], faces[i+2], faces[i+1]);
    } else {
      idxOut.push(faces[i], faces[i+1], faces[i+2]);
    }
  }

  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(outPos, 3));
  g.setIndex(idxOut);
  g.computeVertexNormals();
  g.computeBoundingSphere();
  return g;
}

// Compute cross-section of a BufferGeometry with a plane
function computeCrossSectionFromGeo(geo, plane) {
  const posAttr = geo.attributes.position;
  const index = geo.index;
  if (!posAttr) return [];

  const points = [];
  const vA = new Vector3(), vB = new Vector3(), vC = new Vector3();
  const triCount = index ? index.count / 3 : posAttr.count / 3;

  for (let i = 0; i < triCount; i++) {
    const a = index ? index.getX(i*3) : i*3;
    const b = index ? index.getX(i*3+1) : i*3+1;
    const c = index ? index.getX(i*3+2) : i*3+2;

    vA.set(posAttr.getX(a), posAttr.getY(a), posAttr.getZ(a));
    vB.set(posAttr.getX(b), posAttr.getY(b), posAttr.getZ(b));
    vC.set(posAttr.getX(c), posAttr.getY(c), posAttr.getZ(c));

    const dA = plane.distanceToPoint(vA);
    const dB = plane.distanceToPoint(vB);
    const dC = plane.distanceToPoint(vC);

    const ints = [];
    if (dA * dB < 0) ints.push(new Vector3().lerpVectors(vA, vB, dA / (dA - dB)));
    if (dB * dC < 0) ints.push(new Vector3().lerpVectors(vB, vC, dB / (dB - dC)));
    if (dC * dA < 0) ints.push(new Vector3().lerpVectors(vC, vA, dC / (dC - dA)));

    if (ints.length >= 2) {
      points.push(ints[0], ints[1]);
    }
  }

  return orderCrossSectionPoints(points);
}

// Build a triangle strip (cap) connecting two loops
function buildCapBetweenLoops(outerLoop, innerLoop) {
  if (outerLoop.length < 3 || innerLoop.length < 3) return null;

  // Resample both loops to have the same number of points
  const numPts = Math.max(outerLoop.length, innerLoop.length);
  const resample = (loop, n) => {
    if (loop.length === n) return loop;
    const result = [];
    for (let i = 0; i < n; i++) {
      const t = i / n * loop.length;
      const idx = Math.floor(t);
      const frac = t - idx;
      const a = loop[idx % loop.length];
      const b = loop[(idx + 1) % loop.length];
      result.push(new Vector3().lerpVectors(a, b, frac));
    }
    return result;
  };

  const outer = resample(outerLoop, numPts);
  const inner = resample(innerLoop, numPts);

  // Align inner loop to outer: find the rotation offset that minimizes total distance
  let bestOffset = 0, bestDist = Infinity;
  const step = Math.max(1, Math.floor(numPts / 20));
  for (let off = 0; off < numPts; off += step) {
    let dist = 0;
    for (let i = 0; i < numPts; i += step) {
      dist += outer[i].distanceTo(inner[(i + off) % numPts]);
    }
    if (dist < bestDist) { bestDist = dist; bestOffset = off; }
  }
  // Fine search around best
  const fineStart = Math.max(0, bestOffset - step);
  const fineEnd = Math.min(numPts, bestOffset + step);
  for (let off = fineStart; off <= fineEnd; off++) {
    let dist = 0;
    for (let i = 0; i < numPts; i += Math.max(1, Math.floor(numPts / 50))) {
      dist += outer[i].distanceTo(inner[(i + off) % numPts]);
    }
    if (dist < bestDist) { bestDist = dist; bestOffset = off; }
  }

  // Build triangle strip
  const positions = [];
  const indices = [];

  for (let i = 0; i < numPts; i++) {
    const o = outer[i];
    const inn = inner[(i + bestOffset) % numPts];
    positions.push(o.x, o.y, o.z);
    positions.push(inn.x, inn.y, inn.z);
  }

  for (let i = 0; i < numPts; i++) {
    const oi = i * 2;       // outer vertex index
    const ii = i * 2 + 1;   // inner vertex index
    const oni = ((i + 1) % numPts) * 2;       // next outer
    const ini = ((i + 1) % numPts) * 2 + 1;   // next inner

    indices.push(oi, oni, ii);
    indices.push(ii, oni, ini);
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(new Float32Array(positions), 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  return geo;
}

// Trim a model's geometry by clipping planes, then build inner+outer offset.
// 1. Collect all world-space positions + faces from the model
// 2. Remove triangles where ALL 3 vertices are outside any clipping plane
// 3. Build outer and inner offset from the SAME trimmed mesh
// → Identical topology → boundary edges match perfectly for caps
function buildOffsetGeoPair(model, outerOffset, innerOffset, trimPlanes) {
  const meshes = [];
  model.traverse((child) => { if (child.isMesh && child.geometry) meshes.push(child); });

  // Collect world-space positions + face indices
  const allPos = [];
  const allFaces = [];
  let vertBase = 0;

  for (const child of meshes) {
    const geo = child.geometry;
    if (!geo.attributes.position) continue;
    const posAttr = geo.attributes.position;
    const index = geo.index;
    const wm = child.matrixWorld;
    const pv = new Vector3();

    for (let i = 0; i < posAttr.count; i++) {
      pv.fromBufferAttribute(posAttr, i).applyMatrix4(wm);
      allPos.push(pv.x, pv.y, pv.z);
    }
    const triCount = index ? index.count / 3 : posAttr.count / 3;
    for (let t = 0; t < triCount; t++) {
      allFaces.push(
        (index ? index.getX(t*3) : t*3) + vertBase,
        (index ? index.getX(t*3+1) : t*3+1) + vertBase,
        (index ? index.getX(t*3+2) : t*3+2) + vertBase
      );
    }
    vertBase += posAttr.count;
  }

  const numVerts = allPos.length / 3;
  let numFaces = allFaces.length / 3;
  if (numVerts < 3 || numFaces < 1) return { outerGeo: null, innerGeo: null };

  // --- Weld duplicate vertices ---
  const WELD_PRECISION = 1000;
  const posKey = (i) => `${Math.round(allPos[i*3]*WELD_PRECISION)}_${Math.round(allPos[i*3+1]*WELD_PRECISION)}_${Math.round(allPos[i*3+2]*WELD_PRECISION)}`;
  const keyToNew = new Map();
  const oldToNew = new Int32Array(numVerts);
  const weldedPos = [];
  let weldedCount = 0;
  for (let i = 0; i < numVerts; i++) {
    const k = posKey(i);
    if (keyToNew.has(k)) { oldToNew[i] = keyToNew.get(k); }
    else { const ni = weldedCount++; keyToNew.set(k, ni); oldToNew[i] = ni; weldedPos.push(allPos[i*3], allPos[i*3+1], allPos[i*3+2]); }
  }
  // Remap faces
  for (let i = 0; i < allFaces.length; i++) allFaces[i] = oldToNew[allFaces[i]];

  // --- Trim: remove faces where ALL 3 vertices are outside any plane ---
  const MARGIN = 15; // generous margin — GPU clipping handles the precise cut
  const keptFaces = [];
  const hasTrim = trimPlanes && trimPlanes.length > 0;

  for (let f = 0; f < numFaces; f++) {
    const a = allFaces[f*3], b = allFaces[f*3+1], c = allFaces[f*3+2];
    if (hasTrim) {
      let outside = false;
      for (const plane of trimPlanes) {
        const nx = plane.normal.x, ny = plane.normal.y, nz = plane.normal.z, pc = plane.constant;
        const dA = nx*weldedPos[a*3] + ny*weldedPos[a*3+1] + nz*weldedPos[a*3+2] + pc;
        const dB = nx*weldedPos[b*3] + ny*weldedPos[b*3+1] + nz*weldedPos[b*3+2] + pc;
        const dC = nx*weldedPos[c*3] + ny*weldedPos[c*3+1] + nz*weldedPos[c*3+2] + pc;
        if (dA < -MARGIN && dB < -MARGIN && dC < -MARGIN) { outside = true; break; }
      }
      if (outside) continue;
    }
    keptFaces.push(a, b, c);
  }

  numFaces = keptFaces.length / 3;
  if (numFaces < 1) return { outerGeo: null, innerGeo: null };

  // --- BFS winding fix on trimmed mesh ---
  const faces = keptFaces;
  const nv = weldedCount;
  const nf = numFaces;

  const edgeMap = new Map();
  const ek = (a, b) => a < b ? `${a}_${b}` : `${b}_${a}`;
  for (let fi = 0; fi < nf; fi++) {
    const i = fi * 3;
    for (const [e0, e1] of [[faces[i],faces[i+1]],[faces[i+1],faces[i+2]],[faces[i+2],faces[i]]]) {
      const k = ek(e0, e1);
      if (!edgeMap.has(k)) edgeMap.set(k, []);
      edgeMap.get(k).push(fi);
    }
  }
  const visited = new Uint8Array(nf);
  const queue = [0];
  visited[0] = 1;
  while (queue.length > 0) {
    const fi = queue.shift();
    const fj = fi * 3;
    const edges = [[faces[fj],faces[fj+1]],[faces[fj+1],faces[fj+2]],[faces[fj+2],faces[fj]]];
    for (const [e0, e1] of edges) {
      const nbrs = edgeMap.get(ek(e0, e1));
      if (!nbrs) continue;
      for (const ni of nbrs) {
        if (visited[ni]) continue;
        visited[ni] = 1;
        const nj = ni * 3;
        const na = faces[nj], nb = faces[nj+1], nc = faces[nj+2];
        for (const [ne0, ne1] of [[na,nb],[nb,nc],[nc,na]]) {
          if ((ne0 === e0 && ne1 === e1) || (ne0 === e1 && ne1 === e0)) {
            if (ne0 === e0 && ne1 === e1) { faces[nj] = na; faces[nj+1] = nc; faces[nj+2] = nb; }
            break;
          }
        }
        queue.push(ni);
      }
    }
  }

  // --- Compute vertex normals ---
  const vnorm = new Float32Array(nv * 3);
  const vA = new Vector3(), vB = new Vector3(), vC = new Vector3();
  const e1 = new Vector3(), e2 = new Vector3(), fn = new Vector3();
  for (let f = 0; f < nf; f++) {
    const a = faces[f*3], b = faces[f*3+1], c = faces[f*3+2];
    vA.set(weldedPos[a*3], weldedPos[a*3+1], weldedPos[a*3+2]);
    vB.set(weldedPos[b*3], weldedPos[b*3+1], weldedPos[b*3+2]);
    vC.set(weldedPos[c*3], weldedPos[c*3+1], weldedPos[c*3+2]);
    e1.subVectors(vB, vA); e2.subVectors(vC, vA); fn.crossVectors(e1, e2);
    vnorm[a*3] += fn.x; vnorm[a*3+1] += fn.y; vnorm[a*3+2] += fn.z;
    vnorm[b*3] += fn.x; vnorm[b*3+1] += fn.y; vnorm[b*3+2] += fn.z;
    vnorm[c*3] += fn.x; vnorm[c*3+1] += fn.y; vnorm[c*3+2] += fn.z;
  }
  for (let i = 0; i < nv; i++) {
    const j = i*3;
    const len = Math.sqrt(vnorm[j]**2 + vnorm[j+1]**2 + vnorm[j+2]**2);
    if (len > 1e-6) { vnorm[j] /= len; vnorm[j+1] /= len; vnorm[j+2] /= len; }
  }

  // --- Check outward direction ---
  const box = new Box3();
  for (let i = 0; i < nv; i++) box.expandByPoint(new Vector3(weldedPos[i*3], weldedPos[i*3+1], weldedPos[i*3+2]));
  const center = new Vector3(); box.getCenter(center);
  let outCnt = 0, sampCnt = 0;
  const step = Math.max(1, Math.floor(nv / 500));
  for (let i = 0; i < nv; i += step) {
    const j = i*3;
    const dot = (weldedPos[j]-center.x)*vnorm[j] + (weldedPos[j+1]-center.y)*vnorm[j+1] + (weldedPos[j+2]-center.z)*vnorm[j+2];
    if (dot > 0) outCnt++;
    sampCnt++;
  }
  const shouldFlip = outCnt < sampCnt / 2;

  // --- Build outer and inner offset positions from the SAME trimmed mesh ---
  const buildGeo = (offset, flipNormals) => {
    const outPos = new Float32Array(nv * 3);
    for (let i = 0; i < nv; i++) {
      const j = i*3;
      let nx = vnorm[j], ny = vnorm[j+1], nz = vnorm[j+2];
      if (shouldFlip) { nx = -nx; ny = -ny; nz = -nz; }
      outPos[j] = weldedPos[j] + nx * offset;
      outPos[j+1] = weldedPos[j+1] + ny * offset;
      outPos[j+2] = weldedPos[j+2] + nz * offset;
    }
    const idxOut = [];
    for (let f = 0; f < nf; f++) {
      const i = f*3;
      if (flipNormals !== shouldFlip) { idxOut.push(faces[i], faces[i+2], faces[i+1]); }
      else { idxOut.push(faces[i], faces[i+1], faces[i+2]); }
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(outPos, 3));
    g.setIndex(idxOut);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  };

  return {
    outerGeo: buildGeo(outerOffset, false),
    innerGeo: buildGeo(innerOffset, true),
  };
}

const ShellPreview = React.memo(({ model, upperCutY, lowerCutY, upperPoint, upperNormal, upperTiltX, upperTiltZ, lowerPoint, lowerNormal, lowerTiltX, lowerTiltZ, skinOffset, wallThickness, thumbPoint, thumbNormal, thumbMode, slitRotation, slitWidth, slitTilt, slitMarkerRotation, slitMarkerWidth, slitClickPoint, slitClickPoint2, closureCount, velcroGeo, armAxisCenter, armAxisDir, color, onGeosReady, holePatternEnabled, holesPerRow, holeSpacing, holePatternApplied, onCutProgress, onPlanProgress }) => {
  // Stable refs to the parent's progress callbacks so the heavy effects below
  // don't have to re-run when the callback identity changes.
  const onCutProgressRef = useRef(onCutProgress);
  useEffect(() => { onCutProgressRef.current = onCutProgress; }, [onCutProgress]);
  const onPlanProgressRef = useRef(onPlanProgress);
  useEffect(() => { onPlanProgressRef.current = onPlanProgress; }, [onPlanProgress]);
  const innerOffset = skinOffset || 0.3;
  const outerOffset = innerOffset + (wallThickness || 2.5);

  // Build clipping planes — split into arm (upper+lower) and thumb
  const { armPlanes, thumbPlane, allPlanes } = useMemo(() => {
    const arm = [];
    const cx = model ? (() => { const b = new Box3().setFromObject(model); return { x: (b.min.x+b.max.x)/2, z: (b.min.z+b.max.z)/2 }; })() : { x: 0, z: 0 };

    if (upperCutY != null) {
      const baseUpper = upperPoint || { x: cx.x, y: 0, z: cx.z };
      const pt = { x: baseUpper.x, y: upperCutY, z: baseUpper.z };
      const adj = adjustPointNormal(
        pt,
        upperNormal || { x: 0, y: 1, z: 0 },
        0, upperTiltX || 0, upperTiltZ || 0
      );
      const n = new Vector3(-adj.normal.x, -adj.normal.y, -adj.normal.z);
      arm.push(new Plane(n, -n.dot(adj.point)));
    }

    if (lowerCutY != null) {
      const baseLower = lowerPoint || { x: cx.x, y: 0, z: cx.z };
      const pt = { x: baseLower.x, y: lowerCutY, z: baseLower.z };
      const adj = adjustPointNormal(
        pt,
        lowerNormal || { x: 0, y: 1, z: 0 },
        0, lowerTiltX || 0, lowerTiltZ || 0
      );
      const n = new Vector3(adj.normal.x, adj.normal.y, adj.normal.z);
      arm.push(new Plane(n, -n.dot(adj.point)));
    }

    let thumb = null;
    if (thumbPoint && thumbNormal) {
      const tp = thumbPoint instanceof Vector3 ? thumbPoint : new Vector3(thumbPoint.x, thumbPoint.y, thumbPoint.z);
      const tn = thumbNormal instanceof Vector3 ? thumbNormal.clone() : new Vector3(thumbNormal.x, thumbNormal.y, thumbNormal.z);
      tn.normalize();

      const optimized = findMinCircumferencePlane(model, tp, tn, 0.6, 5);
      const optN = optimized.normal.clone();

      const modelBox = new Box3().setFromObject(model);
      const center = new Vector3();
      modelBox.getCenter(center);
      if (optN.dot(new Vector3().subVectors(center, tp)) < 0) optN.negate();
      thumb = new Plane(optN, -optN.dot(tp));
    }

    return {
      armPlanes: arm,
      thumbPlane: thumb,
      allPlanes: thumb ? [...arm, thumb] : arm,
    };
  }, [model, upperCutY, lowerCutY, upperPoint, upperNormal, upperTiltX, upperTiltZ, lowerPoint, lowerNormal, lowerTiltX, lowerTiltZ, thumbPoint, thumbNormal, thumbMode]);

  // Build slit planes: 2 vertical planes forming a wedge cut along the arm axis
  // Build slit planes: 2 vertical planes through model center.
  // Sequential clipping keeps the INTERSECTION of positive half-spaces.
  // To REMOVE a wedge, we can't clip both planes sequentially.
  // Instead: each plane's normal points AWAY from slit center (into the shell).
  // We clip the shell separately with each plane, producing two halves,
  // then merge them → the slit gap is where neither half has geometry.
  // Compute slit axis from cross-section centers of upper and lower cut planes.
  // This aligns the slit exactly to the orthesis bounds.
  const slitAxis = useMemo(() => {
    if (!model || armPlanes.length < 2) return null;

    const findCrossCenter = (plane) => {
      const loop = computeCrossSection(model, plane);
      if (loop.length < 3) return null;
      const cx = loop.reduce((s, p) => s + p.x, 0) / loop.length;
      const cy = loop.reduce((s, p) => s + p.y, 0) / loop.length;
      const cz = loop.reduce((s, p) => s + p.z, 0) / loop.length;
      return new Vector3(cx, cy, cz);
    };

    const upperCenter = findCrossCenter(armPlanes[0]);
    const lowerCenter = findCrossCenter(armPlanes[1]);
    if (!upperCenter || !lowerCenter) return null;

    const center = new Vector3().addVectors(upperCenter, lowerCenter).multiplyScalar(0.5);
    const dir = new Vector3().subVectors(upperCenter, lowerCenter).normalize();
    return { center, dir, upper: upperCenter, lower: lowerCenter };
  }, [model, armPlanes]);

  // Build local coordinate frame from slit axis (or fallback to arm axis / Y-up)
  const armFrame = useMemo(() => {
    const axisY = slitAxis ? slitAxis.dir.clone() : (armAxisDir ? armAxisDir.clone().normalize() : new Vector3(0, 1, 0));
    const up = Math.abs(axisY.dot(new Vector3(1, 0, 0))) < 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 0, 1);
    const axisX = new Vector3().crossVectors(axisY, up).normalize();
    const axisZ = new Vector3().crossVectors(axisX, axisY).normalize();
    return { axisX, axisY, axisZ };
  }, [slitAxis, armAxisDir]);

  // Build slit planes from two click points (A=position, B=tilt direction).
  // An imaginary center plane goes through both A and B.
  // The two cutting planes are parallel to this center plane,
  // shifted left/right by width/2 from point A.
  const buildSlitPlanesFromPoints = (ptA, ptB, width) => {
    if (!ptA || !model) return [];
    const box = new Box3().setFromObject(model);
    const center = slitAxis ? slitAxis.center.clone() : (armAxisCenter ? armAxisCenter.clone() : (() => { const c = new Vector3(); box.getCenter(c); return c; })());
    const axisDir = slitAxis ? slitAxis.dir : new Vector3(0, 1, 0);

    // Slit line direction: A→B if B exists, otherwise along arm axis
    const slitLineDir = ptB
      ? new Vector3().subVectors(ptB, ptA).normalize()
      : axisDir.clone();

    // Radial direction: from axis to point A (perpendicular to axis)
    const toA = new Vector3().subVectors(ptA, center);
    toA.sub(axisDir.clone().multiplyScalar(toA.dot(axisDir)));
    const radialDir = toA.length() > 0.01 ? toA.clone().normalize() : armFrame.axisX.clone();

    // Side direction: perpendicular to both slit line and radial
    // This is the direction the planes are offset in
    const sideDir = new Vector3().crossVectors(slitLineDir, radialDir).normalize();

    // Each plane normal points OUTWARD (away from slit center).
    // clipGeo keeps positive side = the shell on that side of the cut.
    // We clip SEPARATELY with each plane, then MERGE (union) the results.
    // This removes the strip between the two planes.
    const halfW = width / 2;
    const planes = [];
    for (const side of [-1, 1]) {
      const n = sideDir.clone().multiplyScalar(side); // OUTWARD from slit
      const planePt = ptA.clone().addScaledVector(sideDir, side * halfW);
      planes.push(new Plane(n, -n.dot(planePt)));
    }
    return planes;
  };

  // Fallback for slider-only mode (no click points)
  const buildSlitPlanesFromAngle = (angle, width) => {
    if (angle == null || width == null || !model) return [];
    const box = new Box3().setFromObject(model);
    const center = slitAxis ? slitAxis.center.clone() : (() => { const c = new Vector3(); box.getCenter(c); return c; })();
    const { axisX, axisY, axisZ } = armFrame;
    const radius = Math.max(box.max.x - box.min.x, box.max.z - box.min.z) / 2;
    const halfAngle = Math.atan2(width / 2, radius);
    const planes = [];
    for (const side of [-1, 1]) {
      const ea = angle + side * halfAngle;
      const n = new Vector3()
        .addScaledVector(axisX, -Math.sin(ea) * side)
        .addScaledVector(axisZ, Math.cos(ea) * side)
        .normalize();
      planes.push(new Plane(n, -n.dot(center)));
    }
    return planes;
  };

  const slitPlanes = useMemo(() => {
    if (slitWidth == null) return [];
    if (slitClickPoint) {
      return buildSlitPlanesFromPoints(slitClickPoint, slitClickPoint2, slitWidth);
    }
    if (slitRotation != null) {
      return buildSlitPlanesFromAngle(slitRotation, slitWidth);
    }
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slitClickPoint, slitClickPoint2, slitRotation, slitWidth, model, armFrame, slitAxis]);

  // Build offset geos (no pre-trim — software clipping handles everything)
  // MOVED ABOVE closureGeos so the closure clipping can use innerGeo via BVH.
  const { outerGeo, innerGeo } = useMemo(() => {
    if (!model) return { outerGeo: null, innerGeo: null };
    return buildOffsetGeoPair(model, outerOffset, innerOffset, []);
  }, [model, outerOffset, innerOffset]);

  // BVH over the inner shell mesh — enables fast per-vertex SDF queries so
  // each connector foot can be clipped at the actual curved inner surface
  // (not a flat approximation plane).
  const innerBVH = useMemo(() => {
    if (!innerGeo) return null;
    return new MeshBVH(innerGeo);
  }, [innerGeo]);
  // Outer-shell BVH for face classification (after Manifold cuts produce a
  // single wall solid, we split it back into outer/inner faces by closest
  // point so the existing clippedGeos / capGeos pipeline can build proper
  // boundary caps at the user's trim planes).
  const outerBVH = useMemo(() => {
    if (!outerGeo) return null;
    return new MeshBVH(outerGeo);
  }, [outerGeo]);

  // Precomputed face normals for innerGeo — already oriented CONSISTENTLY
  // OUTWARD (away from the inner shell's bbox center). This way the SDF can
  // dot directly with the stored normal without any per-query sign flipping.
  const innerFaceNormals = useMemo(() => {
    if (!innerGeo) return null;
    innerGeo.computeBoundingBox();
    const meshCenter = new Vector3();
    innerGeo.boundingBox.getCenter(meshCenter);
    const posAttr = innerGeo.attributes.position;
    const idx = innerGeo.index;
    if (!idx) return null;
    const nf = idx.count / 3;
    const normals = new Float32Array(nf * 3);
    const v0 = new Vector3(), v1 = new Vector3(), v2 = new Vector3();
    const e1 = new Vector3(), e2 = new Vector3(), n = new Vector3();
    const toFace = new Vector3();
    for (let i = 0; i < nf; i++) {
      v0.fromBufferAttribute(posAttr, idx.getX(i * 3));
      v1.fromBufferAttribute(posAttr, idx.getX(i * 3 + 1));
      v2.fromBufferAttribute(posAttr, idx.getX(i * 3 + 2));
      e1.subVectors(v1, v0);
      e2.subVectors(v2, v0);
      n.crossVectors(e1, e2).normalize();
      // Vector from inner-shell bbox center to this face center (outward).
      toFace.set(
        (v0.x + v1.x + v2.x) / 3 - meshCenter.x,
        (v0.y + v1.y + v2.y) / 3 - meshCenter.y,
        (v0.z + v1.z + v2.z) / 3 - meshCenter.z,
      );
      // Flip the raw normal if it points back toward the center (= inward).
      if (n.dot(toFace) < 0) n.negate();
      normals[i * 3] = n.x;
      normals[i * 3 + 1] = n.y;
      normals[i * 3 + 2] = n.z;
    }
    return normals;
  }, [innerGeo]);

  // Build closure (velcro strap holder) geometries placed along the slit.
  // Each closure is a small arched bridge on the shell surface, on both sides
  // of the slit, with a gap underneath for the strap to pass through.
  const closureGeos = useMemo(() => {
    if (!closureCount || closureCount < 1 || !slitClickPoint || !model || !slitAxis || !velcroGeo || !innerBVH) return [];

    const axisDir = slitAxis.dir.clone();
    const center = slitAxis.center.clone();

    // Slit line direction (A→B)
    const slitLineDir = slitClickPoint2
      ? new Vector3().subVectors(slitClickPoint2, slitClickPoint).normalize()
      : axisDir.clone();

    // Radial direction from axis toward click point A
    const toA = new Vector3().subVectors(slitClickPoint, center);
    toA.sub(axisDir.clone().multiplyScalar(toA.dot(axisDir)));
    if (toA.length() < 0.01) return [];
    const radialDir = toA.clone().normalize();

    // Side direction: perpendicular to slit line AND radial (tangent along shell)
    const sideDir = new Vector3().crossVectors(slitLineDir, radialDir).normalize();

    // Slit length range — between upper and lower cut planes along slit line
    const upperC = slitAxis.upper, lowerC = slitAxis.lower;
    const axisStart = new Vector3().subVectors(lowerC, slitClickPoint).dot(slitLineDir);
    const axisEnd = new Vector3().subVectors(upperC, slitClickPoint).dot(slitLineDir);
    const length = axisEnd - axisStart;
    if (Math.abs(length) < 5) return [];

    // Velcro STL local axes (sorted by extent):
    //  - longest  → along the slit
    //  - middle   → radial (depth of C arms)
    //  - shortest → tangential perpendicular to slit
    velcroGeo.computeBoundingBox();
    const vbb = velcroGeo.boundingBox;
    const vSize = new Vector3();
    vbb.getSize(vSize);
    const sortedAxes = [
      { name: 'x', size: vSize.x },
      { name: 'y', size: vSize.y },
      { name: 'z', size: vSize.z },
    ].sort((a, b) => b.size - a.size);
    const localLong  = sortedAxes[0].name;
    const localMid   = sortedAxes[1].name;
    const localShort = sortedAxes[2].name;
    const midSize   = sortedAxes[1].size;
    const shortSize = sortedAxes[2].size;

    // Build a raycaster to find the actual shell surface at each height
    const raycaster = new Raycaster();
    const meshes = [];
    model.traverse((child) => { if (child.isMesh) meshes.push(child); });

    // Per-connector basis builder — both arms (at +Z and -Z ends along the
    // long axis) get their own surface contact via raycast. The connector is
    // then positioned and oriented so that both arms touch the surface plus
    // an embedding buffer.
    const longSize = sortedAxes[0].size;
    const BUCKLE_INNER_Y_LOCAL = -11;
    // Desired gap between the GREEN MARKER and orthesis outer shell. The
    // markers stay at +4mm above the outer surface (user-defined reference).
    const BASE_GAP = 4.0;
    const buildClosureMesh = (tAlongCenter, side) => {
      const cloned = velcroGeo.clone();
      // Re-center the cloned geometry at its bbox origin so the anchor math
      // (which assumes a centered mesh) places the connector correctly.
      cloned.computeBoundingBox();
      const vbCenter = new Vector3();
      cloned.boundingBox.getCenter(vbCenter);
      cloned.translate(-vbCenter.x, -vbCenter.y, -vbCenter.z);

      // Two arm positions along slit direction (each at half-longSize from center)
      const armOffsets = [-longSize / 2, +longSize / 2];
      const armSurfaces = []; // surface points for each arm

      for (const off of armOffsets) {
        const tArm = tAlongCenter + off;
        const armPoint = slitClickPoint.clone().addScaledVector(slitLineDir, tArm);

        // Project onto axis at this height
        const t = new Vector3().subVectors(armPoint, center).dot(axisDir);
        const axisPoint = center.clone().addScaledVector(axisDir, t);

        // Raycast from axis outward in radialDir to find shell surface
        raycaster.set(axisPoint, radialDir);
        const hits = raycaster.intersectObjects(meshes);
        if (hits.length > 0 && hits[0].face) {
          // Use REAL surface normal (handles both axial AND azimuthal curvature)
          const normalMatrix = new Matrix3().getNormalMatrix(hits[0].object.matrixWorld);
          const worldNormal = hits[0].face.normal.clone().applyMatrix3(normalMatrix).normalize();
          // Ensure normal points outward (same general direction as radial)
          if (worldNormal.dot(radialDir) < 0) worldNormal.negate();
          armSurfaces.push({ point: hits[0].point.clone(), normal: worldNormal });
        } else {
          armSurfaces.push({ point: armPoint, normal: radialDir.clone() });
        }
      }

      // Center surface point: midpoint between the two arms
      const surfaceCenter = new Vector3().addVectors(armSurfaces[0].point, armSurfaces[1].point).multiplyScalar(0.5);
      // Mean surface normal — used only to compute the lateral offset direction.
      const localRadial = new Vector3().addVectors(armSurfaces[0].normal, armSurfaces[1].normal).normalize();
      // Long direction: from arm 0 surface point to arm 1 surface point (follows curve)
      const longRaw = new Vector3().subVectors(armSurfaces[1].point, armSurfaces[0].point).normalize();
      // Local side (perpendicular to long and average radial) — used for the
      // sideOffset that puts the connector beside the slit.
      const localLongTemp = longRaw.clone().sub(localRadial.clone().multiplyScalar(longRaw.dot(localRadial))).normalize();
      const localSide = new Vector3().crossVectors(localLongTemp, localRadial).normalize();

      // Lateral position of this connector (one side of the slit).
      const sideOffset = (slitWidth ? slitWidth / 2 : 4) + 2 + shortSize / 2;
      const vizCenter = surfaceCenter.clone().addScaledVector(localSide, side * sideOffset);

      // Raycast at vizCenter to get the EXACT scan-surface point + its local
      // normal — used as the basis for BOTH the buckle anchor and the debug
      // disks, so the buckle's bar2 inner face lands precisely on the green
      // disk with matching orientation.
      raycaster.set(vizCenter.clone().addScaledVector(localRadial, 100), localRadial.clone().negate());
      const vizHits = raycaster.intersectObjects(meshes);
      let surfaceHit, surfaceNormal;
      if (vizHits.length > 0 && vizHits[0].face) {
        surfaceHit = vizHits[0].point.clone();
        const nm = new Matrix3().getNormalMatrix(vizHits[0].object.matrixWorld);
        surfaceNormal = vizHits[0].face.normal.clone().applyMatrix3(nm).normalize();
        if (surfaceNormal.dot(localRadial) < 0) surfaceNormal.negate();
      } else {
        surfaceHit = vizCenter.clone();
        surfaceNormal = localRadial.clone();
      }

      // Build the connector basis using the LOCAL surface normal (not the
      // average) so the buckle's mid axis is exactly perpendicular to the
      // orthesis surface at this position.
      const longAxis = longRaw.clone().sub(surfaceNormal.clone().multiplyScalar(longRaw.dot(surfaceNormal))).normalize();
      const sideAxis = new Vector3().crossVectors(longAxis, surfaceNormal).normalize();

      const worldLong  = longAxis.clone();
      const worldMid   = surfaceNormal.clone().negate();  // +Y_local → INWARD (toward orthesis)
      const worldShort = sideAxis.clone();

      // Right-handed check: det of matrix m = cols.x · (cols.y × cols.z).
      // (Previous version used cross(worldLong, worldMid).dot(worldShort) which
      // is -det, so the conditional negated worldShort in the WRONG cases —
      // producing a reflected/left-handed matrix that mirrored the connector.)
      const det = new Vector3().crossVectors(worldMid, worldLong).dot(worldShort);
      if (det < 0) worldShort.negate();

      const cols = { x: new Vector3(), y: new Vector3(), z: new Vector3() };
      cols[localLong]  = worldLong;
      cols[localMid]   = worldMid;
      cols[localShort] = worldShort;

      // Anchor: place the buckle's inner face (Y_local = BUCKLE_INNER_Y_LOCAL)
      // exactly on the green disk at surfaceHit + (outerOffset + BASE_GAP) * surfaceNormal.
      // With worldMid = -surfaceNormal, a point at local y_centered ends up at
      //   anchor + y_centered * (-surfaceNormal)
      // For the buckle inner face: y_centered = BUCKLE_INNER_Y_LOCAL - vbCenter[localMid]
      const buckleInnerYCentered = BUCKLE_INNER_Y_LOCAL - vbCenter[localMid];
      const anchorDepth = outerOffset + BASE_GAP + buckleInnerYCentered;
      const anchor = surfaceHit.clone().addScaledVector(surfaceNormal, anchorDepth);

      const m = new Matrix4().makeBasis(cols.x, cols.y, cols.z);
      m.setPosition(anchor);

      cloned.applyMatrix4(m);

      // Per-vertex SDF clipping against the actual inner shell mesh (via BVH).
      // Each vertex's signed distance is measured against its OWN closest face
      // on the inner shell, using THAT face's normal — so the sign is correct
      // even for vertices far from the connector center, in regions where the
      // local normal differs from the connector's average surfaceNormal.
      // CLIP_INSET pushes the effective clip slightly outward so the cap
      // stays buried inside the wall material (not visible from inside).
      const CLIP_INSET = 1.0;
      const sdfTarget = { point: new Vector3(), distance: 0, faceIndex: -1 };
      const sdfDiff = new Vector3();
      const sdfQuery = (p) => {
        innerBVH.closestPointToPoint(p, sdfTarget);
        const fi = sdfTarget.faceIndex;
        // Face normal is pre-oriented OUTWARD (built into innerFaceNormals),
        // so the dot product directly gives positive for OUTWARD and negative
        // for INWARD of the inner shell — no per-query sign correction needed.
        const nx = innerFaceNormals[fi * 3];
        const ny = innerFaceNormals[fi * 3 + 1];
        const nz = innerFaceNormals[fi * 3 + 2];
        sdfDiff.subVectors(p, sdfTarget.point);
        return (sdfDiff.x * nx + sdfDiff.y * ny + sdfDiff.z * nz) - CLIP_INSET;
      };
      const posAttr = cloned.attributes.position;
      const idx = cloned.index;
      const triCount = idx ? idx.count / 3 : posAttr.count / 3;
      const newPos = [];
      const cutEdges = []; // [[Vector3, Vector3], ...] new edges on the curved clip
      const getV = (i) => new Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
      const intersect = (p1, d1, p2, d2) => {
        // Linear lerp between p1 and p2 at the zero-crossing of the SDF.
        // For small triangles the SDF is approximately linear, so this lerp
        // lands close to the true curved clip surface.
        const t = d1 / (d1 - d2);
        return new Vector3().lerpVectors(p1, p2, t);
      };
      for (let t = 0; t < triCount; t++) {
        const ai = idx ? idx.getX(t*3) : t*3;
        const bi = idx ? idx.getX(t*3+1) : t*3+1;
        const ci = idx ? idx.getX(t*3+2) : t*3+2;
        const a = getV(ai), b = getV(bi), c = getV(ci);
        const da = sdfQuery(a);
        const db = sdfQuery(b);
        const dc = sdfQuery(c);
        if (da >= 0 && db >= 0 && dc >= 0) {
          newPos.push(a.x,a.y,a.z, b.x,b.y,b.z, c.x,c.y,c.z);
          continue;
        }
        if (da < 0 && db < 0 && dc < 0) continue;
        const verts = [{p:a,d:da},{p:b,d:db},{p:c,d:dc}];
        const pos = verts.filter(v => v.d >= 0);
        const neg = verts.filter(v => v.d < 0);
        if (pos.length === 1) {
          const [p1] = pos, [n1, n2] = neg;
          const i1 = intersect(p1.p, p1.d, n1.p, n1.d);
          const i2 = intersect(p1.p, p1.d, n2.p, n2.d);
          newPos.push(p1.p.x,p1.p.y,p1.p.z, i1.x,i1.y,i1.z, i2.x,i2.y,i2.z);
          cutEdges.push([i1, i2]);
        } else {
          const [p1, p2] = pos, [n1] = neg;
          const i1 = intersect(p1.p, p1.d, n1.p, n1.d);
          const i2 = intersect(p2.p, p2.d, n1.p, n1.d);
          newPos.push(p1.p.x,p1.p.y,p1.p.z, p2.p.x,p2.p.y,p2.p.z, i1.x,i1.y,i1.z);
          newPos.push(p2.p.x,p2.p.y,p2.p.z, i2.x,i2.y,i2.z, i1.x,i1.y,i1.z);
          cutEdges.push([i2, i1]);
        }
      }

      // Build cap triangles: find closed loops in cutEdges, fan-triangulate
      // each loop from its centroid. This closes the open holes left by clipping.
      const PREC = 1000;
      const k3 = (v) => `${Math.round(v.x*PREC)}_${Math.round(v.y*PREC)}_${Math.round(v.z*PREC)}`;
      const vertMap = new Map(); // key -> { p, id }
      const getId = (v) => {
        const k = k3(v);
        const e = vertMap.get(k);
        if (e) return e.id;
        const id = vertMap.size;
        vertMap.set(k, { p: v, id });
        return id;
      };
      const edgeIds = cutEdges.map(([a, b]) => [getId(a), getId(b)]);
      const adj = new Map();
      for (let i = 0; i < edgeIds.length; i++) {
        const [a, b] = edgeIds[i];
        if (!adj.has(a)) adj.set(a, []);
        if (!adj.has(b)) adj.set(b, []);
        adj.get(a).push({ to: b, eIdx: i });
        adj.get(b).push({ to: a, eIdx: i });
      }
      const idToPoint = new Map();
      for (const e of vertMap.values()) idToPoint.set(e.id, e.p);
      const usedEdge = new Set();
      // Cap normal must point INWARD (-localRadial) so it faces the discarded side.
      // For a cut edge [e1, e2] from a 1-pos triangle, e1→e2 wraps CCW around the
      // hole when viewed from the kept (+localRadial) side. So fan triangle
      // (centroid, e1, e2) is CCW from outside → normal is +localRadial. We need
      // the OPPOSITE: (centroid, e2, e1) gives -localRadial cap normal.
      for (let start = 0; start < edgeIds.length; start++) {
        if (usedEdge.has(start)) continue;
        const loopVerts = [];
        let curEdge = start;
        let prevVert = edgeIds[start][0];
        while (curEdge !== null && !usedEdge.has(curEdge)) {
          usedEdge.add(curEdge);
          loopVerts.push(prevVert);
          const [a, b] = edgeIds[curEdge];
          const nextVert = prevVert === a ? b : a;
          const cands = (adj.get(nextVert) || []).filter(n => !usedEdge.has(n.eIdx));
          curEdge = cands.length > 0 ? cands[0].eIdx : null;
          prevVert = nextVert;
        }
        if (loopVerts.length < 3) continue;
        const centroid = new Vector3();
        for (const id of loopVerts) centroid.add(idToPoint.get(id));
        centroid.divideScalar(loopVerts.length);
        for (let i = 0; i < loopVerts.length; i++) {
          const e1 = idToPoint.get(loopVerts[i]);
          const e2 = idToPoint.get(loopVerts[(i + 1) % loopVerts.length]);
          // Reversed winding so cap normal points -localRadial (inward / toward axis)
          newPos.push(centroid.x, centroid.y, centroid.z, e2.x, e2.y, e2.z, e1.x, e1.y, e1.z);
        }
      }

      const clipped = new BufferGeometry();
      clipped.setAttribute('position', new Float32BufferAttribute(newPos, 3));
      clipped.computeVertexNormals();
      clipped.computeBoundingSphere();

      // Debug visualization: 4mm gap line + reference surface markers (disks).
      // Both reference points are computed from the SAME surfaceHit and
      // surfaceNormal used to anchor the connector — so the green disk lies
      // exactly on the buckle's inner face (Y_local = BUCKLE_INNER_Y_LOCAL).
      const outerShellPoint = surfaceHit.clone().addScaledVector(surfaceNormal, outerOffset);
      const bucklePoint = surfaceHit.clone().addScaledVector(surfaceNormal, outerOffset + BASE_GAP);
      // SECOND verification: transform a point at the buckle's bar2 inner face
      // (X=0, Y=BUCKLE_INNER_Y_LOCAL after centering, Z=0) via the SAME matrix
      // applied to the connector. This must land on bucklePoint if math is right.
      const verifyLocalPoint = new Vector3(0, BUCKLE_INNER_Y_LOCAL - vbCenter[localMid], 0);
      // Map local centered coords to world axes (same as matrix m does):
      const verifyWorld = anchor.clone()
        .addScaledVector(cols.x, verifyLocalPoint.x)
        .addScaledVector(cols.y, verifyLocalPoint.y)
        .addScaledVector(cols.z, verifyLocalPoint.z);
      const baseDiskQ = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), surfaceNormal);
      const mkDisk = (point) => {
        const disk = new CircleGeometry(2.0, 24);
        disk.applyQuaternion(baseDiskQ);
        disk.translate(point.x, point.y, point.z);
        return disk;
      };
      const gapLineGeo = new BufferGeometry();
      gapLineGeo.setAttribute('position', new Float32BufferAttribute(new Float32Array([
        outerShellPoint.x, outerShellPoint.y, outerShellPoint.z,
        bucklePoint.x, bucklePoint.y, bucklePoint.z,
      ]), 3));
      // BLUE debug disk at where Y_local = BUCKLE_INNER_Y_LOCAL lands.
      // Must visually coincide with the green disk if positioning is correct.
      const verifyDiskQ = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), surfaceNormal);
      const verifyDisk = new CircleGeometry(1.0, 24);
      verifyDisk.applyQuaternion(verifyDiskQ);
      verifyDisk.translate(verifyWorld.x, verifyWorld.y, verifyWorld.z);
      // Additional debug disks at OTHER candidate Y_local positions so user can
      // see where each STL bar lands in world and identify the "Mittelstück".
      const mkLocalDisk = (yLocal, radius) => {
        const localY_centered = yLocal - vbCenter[localMid];
        const w = anchor.clone()
          .addScaledVector(cols.y, localY_centered);
        const d = new CircleGeometry(radius, 24);
        d.applyQuaternion(verifyDiskQ);
        d.translate(w.x, w.y, w.z);
        return d;
      };
      const debugBar1 = mkLocalDisk(-2.5, 1.5);   // bar1 (top of C, far from orthesis)
      const debugY5   = mkLocalDisk(5.0,  1.5);   // intermediate feature
      const debugBar3 = mkLocalDisk(8.0,  1.5);   // bar3 (Y=8, possibly the foot anchor)
      clipped.userData = {
        gapViz: {
          outerDisk: mkDisk(outerShellPoint),
          buckleDisk: mkDisk(bucklePoint),
          verifyDisk,
          debugBar1,
          debugY5,
          debugBar3,
          lineGeo: gapLineGeo,
        },
      };
      return clipped;
    };

    // Distribute closures evenly along slit length, on both sides of the slit
    const closures = [];
    for (let i = 0; i < closureCount; i++) {
      const frac = (i + 1) / (closureCount + 1);
      const tAlong = axisStart + length * frac;
      for (const side of [-1, 1]) {
        const mesh = buildClosureMesh(tAlong, side);
        if (mesh) closures.push(mesh);
      }
    }
    return closures;
  }, [closureCount, slitClickPoint, slitClickPoint2, slitAxis, slitWidth, model, velcroGeo, innerOffset, outerOffset, innerBVH, innerFaceNormals]);

  // ============================================================
  // Hole pattern (Phase 2): orthesis circumference is divided into TWO
  // continuous halves by the slit and (when present) the thumb opening.
  // Each half gets its own slightly-curved central rib, and a row of hole
  // markers is placed on each side of that rib.
  //
  // Runs async with periodic yields (setTimeout 0) so the UI thread can
  // paint between heavy raycast batches. This gives a real progress bar
  // on mobile where the full computation can take 1–3s.
  // ============================================================
  const [holePattern, setHolePattern] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const yieldNow = () => new Promise((resolve) => setTimeout(resolve, 0));
    const reportProgress = (p) => { if (onPlanProgressRef.current) onPlanProgressRef.current(p); };
    const run = async () => {
    if (!holePatternEnabled || !slitAxis || !model || !slitClickPoint) {
      setHolePattern(null);
      return;
    }
    reportProgress(1);

    const axisDir = slitAxis.dir.clone().normalize();
    const axisCenter = slitAxis.center.clone();
    const axisLower = slitAxis.lower.clone();
    const axisUpper = slitAxis.upper.clone();

    // Axial range (signed projection onto axisDir, relative to axisCenter)
    const lowerOnAxis = new Vector3().subVectors(axisLower, axisCenter).dot(axisDir);
    const upperOnAxis = new Vector3().subVectors(axisUpper, axisCenter).dot(axisDir);
    const uMin = Math.min(lowerOnAxis, upperOnAxis);
    const uMax = Math.max(lowerOnAxis, upperOnAxis);
    const axialLength = uMax - uMin;
    if (axialLength < 10) { setHolePattern(null); return; }

    // Azimuth basis: v=0 at slit, positive v wraps around the long way.
    const toSlit = new Vector3().subVectors(slitClickPoint, axisCenter);
    toSlit.sub(axisDir.clone().multiplyScalar(toSlit.dot(axisDir)));
    if (toSlit.length() < 0.01) { setHolePattern(null); return; }
    toSlit.normalize();
    const perp = new Vector3().crossVectors(axisDir, toSlit).normalize();

    // Approximate orthesis radius from slitClickPoint -> axis projection length
    const approxRadius = new Vector3().subVectors(slitClickPoint, axisCenter)
      .sub(axisDir.clone().multiplyScalar(new Vector3().subVectors(slitClickPoint, axisCenter).dot(axisDir)))
      .length() || 50;

    // Slit azimuthal half-angle (covers material removed by the slit cut)
    const slitHalfAngle = Math.atan2((slitWidth || 20) / 2, approxRadius);
    const padding = 0.15;

    // Thumb opening (if user has set thumbBase + thumbTip, thumbPoint is valid).
    // Splits the orthesis circumference into two halves so each gets its own
    // central rib. We use thumbPoint regardless of thumbMode ('free' vs 'fixed')
    // because both modes still leave a thumb cutout in the orthesis surface.
    let vThumb = null;
    let thumbHalfAngle = 0;
    if (thumbPoint) {
      const toThumb = new Vector3().subVectors(thumbPoint, axisCenter);
      toThumb.sub(axisDir.clone().multiplyScalar(toThumb.dot(axisDir)));
      if (toThumb.length() > 0.01) {
        toThumb.normalize();
        const dotS = toThumb.dot(toSlit);
        const dotP = toThumb.dot(perp);
        vThumb = Math.atan2(dotP, dotS);
        if (vThumb < 0) vThumb += 2 * Math.PI;
        thumbHalfAngle = Math.atan2(25 / 2, approxRadius); // ~25mm thumb opening heuristic
      }
    }

    // Build the list of halves [vStart, vEnd] (each in (0, 2π))
    const halves = [];
    if (vThumb !== null) {
      const slitMin = slitHalfAngle + padding;
      const slitMax = 2 * Math.PI - slitHalfAngle - padding;
      const thumbMin = vThumb - thumbHalfAngle - padding;
      const thumbMax = vThumb + thumbHalfAngle + padding;
      // Half A: from slit (going CCW) to thumb
      if (slitMin < thumbMin) halves.push({ vStart: slitMin, vEnd: thumbMin });
      // Half B: from thumb to slit (wrapping)
      if (thumbMax < slitMax) halves.push({ vStart: thumbMax, vEnd: slitMax });
    } else {
      halves.push({ vStart: slitHalfAngle + padding, vEnd: 2 * Math.PI - slitHalfAngle - padding });
    }

    const raycaster = new Raycaster();
    const meshes = [];
    model.traverse((child) => { if (child.isMesh) meshes.push(child); });

    const radialAt = (v) => toSlit.clone().multiplyScalar(Math.cos(v))
      .addScaledVector(perp, Math.sin(v))
      .normalize();

    // Connector exclusion zones removed — the blue region outline (built from
    // the per-half slit/thumb boundaries with edgeMargin inset) already keeps
    // hole placement away from connectors, so the extra distance check + the
    // magenta circle visualization were redundant.

    const ribAmplitude = 0.12; // ~7° azimuthal swing
    const ribWavelength = axialLength;
    // Hole shape (parallelogram with vertical sides + slanted top/bottom).
    // SIZE is now derived from the sliders:
    //   - holesPerRow  → vertical count of holes per column (one left + one right of rib)
    //   - holeSpacing  → edge-to-edge gap to rib (half) and to neighbour holes axially
    // Top/bottom orthesis-edge margin is also holeSpacing/2 (= same as rib margin).
    const fdmAngleRad = 50 * Math.PI / 180;
    const ribMargin = holeSpacing / 2;   // mm — distance hole edge → rib
    const edgeMargin = holeSpacing / 2;  // mm — distance hole top/bottom edge → orthesis edge
    // Axial hole height: fit `holesPerRow` holes within (axialLength − 2×edgeMargin),
    // separated by `holeSpacing` between consecutive holes (edge-to-edge).
    const nRows = Math.max(1, holesPerRow);
    const availableAxial = Math.max(0, axialLength - 2 * edgeMargin);
    let holeH = (availableAxial - (nRows - 1) * holeSpacing) / nRows;
    if (holeH < 3) holeH = 3; // clamp: too many holes for the spacing → collapse to 3mm min

    // For each half: compute centered rib + 2 columns of holes (one on each side).
    const allCenters = [];
    const allRibPoints = [];
    const allowedRegions = []; // 3D outlines of the per-half available area for visualization
    // Convert mm offset (circumferential) to azimuth radians using approxRadius.
    const azOffset = (cMM) => cMM / approxRadius;
    // FDM-friendly upper bound on hole width: tan(60°) × (drop) = (W/2) × tan(60°)
    // requires the half-drop ≤ edgeMargin (= holeSpacing/2) so corners stay within
    // the orthesis. Solving: W_max = holeSpacing / tan(60°).
    const fdmMaxW = holeSpacing / Math.tan(fdmAngleRad);

    // Sample helper: project (u, v) into the orthesis surface via raycast.
    const sampleSurface = (u, v) => {
      const axisPoint = axisCenter.clone().addScaledVector(axisDir, u);
      raycaster.set(axisPoint, radialAt(v));
      const hits = raycaster.intersectObjects(meshes);
      return hits.length > 0 ? hits[0].point.clone() : null;
    };
    // Per-corner LOCAL SCAN NORMAL — used as the extrusion direction for the
    // hole brush prism, so each corner extrudes ALONG the actual orthese wall
    // direction. The previous axis-radial approximation drifted at corners
    // where the local surface normal differs from the axis-radial direction
    // (e.g., near the wrist taper or the thumb bulge), leaving uncovered
    // wall fragments INSIDE the hole boundary (the user's "tongue" artifacts).
    //
    // We raycast from the axis through the polygon corner against the scan
    // model and take the hit face's normal. Orientation is forced to OUTWARD
    // (positive dot with the axis-radial direction) so brushes consistently
    // extend away from the arm.
    // Per-vertex INTERPOLIERTE Scan-Normale am Raycast-Hit-Punkt.
    //
    // Wichtig: die Orthesen-Außenfläche wird mit per-vertex geglätteten
    // Normalen offsetiert (siehe buildOffsetGeoPair: `vnorm[]` als Vertex-
    // Normale für die Offset-Richtung jedes Scan-Vertex). Wenn der Hole-Cutter
    // hingegen die FLACHE Face-Normale verwenden würde, würde er bei starker
    // Krümmung in eine leicht andere Richtung extrudieren als die Wand → Seiten-
    // flächen schneiden minimal verschoben durch die Wand → schmale Reste.
    //
    // Wir interpolieren daher die Vertex-Normalen der getroffenen Triangle
    // anhand der baryzentrischen Koordinaten des Hit-Punkts — exakt das gleiche
    // Normalfeld wie das der Orthese.
    const _scanNormalTmp = {
      local: new Vector3(), axisPoint: new Vector3(), normalMat: new Matrix3(),
      pA: new Vector3(), pB: new Vector3(), pC: new Vector3(),
      nA: new Vector3(), nB: new Vector3(), nC: new Vector3(),
      bary: new Vector3(),
    };
    const _triHelper = new Triangle();
    const getLocalScanNormal = (point) => {
      _scanNormalTmp.local.subVectors(point, axisCenter);
      const proj = _scanNormalTmp.local.dot(axisDir);
      const radial = _scanNormalTmp.local.sub(axisDir.clone().multiplyScalar(proj));
      const len = radial.length();
      if (len < 0.001) return new Vector3(1, 0, 0);
      const radialDir = radial.multiplyScalar(1 / len);
      _scanNormalTmp.axisPoint.copy(axisCenter).addScaledVector(axisDir, proj);
      raycaster.set(_scanNormalTmp.axisPoint, radialDir);
      const hits = raycaster.intersectObjects(meshes);
      if (hits.length === 0 || !hits[0].face) return radialDir.clone();

      const hit = hits[0];
      const obj = hit.object;
      const geo = obj.geometry;
      const posAttr = geo.attributes.position;
      const normAttr = geo.attributes.normal;
      _scanNormalTmp.normalMat.getNormalMatrix(obj.matrixWorld);

      // Fallback wenn das Scan-Mesh keine Vertex-Normalen hat: alte Face-Normale.
      if (!normAttr || !posAttr) {
        const n = hit.face.normal.clone().applyMatrix3(_scanNormalTmp.normalMat).normalize();
        if (n.dot(radialDir) < 0) n.negate();
        return n;
      }

      const a = hit.face.a, b = hit.face.b, c = hit.face.c;
      _scanNormalTmp.pA.fromBufferAttribute(posAttr, a).applyMatrix4(obj.matrixWorld);
      _scanNormalTmp.pB.fromBufferAttribute(posAttr, b).applyMatrix4(obj.matrixWorld);
      _scanNormalTmp.pC.fromBufferAttribute(posAttr, c).applyMatrix4(obj.matrixWorld);
      _triHelper.set(_scanNormalTmp.pA, _scanNormalTmp.pB, _scanNormalTmp.pC);
      _triHelper.getBarycoord(hit.point, _scanNormalTmp.bary);

      _scanNormalTmp.nA.fromBufferAttribute(normAttr, a).applyMatrix3(_scanNormalTmp.normalMat);
      _scanNormalTmp.nB.fromBufferAttribute(normAttr, b).applyMatrix3(_scanNormalTmp.normalMat);
      _scanNormalTmp.nC.fromBufferAttribute(normAttr, c).applyMatrix3(_scanNormalTmp.normalMat);
      const n = new Vector3()
        .addScaledVector(_scanNormalTmp.nA, _scanNormalTmp.bary.x)
        .addScaledVector(_scanNormalTmp.nB, _scanNormalTmp.bary.y)
        .addScaledVector(_scanNormalTmp.nC, _scanNormalTmp.bary.z);
      const lenSq = n.lengthSq();
      // Fallback wenn die Interpolation degeneriert ist (Triangle hat Fläche 0
      // → NaN-Bary; oder Vertex-Normalen sind alle ~0) → Face-Normale.
      // Vorsicht: NaN < x ergibt false, also explizit auf Endlichkeit prüfen.
      if (!Number.isFinite(lenSq) || lenSq < 1e-8) {
        n.copy(hit.face.normal).applyMatrix3(_scanNormalTmp.normalMat);
        if (n.lengthSq() < 1e-8) return radialDir.clone();
      }
      n.normalize();
      if (!Number.isFinite(n.x) || !Number.isFinite(n.y) || !Number.isFinite(n.z)) {
        return radialDir.clone();
      }
      if (n.dot(radialDir) < 0) n.negate();
      return n;
    };

    // Pre-declare per-hole output arrays (populated inside the half loop now
    // so vertical clipping and the curved-inner-edge polygon are built in one pass).
    const holePolygons = [];
    const holeBases = [];
    const holeTangents2D = [];
    // Per-corner radial directions parallel to holePolygons — used by
    // cutShells to build surface-aligned brushes that match the markers.
    const holeCornerNormals = [];

    // Progress accounting: each half has 4 fixed sub-steps (profile, region,
    // rib, triangle post-process) plus nRows hole placements.
    const totalSteps = Math.max(1, halves.length * (4 + Math.max(1, holesPerRow)));
    let completedSteps = 0;
    const advance = async () => {
      completedSteps++;
      reportProgress(2 + (completedSteps / totalSteps) * 96);
      await yieldNow();
    };

    for (const half of halves) {
      if (cancelled) return;
      const vMid = (half.vStart + half.vEnd) / 2;
      const ribVAt = (u) => vMid + ribAmplitude * Math.sin(2 * Math.PI * (u - uMin) / ribWavelength);
      const azEdgeMargin = azOffset(edgeMargin);
      const azRibMargin = azOffset(ribMargin);
      const vLo = half.vStart + azEdgeMargin;
      const vHi = half.vEnd - azEdgeMargin;

      // Per-half ORTHESIS EDGE PROFILE: for each sampled azimuth v, scan u
      // along the axis and record min-u (botU) and max-u (topU) where a
      // radial ray hits the surface. Captures the (possibly slanted/curved)
      // top and bottom edges of THIS half. Used for both the region outline
      // and per-hole vertical clipping.
      const profileSamples = 30;
      const scanSamples = 50;
      const profile = [];
      for (let pi = 0; pi <= profileSamples; pi++) {
        const v = half.vStart + (half.vEnd - half.vStart) * (pi / profileSamples);
        let topU = null, botU = null;
        for (let si = 0; si <= scanSamples; si++) {
          const uS = uMin + (si / scanSamples) * axialLength;
          const axisPointS = axisCenter.clone().addScaledVector(axisDir, uS);
          raycaster.set(axisPointS, radialAt(v));
          if (raycaster.intersectObjects(meshes).length > 0) {
            if (botU === null) botU = uS;
            topU = uS;
          }
        }
        profile.push({ v, topU, botU });
      }
      await advance();
      if (cancelled) return;
      const queryProfile = (v) => {
        if (v <= profile[0].v) return profile[0];
        if (v >= profile[profile.length - 1].v) return profile[profile.length - 1];
        for (let i = 0; i < profile.length - 1; i++) {
          const p0 = profile[i], p1 = profile[i + 1];
          if (v >= p0.v && v <= p1.v) {
            const t = (v - p0.v) / (p1.v - p0.v);
            const tU = (p0.topU != null && p1.topU != null) ? p0.topU * (1 - t) + p1.topU * t : (p0.topU ?? p1.topU);
            const bU = (p0.botU != null && p1.botU != null) ? p0.botU * (1 - t) + p1.botU * t : (p0.botU ?? p1.botU);
            return { v, topU: tU, botU: bU };
          }
        }
        return { v, topU: null, botU: null };
      };

      // Region outline — top/bottom follow the orthesis edge (with edgeMargin
      // inset); left/right are constant-azimuth slices at vLo and vHi.
      if (vHi > vLo) {
        const regionPts = [];
        const azSamples = 24;
        for (let i = 0; i <= azSamples; i++) {
          const v = vLo + (vHi - vLo) * (i / azSamples);
          const { topU } = queryProfile(v);
          if (topU == null) continue;
          const p = sampleSurface(topU - edgeMargin, v);
          if (p) regionPts.push(p);
        }
        {
          const { topU, botU } = queryProfile(vHi);
          if (topU != null && botU != null) {
            const axS = 10;
            const uTop = topU - edgeMargin, uBot = botU + edgeMargin;
            for (let i = 1; i <= axS; i++) {
              const t = i / axS;
              const p = sampleSurface(uTop + (uBot - uTop) * t, vHi);
              if (p) regionPts.push(p);
            }
          }
        }
        for (let i = 0; i <= azSamples; i++) {
          const v = vHi - (vHi - vLo) * (i / azSamples);
          const { botU } = queryProfile(v);
          if (botU == null) continue;
          const p = sampleSurface(botU + edgeMargin, v);
          if (p) regionPts.push(p);
        }
        {
          const { topU, botU } = queryProfile(vLo);
          if (topU != null && botU != null) {
            const axS = 10;
            const uTop = topU - edgeMargin, uBot = botU + edgeMargin;
            for (let i = 1; i <= axS; i++) {
              const t = i / axS;
              const p = sampleSurface(uBot + (uTop - uBot) * t, vLo);
              if (p) regionPts.push(p);
            }
          }
        }
        if (regionPts.length > 2) {
          regionPts.push(regionPts[0].clone());
          allowedRegions.push(regionPts);
        }
      }
      await advance();
      if (cancelled) return;
      const ribSamples = 30;
      const ribPoints = [];
      for (let i = 0; i <= ribSamples; i++) {
        const u = uMin + (i / ribSamples) * axialLength;
        const v = ribVAt(u);
        const axisPoint = axisCenter.clone().addScaledVector(axisDir, u);
        raycaster.set(axisPoint, radialAt(v));
        const hits = raycaster.intersectObjects(meshes);
        if (hits.length > 0) ribPoints.push(hits[0].point.clone());
      }
      allRibPoints.push(ribPoints);
      await advance();
      if (cancelled) return;

      // Hole layout per half: use the median-azimuth profile entry to lay
      // rows out axially with edge-to-edge spacing = holeSpacing and
      // edgeMargin from the half's orthesis top/bottom edges.
      const midV = (vLo + vHi) / 2;
      const midProf = queryProfile(midV);
      if (midProf.topU == null || midProf.botU == null) {
        completedSteps += nRows + 1;
        reportProgress(2 + (completedSteps / totalSteps) * 96);
        continue;
      }
      const localAxialLength = midProf.topU - midProf.botU;
      if (localAxialLength <= 2 * edgeMargin) {
        completedSteps += nRows + 1;
        reportProgress(2 + (completedSteps / totalSteps) * 96);
        continue;
      }
      const localAvailableAxial = localAxialLength - 2 * edgeMargin;
      let localHoleH = (localAvailableAxial - (nRows - 1) * holeSpacing) / nRows;
      if (localHoleH < 3) localHoleH = 3;
      const axialPitch = localHoleH + holeSpacing;
      const firstCenterU = midProf.botU + edgeMargin + localHoleH / 2;

      // Per-side tracking of the BOTTOM-most successfully placed hole, so we
      // can drop the FDM slant on its bottom edge and snap BR/BE corners to
      // the region's bottom boundary (no overhang needed at the orthese end).
      const bottomPlacedForSide = { '-1': false, '1': false };
      // Per-side stack of placed hole data so we can identify the TOP-most
      // hole AFTER the row loop finishes and rebuild it as a triangle.
      const placedHolesPerSide = { '-1': [], '1': [] };

      const stepsBeforeRowLoop = completedSteps;
      for (let r = 0; r < nRows; r++) {
        await advance();
        if (cancelled) return;
        const u = firstCenterU + r * axialPitch;
        if (u > midProf.topU - edgeMargin - localHoleH / 2 + 0.1) break;
        const ribV = ribVAt(u);
        for (const side of [-1, +1]) {
          const innerAzAtCenter = ribV + side * azRibMargin;
          const outerAz = side > 0 ? vHi : vLo;
          const azWidthSigned = (outerAz - innerAzAtCenter) * side;
          // Force BOTH columns even when narrow: relaxed thresholds so a
          // half stays symmetric (both sides get a column) even at axial
          // positions where the rib swings close to one region edge.
          if (azWidthSigned <= 0.002) continue;
          const W_mm = azWidthSigned * approxRadius;
          if (W_mm < 1) continue;
          const vCenter = (innerAzAtCenter + outerAz) / 2;

          // Vertical clipping: query the edge profile at a few v's across
          // the hole's azimuth range and take the most restrictive top/bottom.
          // If hole extends past region top/bottom it gets shortened (u center
          // shifts toward the constraining edge).
          const vSamplesQ = [innerAzAtCenter, vCenter, outerAz];
          let hardTopU = +Infinity, hardBotU = -Infinity;
          for (const vq of vSamplesQ) {
            const { topU, botU } = queryProfile(vq);
            if (topU != null) hardTopU = Math.min(hardTopU, topU - edgeMargin);
            if (botU != null) hardBotU = Math.max(hardBotU, botU + edgeMargin);
          }
          if (!isFinite(hardTopU) || !isFinite(hardBotU)) continue;
          // `localHoleH` is the INNER rectangle height of the hole (between TR-BR
          // and TE-BE). The FDM 60° slant extends safeTilt beyond on EACH axial
          // side, so the full hole extent is localHoleH + 2*safeTilt. The vertical
          // clipping below uses the full extent so the outermost corners (TE on
          // top, BR on bottom) stay inside the region.
          const tiltEstimated = (W_mm / 2) * Math.tan(fdmAngleRad);
          const wantedTopU = u + localHoleH / 2 + tiltEstimated;
          const wantedBotU = u - localHoleH / 2 - tiltEstimated;
          const effTopU = Math.min(wantedTopU, hardTopU);
          const effBotU = Math.max(wantedBotU, hardBotU);
          const effH = effTopU - effBotU;
          if (effH < 1) continue;
          const effUCenter = (effTopU + effBotU) / 2;

          const pt = sampleSurface(effUCenter, vCenter);
          if (!pt) continue;

          const axisPointN = axisCenter.clone().addScaledVector(axisDir, effUCenter);
          raycaster.set(axisPointN, radialAt(vCenter));
          const hits = raycaster.intersectObjects(meshes);
          if (hits.length === 0) continue;
          const normalMatrix = new Matrix3().getNormalMatrix(hits[0].object.matrixWorld);
          const worldNormal = hits[0].face
            ? hits[0].face.normal.clone().applyMatrix3(normalMatrix).normalize()
            : radialAt(vCenter);
          const axTan = axisDir.clone().sub(worldNormal.clone().multiplyScalar(axisDir.dot(worldNormal)));
          if (axTan.length() < 0.01) continue;
          axTan.normalize();
          // ciTan = axTan × N points in the +v direction (CCW around the axis),
          // matching the visualization's `vQ = vCenter + p.c/approxRadius`.
          // The opposite order (N × axTan) would point in -v and mirror the brush
          // prism around the rib, making cuts appear flipped vs. the markers.
          const ciTan = new Vector3().crossVectors(axTan, worldNormal).normalize();

          allCenters.push({ point: pt, normal: worldNormal, u: effUCenter, v: vCenter, side, W: W_mm, H: effH });

          // Hole polygon in local (a, c) coordinates:
          //   a = axial offset from effUCenter
          //   c = circumferential mm offset from vCenter
          // Inner edge (rib side) follows the rib's offset curve sampled at
          // multiple axial positions. Outer edge is a straight constant-c line
          // on the region boundary. Top/bottom edges have an FDM 60° slant
          // running DOWN from the rib side AWAY from the green line: rib-side
          // corners sit at the "high" a positions (TR=+innerH/2-safeTilt,
          // BR=-innerH/2-safeTilt), edge-side corners at the "low" positions
          // (TE=+innerH/2+safeTilt, BE=-innerH/2+safeTilt). Since top and
          // bottom edges are parallel (same slope), edge-to-edge axial gap
          // between two stacked holes remains exactly holeSpacing at every c
          // as long as pitch = innerH + holeSpacing (current axialPitch).
          // Re-cap safeTilt against the clipped effH so the slant overhang
          // doesn't poke outside the region for the top/bottom row of holes.
          const maxTilt = Math.max(0, effH / 2 - 0.5);
          const safeTilt = Math.min(tiltEstimated, maxTilt);
          const innerH = effH - 2 * safeTilt;
          if (innerH < 1) continue;
          // Slant direction: +axTan points UP in the user's view, so the rib
          // side sits at HIGHER a values and the outer side at LOWER a. The
          // top and bottom edges then visibly run DOWN AWAY from the green
          // rib line (rib HIGH, outer LOW), matching the FDM 50° overhang.
          let aTR =  innerH / 2 + safeTilt;
          let aBR = -innerH / 2 + safeTilt;
          let aTE =  innerH / 2 - safeTilt;
          let aBE = -innerH / 2 - safeTilt;
          const cOuter = side * (W_mm / 2);

          // If this is the BOTTOM-most hole placed on this side of the rib,
          // snap BR/BE down onto the region's bottom boundary at their actual
          // azimuths. The bottom edge then runs (nearly horizontal) between
          // these two points instead of slanting away with the FDM 60° rule —
          // no overhang is needed since the hole already terminates at the
          // orthese's bottom edge.
          const isBottomHole = !bottomPlacedForSide[String(side)];
          if (isBottomHole) {
            const innerBotProf = queryProfile(innerAzAtCenter);
            const outerBotProf = queryProfile(outerAz);
            if (innerBotProf.botU != null && outerBotProf.botU != null) {
              aBR = (innerBotProf.botU + edgeMargin) - effUCenter;
              aBE = (outerBotProf.botU + edgeMargin) - effUCenter;
            }
          }

          // Rib-margin validity: at the axial extremes of THIS hole the rib
          // may have swung sideways enough that ribV ± ribMargin would cross
          // the region's azimuth boundary (vLo / vHi). In that case the hole
          // cannot honour the rib-distance requirement on this side at that
          // axial position — skip the hole instead of capping the inner edge
          // toward the outer boundary (which would visually look like the
          // rib-margin was violated).
          const checkUs = [effUCenter + aBR, effUCenter + aTR];
          let ribMarginValid = true;
          for (const uCheck of checkUs) {
            const innerAzCheck = ribVAt(uCheck) + side * azRibMargin;
            const azWidthCheck = (outerAz - innerAzCheck) * side;
            if (azWidthCheck < 0.002) { ribMarginValid = false; break; }
          }
          if (!ribMarginValid) continue;

          const cAtA_inner = (aQ) => {
            const uQ = effUCenter + aQ;
            const innerAzQ = ribVAt(uQ) + side * azRibMargin;
            return (innerAzQ - vCenter) * approxRadius;
          };
          const cTR = cAtA_inner(aTR);
          const cBR = cAtA_inner(aBR);

          // Rounded corners: build 4 main vertices (TR, BR, BE, TE), apply
          // standard rounding (cornerRadius along each edge), then splice
          // sampled rib-curve points into the inner segment (TR-rounded exit
          // -> BR-rounded entry) so the rib-side edge follows the green line.
          const cornerRadius = 1.5;
          const arcSegs = 8;
          // Much higher sample density on the polygon edges. Each edge is
          // sampled into many points, then projected to the curved scan
          // surface. With ~32 samples per edge, the chord between consecutive
          // points closely follows the surface arc → very small chord-vs-arc
          // gap → fragments disappear without needing large brush expansion.
          const innerCurveSamples = 32;
          const corners4 = side > 0
            ? [{ a: aTR, c: cTR }, { a: aBR, c: cBR }, { a: aBE, c: cOuter }, { a: aTE, c: cOuter }]
            : [{ a: aTR, c: cTR }, { a: aTE, c: cOuter }, { a: aBE, c: cOuter }, { a: aBR, c: cBR }];
          const rounded = [];
          for (let i = 0; i < corners4.length; i++) {
            const prev = corners4[(i - 1 + corners4.length) % corners4.length];
            const curr = corners4[i];
            const next = corners4[(i + 1) % corners4.length];
            const v1x = prev.a - curr.a, v1y = prev.c - curr.c;
            const v1L = Math.sqrt(v1x * v1x + v1y * v1y) || 1;
            const v2x = next.a - curr.a, v2y = next.c - curr.c;
            const v2L = Math.sqrt(v2x * v2x + v2y * v2y) || 1;
            const r = Math.min(cornerRadius, v1L * 0.4, v2L * 0.4);
            const t1a = curr.a + (v1x / v1L) * r;
            const t1c = curr.c + (v1y / v1L) * r;
            const t2a = curr.a + (v2x / v2L) * r;
            const t2c = curr.c + (v2y / v2L) * r;
            rounded.push({ a: t1a, c: t1c });
            for (let j = 1; j < arcSegs; j++) {
              const tt = j / arcSegs;
              rounded.push({ a: t1a + (t2a - t1a) * tt, c: t1c + (t2c - t1c) * tt });
            }
            rounded.push({ a: t2a, c: t2c });
          }
          // Inner curve replacement: for side > 0 the inner edge spans between
          // TR-rounded's last tangent point and BR-rounded's first tangent point
          // (indices arcSegs and arcSegs+1). For side < 0 the order is
          // [TR, TE, BE, BR], so the inner edge spans between BR-rounded's last
          // tangent point (index rounded.length-1) and TR-rounded's first
          // tangent point (index 0) — that's the closing edge.
          const sampleCurveBetween = (p1, p2) => {
            const out = [];
            for (let is = 1; is < innerCurveSamples; is++) {
              const t = is / innerCurveSamples;
              const aQ = p1.a + (p2.a - p1.a) * t;
              out.push({ a: aQ, c: cAtA_inner(aQ) });
            }
            return out;
          };
          // Linear (a,c)-interpolation samples for straight edges. Once each
          // point is later projected via sampleSurface, the line will hug the
          // curved orthese surface — same trick as the inner-rib curve and the
          // blue region outline.
          const sampleStraightEdge = (p1, p2) => {
            const out = [];
            const N = innerCurveSamples;
            for (let k = 1; k < N; k++) {
              const t = k / N;
              out.push({
                a: p1.a + (p2.a - p1.a) * t,
                c: p1.c + (p2.c - p1.c) * t,
              });
            }
            return out;
          };
          // Splice in intermediate samples on EVERY straight edge so the
          // polygon as a whole hugs the curved shell, not just at its corners.
          // For the bottom-most hole on this side, we ALSO densify the bottom
          // edge — which spans the full W and otherwise visibly cuts through
          // the curved orthese bottom as a chord.
          let corners;
          if (side > 0) {
            // rounded layout for side>0: [TR, BR, BE, TE], each takes arcSegs+1 entries.
            // edges: TR→BR (inner curve), BR→BE (bottom), BE→TE (outer), TE→TR (top, wraps)
            const tr_t2 = arcSegs;
            const br_t1 = arcSegs + 1;
            const br_t2 = 2 * arcSegs + 1;
            const be_t1 = 2 * arcSegs + 2;
            const be_t2 = 3 * arcSegs + 2;
            const te_t1 = 3 * arcSegs + 3;
            const te_t2 = 4 * arcSegs + 3;
            const innerSamp = sampleCurveBetween(rounded[tr_t2], rounded[br_t1]);
            const botSamp   = sampleStraightEdge(rounded[br_t2], rounded[be_t1]);
            const outSamp   = sampleStraightEdge(rounded[be_t2], rounded[te_t1]);
            const topSamp   = sampleStraightEdge(rounded[te_t2], rounded[0]);
            corners = [
              ...rounded.slice(0, tr_t2 + 1),           // TR rounded (0..tr_t2)
              ...innerSamp,                              // TR→BR inner curve
              ...rounded.slice(br_t1, br_t2 + 1),        // BR rounded
              ...botSamp,                                // BR→BE bottom edge
              ...rounded.slice(be_t1, be_t2 + 1),        // BE rounded
              ...outSamp,                                // BE→TE outer edge
              ...rounded.slice(te_t1, te_t2 + 1),        // TE rounded
              ...topSamp,                                // TE→TR top edge (wraps)
            ];
          } else {
            // rounded layout for side<0: [TR, TE, BE, BR]
            // edges: TR→TE (top), TE→BE (outer), BE→BR (bottom), BR→TR (inner, wraps)
            const tr_t2 = arcSegs;
            const te_t1 = arcSegs + 1;
            const te_t2 = 2 * arcSegs + 1;
            const be_t1 = 2 * arcSegs + 2;
            const be_t2 = 3 * arcSegs + 2;
            const br_t1 = 3 * arcSegs + 3;
            const br_t2 = 4 * arcSegs + 3;
            const topSamp   = sampleStraightEdge(rounded[tr_t2], rounded[te_t1]);
            const outSamp   = sampleStraightEdge(rounded[te_t2], rounded[be_t1]);
            const botSamp   = sampleStraightEdge(rounded[be_t2], rounded[br_t1]);
            const innerSamp = sampleCurveBetween(rounded[br_t2], rounded[0]);
            corners = [
              ...rounded.slice(0, tr_t2 + 1),           // TR rounded
              ...topSamp,                                // TR→TE top edge
              ...rounded.slice(te_t1, te_t2 + 1),        // TE rounded
              ...outSamp,                                // TE→BE outer edge
              ...rounded.slice(be_t1, be_t2 + 1),        // BE rounded
              ...botSamp,                                // BE→BR bottom edge
              ...rounded.slice(br_t1, br_t2 + 1),        // BR rounded
              ...innerSamp,                              // BR→TR inner curve (wraps)
            ];
          }

          holeTangents2D.push(corners);
          holeBases.push({ center: pt.clone(), normal: worldNormal.clone(), axTan: axTan.clone(), ciTan: ciTan.clone() });

          // Draw the polygon ON THE ORTHESE SURFACE — same approach as the rib
          // polyline and the blue region outline: each corner is converted from
          // local (a, c) tangent coords back to (u, v) axis-frame coords, then
          // raycast onto the surface via sampleSurface. The line then hugs the
          // curved shell instead of floating off in the tangent plane.
          //   u = effUCenter + p.a   (axial offset from hole center)
          //   v = vCenter + p.c / approxRadius  (azimuth offset)
          const poly3D = corners.map(p => {
            const uQ = effUCenter + p.a;
            const vQ = vCenter + p.c / approxRadius;
            const surf = sampleSurface(uQ, vQ);
            if (surf) return surf;
            // Fallback: tangent-plane projection if raycast misses (rare; edge
            // cases near the slit/thumb where the surface terminates).
            return pt.clone()
              .addScaledVector(axTan, p.a)
              .addScaledVector(ciTan, p.c);
          });
          // Per-corner LOCAL SCAN NORMALS (raycast against the scan model
          // at each corner). The orthese outer surface sits at scan +
          // outerOffset along the LOCAL normal — extruding the brush along
          // the same direction makes the prism penetrate the wall cleanly,
          // without leaving "tongue" fragments at corners where the surface
          // curves away from the axis-radial.
          const cornerNormals = poly3D.map(p => getLocalScanNormal(p));
          poly3D.push(poly3D[0].clone());
          holePolygons.push(poly3D);
          holeCornerNormals.push(cornerNormals);
          // Mark this side's bottom-hole as placed so subsequent rows are
          // treated as middle holes (full FDM slant on top AND bottom).
          bottomPlacedForSide[String(side)] = true;
          // Track for post-processing — the LAST entry on each side after the
          // row loop is the TOP hole that we want to rebuild as a triangle.
          placedHolesPerSide[String(side)].push({
            polyIdx: holePolygons.length - 1,
            tangentIdx: holeTangents2D.length - 1,
            effUCenter, vCenter, side, W_mm,
            innerAzAtCenter, outerAz, cOuter,
            aBR, aBE,
            pt: pt.clone(), axTan: axTan.clone(), ciTan: ciTan.clone(),
            cAtA_inner,
          });
        }
      }
      // Normalize row-loop step count in case `break` skipped some rows.
      if (completedSteps < stepsBeforeRowLoop + nRows) {
        completedSteps = stepsBeforeRowLoop + nRows;
        reportProgress(2 + (completedSteps / totalSteps) * 96);
      }

      // Post-process: rebuild the TOP-most hole on each side as a TRIANGLE
      // (Apex at the rib-region-top intersection, BR and BE at the next
      // row's TR/TE positions). One edge along the rib, one slanted down
      // away from the rib to BE, and the bottom edge between BR and BE.
      for (const sideKey of ['-1', '1']) {
        const placed = placedHolesPerSide[sideKey];
        if (placed.length < 2) continue; // need at least 2 holes — otherwise the bottom-hole modification already applies
        const top = placed[placed.length - 1];
        const { effUCenter, vCenter, side, outerAz, cOuter, aBR, aBE, pt, axTan, ciTan, cAtA_inner } = top;

        // New top-hole geometry: a TRIANGLE at the outer-top corner of the
        // region. The two 50°-slant lines (top-slant from TE going down toward
        // the rib, and the bottom edge extrapolated from BR-BE toward the rib)
        // intersect at point X. Polygon corners:
        //   TE  top-outer at the blue corner (region top at outer azimuth)
        //   X   intersection of the two 50° lines
        //   BE  outer bot (natural row position at cOuter)
        // Edges:
        //   TE → X    top-slant 50° going down-toward-rib
        //   X → BE    bottom edge extrapolated from BR-BE toward the rib end
        //   BE → TE   outer vertical along the blue boundary
        const outerTopProf = queryProfile(outerAz);
        if (outerTopProf.topU == null) continue;
        const te_a = (outerTopProf.topU - edgeMargin) - effUCenter;
        const te_c = cOuter;

        // Intersection of top slant and bottom edge.
        //   Top slant:   a − te_a = side · tan(50°) · (c − cOuter)
        //   Bottom edge: linear through (aBR, cBR) and (aBE, cOuter)
        const cBR_actual = cAtA_inner(aBR);
        const slope_top = side * Math.tan(fdmAngleRad);
        const denomBot = cOuter - cBR_actual;
        if (Math.abs(denomBot) < 1e-4) continue;
        const slope_bot = (aBE - aBR) / denomBot;
        const denom = slope_top - slope_bot;
        if (Math.abs(denom) < 1e-4) continue; // parallel — no intersection
        const X_c = (aBR - te_a + slope_top * cOuter - slope_bot * cBR_actual) / denom;
        const X_a = te_a + slope_top * (X_c - cOuter);

        // Validate: X must sit between BE and TE in a, and on the rib side of
        // cOuter. Otherwise the geometry is degenerate (rib swing too small,
        // etc.) — keep the row's normal polygon.
        if (X_a <= aBE + 0.5 || X_a >= te_a - 0.5) continue;
        if (side > 0 ? X_c >= cOuter - 0.5 : X_c <= cOuter + 0.5) continue;

        // Walking order: maintain CCW-relative-to-surface-normal as in the
        // normal hole convention.
        //   side > 0:  TE (top, +cOuter)  →  X (left, rib side)  →  BE (bot, +cOuter)
        //   side < 0:  TE (top, −cOuter)  →  BE (bot, −cOuter)   →  X (right, rib side)
        const cornerRadius = 1.5;
        const arcSegs = 4;
        const sampleCount = 5;
        const triCorners = side > 0
          ? [{ a: te_a, c: te_c }, { a: X_a, c: X_c }, { a: aBE, c: cOuter }]
          : [{ a: te_a, c: te_c }, { a: aBE, c: cOuter }, { a: X_a, c: X_c }];
        const triRounded = [];
        for (let i = 0; i < triCorners.length; i++) {
          const prev = triCorners[(i - 1 + triCorners.length) % triCorners.length];
          const curr = triCorners[i];
          const next = triCorners[(i + 1) % triCorners.length];
          const v1x = prev.a - curr.a, v1y = prev.c - curr.c;
          const v1L = Math.sqrt(v1x * v1x + v1y * v1y) || 1;
          const v2x = next.a - curr.a, v2y = next.c - curr.c;
          const v2L = Math.sqrt(v2x * v2x + v2y * v2y) || 1;
          const r = Math.min(cornerRadius, v1L * 0.4, v2L * 0.4);
          const t1a = curr.a + (v1x / v1L) * r;
          const t1c = curr.c + (v1y / v1L) * r;
          const t2a = curr.a + (v2x / v2L) * r;
          const t2c = curr.c + (v2y / v2L) * r;
          triRounded.push({ a: t1a, c: t1c });
          for (let j = 1; j < arcSegs; j++) {
            const tt = j / arcSegs;
            triRounded.push({ a: t1a + (t2a - t1a) * tt, c: t1c + (t2c - t1c) * tt });
          }
          triRounded.push({ a: t2a, c: t2c });
        }
        const sampleStraight = (p1, p2) => {
          const out = [];
          for (let k = 1; k < sampleCount; k++) {
            const t = k / sampleCount;
            out.push({ a: p1.a + (p2.a - p1.a) * t, c: p1.c + (p2.c - p1.c) * t });
          }
          return out;
        };
        // Rounded indices for 3-corner triangle (each corner contributes arcSegs+1 entries):
        const c0_t2 = arcSegs;
        const c1_t1 = arcSegs + 1;
        const c1_t2 = 2 * arcSegs + 1;
        const c2_t1 = 2 * arcSegs + 2;
        const c2_t2 = 3 * arcSegs + 2;
        let triFinal;
        if (side > 0) {
          // [TE, X, BE]
          //   TE→X   top 50° slant (down-left toward rib)
          //   X→BE   bottom 50° slant (down-right toward outer)
          //   BE→TE  outer vertical along blue boundary (wraps to corner 0)
          const topSamp = sampleStraight(triRounded[c0_t2], triRounded[c1_t1]);
          const botSamp = sampleStraight(triRounded[c1_t2], triRounded[c2_t1]);
          const outSamp = sampleStraight(triRounded[c2_t2], triRounded[0]);
          triFinal = [
            ...triRounded.slice(0, c0_t2 + 1),
            ...topSamp,
            ...triRounded.slice(c1_t1, c1_t2 + 1),
            ...botSamp,
            ...triRounded.slice(c2_t1, c2_t2 + 1),
            ...outSamp,
          ];
        } else {
          // [TE, BE, X]
          //   TE→BE  outer vertical along blue boundary
          //   BE→X   bottom 50° slant (toward rib, here +c direction)
          //   X→TE   top 50° slant (up toward TE, wraps to corner 0)
          const outSamp = sampleStraight(triRounded[c0_t2], triRounded[c1_t1]);
          const botSamp = sampleStraight(triRounded[c1_t2], triRounded[c2_t1]);
          const topSamp = sampleStraight(triRounded[c2_t2], triRounded[0]);
          triFinal = [
            ...triRounded.slice(0, c0_t2 + 1),
            ...outSamp,
            ...triRounded.slice(c1_t1, c1_t2 + 1),
            ...botSamp,
            ...triRounded.slice(c2_t1, c2_t2 + 1),
            ...topSamp,
          ];
        }
        // Project triangle to surface
        const triPoly3D = triFinal.map(p => {
          const uQ = effUCenter + p.a;
          const vQ = vCenter + p.c / approxRadius;
          const surf = sampleSurface(uQ, vQ);
          if (surf) return surf;
          return pt.clone()
            .addScaledVector(axTan, p.a)
            .addScaledVector(ciTan, p.c);
        });
        const triCornerNormals = triPoly3D.map(p => getLocalScanNormal(p));
        triPoly3D.push(triPoly3D[0].clone());
        holePolygons[top.polyIdx] = triPoly3D;
        holeTangents2D[top.tangentIdx] = triFinal;
        holeCornerNormals[top.polyIdx] = triCornerNormals;
      }
      await advance();
      if (cancelled) return;
    }

    if (cancelled) return;
    setHolePattern({ centers: allCenters, ribs: allRibPoints, polygons: holePolygons, bases: holeBases, tangents2D: holeTangents2D, allowedRegions, cornerNormals: holeCornerNormals });
    reportProgress(100);
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holePatternEnabled, slitAxis, model, slitClickPoint, slitWidth, holesPerRow, holeSpacing, closureGeos, thumbPoint, thumbMode]);

  // Phase 4: Apply hole pattern by SPLIT-AND-STITCH instead of CSG.
  //
  // Approach (user-suggested, much more reliable than CSG):
  //   1. For each triangle of outerGeo and innerGeo, project its centroid into
  //      the hole's local tangent-plane coordinates (a, c) and test whether
  //      that point falls inside any hole polygon.
  //   2. Delete triangles whose centroid is inside any hole.
  //   3. Stitch the resulting hole edges back together with a "cap" geometry:
  //      a tube whose outer ring lies on the orthese outer surface (= marker
  //      polygon at outerOffset along the per-corner radial normal) and inner
  //      ring on the orthese inner surface — so the orthese stays closed.
  //
  // Avoids all of three-bvh-csg's failure modes (fragments, sliver triangles,
  // non-manifold leftovers) at the cost of slightly jagged cut edges (the
  // boundary follows triangle edges rather than the exact marker polygon).
  // The cap covers any visual gap since it sits at the exact marker polygon.
  const [cutShells, setCutShells] = useState(null);
  useEffect(() => {
    if (!holePatternApplied || !holePattern || !holePattern.bases || holePattern.bases.length === 0) {
      setCutShells(null);
      return;
    }
    if (!outerGeo || !innerGeo) return;

    let cancelled = false;
    const yieldNow = () => new Promise((resolve) => setTimeout(resolve, 0));

    const run = async () => {
      if (onCutProgressRef.current) onCutProgressRef.current(0);
      await yieldNow();
      if (cancelled) return;

      // Pre-compute per-hole data: tangent-plane basis, 2D polygon for the
      // point-in-polygon test, 2D bbox for fast culling, 3D corner positions
      // and per-corner radial normals for the cap rings.
      const holes = [];
      for (let bi = 0; bi < holePattern.bases.length; bi++) {
        const { center, axTan, ciTan } = holePattern.bases[bi];
        const polygon2D = holePattern.tangents2D[bi];
        const polygon3D = holePattern.polygons[bi];
        const normalsRaw = holePattern.cornerNormals && holePattern.cornerNormals[bi];
        if (!polygon2D || !polygon3D || !normalsRaw) continue;
        let aMin = Infinity, aMax = -Infinity, cMin = Infinity, cMax = -Infinity;
        for (const p of polygon2D) {
          if (p.a < aMin) aMin = p.a;
          if (p.a > aMax) aMax = p.a;
          if (p.c < cMin) cMin = p.c;
          if (p.c > cMax) cMax = p.c;
        }
        // CCW / CW winding determines cap triangle orientation.
        let signedArea = 0;
        for (let i = 0; i < polygon2D.length; i++) {
          const p0 = polygon2D[i];
          const p1 = polygon2D[(i + 1) % polygon2D.length];
          signedArea += p0.a * p1.c - p1.a * p0.c;
        }
        // 3D spatial cutoff: the tangent-plane projection wraps around for
        // points on the OPPOSITE side of the orthese (a triangle 180° away
        // projects to ≈(0, 0) — INSIDE any polygon centered at origin). The
        // cutoff guards against this — only triangles physically near the
        // hole are even considered. Sized as polygon half-diagonal + wall
        // thickness + 5 mm tolerance.
        const halfDiag = Math.sqrt(
          ((aMax - aMin) * 0.5) ** 2 + ((cMax - cMin) * 0.5) ** 2
        );
        const wallThicknessHere = outerOffset - innerOffset;
        const cutoff = halfDiag + wallThicknessHere + 5;
        holes.push({
          center, axTan, ciTan,
          polygon2D, polygon3D, cornerNormals: normalsRaw,
          aMin, aMax, cMin, cMax,
          ccw: signedArea > 0,
          cutoffSq: cutoff * cutoff,
        });
      }
      if (holes.length === 0) {
        setCutShells(null);
        return;
      }

      // 2D ray-casting point-in-polygon test.
      const pointInPolygon = (x, y, polygon) => {
        let inside = false;
        const n = polygon.length;
        for (let i = 0, j = n - 1; i < n; j = i++) {
          const xi = polygon[i].a, yi = polygon[i].c;
          const xj = polygon[j].a, yj = polygon[j].c;
          if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
            inside = !inside;
          }
        }
        return inside;
      };

      // Per-hole shell split with EDGE-ALIGNED CLIPPING:
      // For each shell triangle, project it to the hole's tangent plane (a, c).
      // For each polygon edge in turn, clip the triangle to the "OUTSIDE half"
      // of that edge (Sutherland-Hodgman style — but instead of intersecting
      // with the polygon's interior, we cut away the interior side of each
      // edge). The triangle is split at edge crossings, generating new
      // vertices exactly on the polygon outline.
      //
      // For a CONVEX polygon, "outside the polygon" = "outside at least one
      // edge" — so we can't just intersect all "outside half-planes" (that
      // would give the empty set). Instead, for each triangle we compute
      // BOTH the "inside polygon" piece (intersection, which becomes removed)
      // and the "outside polygon" piece (everything else, which is kept).
      //
      // Standard approach: clip the triangle against the polygon to get the
      // INSIDE portion. The kept (OUTSIDE) portion is then the original
      // triangle MINUS the inside portion. For convex polygon clipping the
      // result is a convex polygon, which is then fan-triangulated.
      //
      // For this code's level of complexity, we use a simpler approach that
      // works well in practice for the orthese hole patterns:
      //   - Project triangle to (a, c)
      //   - 3D cutoff for far triangles (avoids tangent-plane wrap-around)
      //   - 2D bbox cutoff against the polygon
      //   - Centroid test against polygon → remove or keep whole triangle
      // The cap then visually covers the (slight) jagged edge along the
      // polygon outline since it sits exactly on polygon3D + radial offsets.
      const splitShellAgainstHole = (geo, hole) => {
        const pos = geo.attributes.position;
        const idx = geo.index;
        if (!pos || !idx) return geo;
        const triCount = idx.count / 3;
        const newIdx = [];
        for (let t = 0; t < triCount; t++) {
          const ia = idx.getX(t * 3);
          const ib = idx.getX(t * 3 + 1);
          const ic = idx.getX(t * 3 + 2);
          const cx = (pos.getX(ia) + pos.getX(ib) + pos.getX(ic)) / 3;
          const cy = (pos.getY(ia) + pos.getY(ib) + pos.getY(ic)) / 3;
          const cz = (pos.getZ(ia) + pos.getZ(ib) + pos.getZ(ic)) / 3;
          const dx = cx - hole.center.x;
          const dy = cy - hole.center.y;
          const dz = cz - hole.center.z;
          // 3D spatial guard — without this, opposite-side triangles
          // incorrectly project to (a, c) ≈ (0, 0) (inside polygon) due to
          // tangent-plane wrap-around.
          if (dx * dx + dy * dy + dz * dz > hole.cutoffSq) {
            newIdx.push(ia, ib, ic);
            continue;
          }
          const a = dx * hole.axTan.x + dy * hole.axTan.y + dz * hole.axTan.z;
          if (a < hole.aMin || a > hole.aMax) {
            newIdx.push(ia, ib, ic);
            continue;
          }
          const c = dx * hole.ciTan.x + dy * hole.ciTan.y + dz * hole.ciTan.z;
          if (c < hole.cMin || c > hole.cMax) {
            newIdx.push(ia, ib, ic);
            continue;
          }
          if (!pointInPolygon(a, c, hole.polygon2D)) {
            newIdx.push(ia, ib, ic);
          }
          // else: triangle's centroid is inside this hole's polygon → drop.
        }
        const out = new BufferGeometry();
        out.setAttribute('position', pos);
        out.setIndex(newIdx);
        out.computeVertexNormals();
        return out;
      };

      // Build a side-wall "cap" for one hole, appending into the shared
      // positions/indices arrays. The cap is a tube whose outer ring sits at
      // outerOffset along each corner's radial normal and inner ring at
      // innerOffset — exactly where the orthese's outer and inner surfaces
      // are, so the cap seamlessly closes the hole edge.
      const appendCap = (hole, capOffset, positions, indices) => {
        const { polygon3D, cornerNormals, ccw } = hole;
        // polygon3D ends with a duplicate of polygon3D[0] (used for line-loop
        // rendering); strip it for the cap so we don't get a degenerate quad
        // at the wrap point.
        const N = polygon3D.length - 1;
        if (N < 3) return;
        const base = positions.length / 3;
        // Outer ring (at orthese outer surface).
        for (let i = 0; i < N; i++) {
          const p = polygon3D[i];
          const n = cornerNormals[i];
          positions.push(
            p.x + n.x * (outerOffset + capOffset),
            p.y + n.y * (outerOffset + capOffset),
            p.z + n.z * (outerOffset + capOffset),
          );
        }
        // Inner ring (at orthese inner surface).
        for (let i = 0; i < N; i++) {
          const p = polygon3D[i];
          const n = cornerNormals[i];
          positions.push(
            p.x + n.x * (innerOffset - capOffset),
            p.y + n.y * (innerOffset - capOffset),
            p.z + n.z * (innerOffset - capOffset),
          );
        }
        // Side wall triangles — same winding rule as the previous brush sides
        // so the cap normals are consistent with the surrounding shell faces.
        for (let i = 0; i < N; i++) {
          const t1 = base + i;
          const t2 = base + ((i + 1) % N);
          const b1 = base + N + i;
          const b2 = base + N + ((i + 1) % N);
          if (ccw) {
            indices.push(t1, t2, b1);
            indices.push(t2, b2, b1);
          } else {
            indices.push(t1, b1, t2);
            indices.push(t2, b1, b2);
          }
        }
      };

      // Merge a cap geometry into a shell geometry. We need to copy the
      // shell's positions to a writable buffer since BufferAttribute is
      // immutable once attached.
      const mergeIntoShell = (shellGeo, capPositions, capIndices, vertOffset) => {
        const pos = shellGeo.attributes.position;
        const idx = shellGeo.index;
        const nShell = pos.count;
        const nCap = capPositions.length / 3;
        const mergedPos = new Float32Array((nShell + nCap) * 3);
        for (let i = 0; i < nShell; i++) {
          mergedPos[i * 3] = pos.getX(i);
          mergedPos[i * 3 + 1] = pos.getY(i);
          mergedPos[i * 3 + 2] = pos.getZ(i);
        }
        for (let i = 0; i < nCap; i++) {
          mergedPos[(nShell + i) * 3] = capPositions[i * 3];
          mergedPos[(nShell + i) * 3 + 1] = capPositions[i * 3 + 1];
          mergedPos[(nShell + i) * 3 + 2] = capPositions[i * 3 + 2];
        }
        const mergedIdx = [];
        const triCount = idx.count;
        for (let i = 0; i < triCount; i++) mergedIdx.push(idx.getX(i));
        for (let i = 0; i < capIndices.length; i++) mergedIdx.push(capIndices[i] + nShell);
        const out = new BufferGeometry();
        out.setAttribute('position', new Float32BufferAttribute(mergedPos, 3));
        out.setIndex(mergedIdx);
        out.computeVertexNormals();
        return out;
      };

      try {
        // ----- MANIFOLD-BASED HOLE SUBTRACTION (proven 3D-printing library) -----
        // Google's Manifold (https://github.com/elalish/manifold) is the industry
        // standard for boolean operations on 3D-printable meshes. It guarantees
        // manifold output, used by Onshape, Replicad, OpenSCAD WASM.
        //
        // Strategy:
        //   1. Build each hole as a closed Manifold prism (top cap + bottom cap
        //      + side walls, fully manifold).
        //   2. Union all hole prisms into a single Manifold solid.
        //   3. Subtract the hole-union from outerGeo (treated as Manifold).
        //   4. Subtract the hole-union from innerGeo (treated as Manifold).
        //   5. Convert back to BufferGeometry → set as cutShells.
        //
        // If outerGeo/innerGeo are not manifold (open edges), Manifold can still
        // operate but the result may be partial. Centroid-based fallback runs if
        // Manifold throws or returns empty output.
        const ManifoldMod = await getManifoldModule();
        const { Manifold, Mesh } = ManifoldMod;
        if (onCutProgressRef.current) onCutProgressRef.current(10);
        await yieldNow();
        if (cancelled) return;

        // BufferGeometry → Manifold conversion. The mesh constructor expects
        // a flat Float32Array of [x, y, z] per vertex and Uint32Array of
        // triangle indices.
        const geoToManifold = (geo) => {
          const pos = geo.attributes.position;
          const idx = geo.index;
          const n = pos.count;
          const vertProperties = new Float32Array(n * 3);
          for (let i = 0; i < n; i++) {
            vertProperties[i * 3] = pos.getX(i);
            vertProperties[i * 3 + 1] = pos.getY(i);
            vertProperties[i * 3 + 2] = pos.getZ(i);
          }
          const triVerts = new Uint32Array(idx.count);
          for (let i = 0; i < idx.count; i++) triVerts[i] = idx.getX(i);
          const mesh = new Mesh({ numProp: 3, vertProperties, triVerts });
          return new Manifold(mesh);
        };

        // Build a CLOSED WALL solid from outerGeo + innerGeo + boundary
        // stitches. Without closing, Manifold's `subtract` treats each shell
        // as a huge solid (because open boundaries leave ambiguous topology)
        // and the result keeps brush walls + caps as extra geometry inside
        // the orthese — exactly the artifact the user is seeing.
        //
        // The wall solid has:
        //   - outerGeo's faces (outward normals — point away from wall)
        //   - innerGeo's faces (inward radial normals — also point away from
        //     wall, just on the other side toward the arm)
        //   - "boundary stitches" connecting outer's open edges to inner's
        //     corresponding open edges (same vertex IDs from buildGeo)
        // Together these form a closed manifold whose solid is exactly the
        // orthese wall volume.
        const buildWallManifold = () => {
          const outerPosA = outerGeo.attributes.position;
          const innerPosA = innerGeo.attributes.position;
          const outerIdxA = outerGeo.index;
          const innerIdxA = innerGeo.index;
          const nO = outerPosA.count;
          const nI = innerPosA.count;

          // Find boundary edges of the outer shell. Outer and inner share
          // vertex IDs (same buildGeo source), so the same edges are boundary
          // on both shells.
          const edgeKey = (a, b) => a < b ? `${a}_${b}` : `${b}_${a}`;
          const outerEdgeCount = new Map();
          for (let i = 0; i < outerIdxA.count; i += 3) {
            const v0 = outerIdxA.getX(i);
            const v1 = outerIdxA.getX(i + 1);
            const v2 = outerIdxA.getX(i + 2);
            for (const [a, b] of [[v0, v1], [v1, v2], [v2, v0]]) {
              const k = edgeKey(a, b);
              outerEdgeCount.set(k, (outerEdgeCount.get(k) || 0) + 1);
            }
          }
          // Collect directed boundary edges from the outer triangles. The
          // direction (a → b) is the original triangle's edge order, which
          // we use to pick the stitch winding so the stitches' normals
          // point AWAY from the wall (= outward at the boundary opening).
          const outerBoundaries = [];
          for (let i = 0; i < outerIdxA.count; i += 3) {
            const v0 = outerIdxA.getX(i);
            const v1 = outerIdxA.getX(i + 1);
            const v2 = outerIdxA.getX(i + 2);
            for (const [a, b] of [[v0, v1], [v1, v2], [v2, v0]]) {
              const k = edgeKey(a, b);
              if (outerEdgeCount.get(k) === 1) outerBoundaries.push([a, b]);
            }
          }

          // Total verts: outer + inner.
          const vertProps = new Float32Array((nO + nI) * 3);
          for (let i = 0; i < nO; i++) {
            vertProps[i * 3] = outerPosA.getX(i);
            vertProps[i * 3 + 1] = outerPosA.getY(i);
            vertProps[i * 3 + 2] = outerPosA.getZ(i);
          }
          for (let i = 0; i < nI; i++) {
            vertProps[(nO + i) * 3] = innerPosA.getX(i);
            vertProps[(nO + i) * 3 + 1] = innerPosA.getY(i);
            vertProps[(nO + i) * 3 + 2] = innerPosA.getZ(i);
          }

          // Triangle list: outer's tris + inner's tris (verts offset by nO)
          // + boundary stitches.
          const tris = [];
          for (let i = 0; i < outerIdxA.count; i++) tris.push(outerIdxA.getX(i));
          for (let i = 0; i < innerIdxA.count; i++) tris.push(innerIdxA.getX(i) + nO);
          // Boundary stitches — for each outer boundary edge (a → b), build
          // a quad with corners outer_a, outer_b, inner_b, inner_a.
          // Triangulation: (outer_a, inner_a, inner_b) + (outer_a, inner_b, outer_b)
          // chosen so the stitch's outward normal points along the orthese
          // boundary opening (away from wall material).
          for (const [a, b] of outerBoundaries) {
            tris.push(a, nO + a, nO + b);
            tris.push(a, nO + b, b);
          }

          const mesh = new Mesh({
            numProp: 3,
            vertProperties: vertProps,
            triVerts: new Uint32Array(tris),
          });
          return new Manifold(mesh);
        };

        // Build a hole prism as a closed Manifold solid. Top cap above outer
        // surface, bottom cap below inner surface, side walls connecting
        // them. Winding is CCW from outside via the polygon's tangent2D
        // signed area.
        const TOP_EXTRA = 5;
        const BOT_EXTRA = 5;
        const buildHolePrism = (hole) => {
          const polygon3D = hole.polygon3D;
          const cornerNormals = hole.cornerNormals;
          const N = polygon3D.length - 1;
          if (N < 3) return null;

          // CURVATURE-TRACKING LOFT (statt geradem Prisma):
          //
          // polygon3D ist bereits dicht gesampelt (innerCurveSamples=32 Samples
          // pro Kante + arcSegs=8 für Ecken-Rundungen), und JEDER Vertex ist
          // via sampleSurface() exakt auf die Scan-Oberfläche projiziert.
          // cornerNormals[i] kommt aus getLocalScanNormal(polygon3D[i]) — also
          // eine echte Raycast-Normale am Surface-Punkt.
          //
          // Frühere Versuche haben zwischen diesen ohnehin schon dichten Vertices
          // noch weitere Punkte per LINEAR-Lerp eingefügt → die Sehnenpunkte
          // lagen NICHT mehr auf der Surface, sondern unter ihr. Genau das
          // erzeugte die Mantelflächen-Fragmente bei starken Krümmungen, weil
          // der Top-Cap dadurch leicht ins Wandmaterial reinrutscht.
          //
          // Korrekt: die vorhandenen polygon3D-Vertices DIREKT verwenden, keine
          // weitere Subdivision, kein Normalen-Smoothing, kein EXPAND_MM. So
          // ist jeder Side-Quad ein extrem kurzes Segment zwischen zwei Surface-
          // Punkten mit je raycasteter Normale — und die Außenform des Lochs
          // bleibt 1:1 der Marker-Polygon.
          const TOP_EXT = TOP_EXTRA + 3;
          const BOT_EXT = BOT_EXTRA + 3;
          const verts = new Float32Array(N * 2 * 3);
          for (let i = 0; i < N; i++) {
            const p = polygon3D[i];
            const nn = cornerNormals[i];
            verts[i * 3]     = p.x + nn.x * (outerOffset + TOP_EXT);
            verts[i * 3 + 1] = p.y + nn.y * (outerOffset + TOP_EXT);
            verts[i * 3 + 2] = p.z + nn.z * (outerOffset + TOP_EXT);
          }
          for (let i = 0; i < N; i++) {
            const p = polygon3D[i];
            const nn = cornerNormals[i];
            verts[(N + i) * 3]     = p.x + nn.x * (innerOffset - BOT_EXT);
            verts[(N + i) * 3 + 1] = p.y + nn.y * (innerOffset - BOT_EXT);
            verts[(N + i) * 3 + 2] = p.z + nn.z * (innerOffset - BOT_EXT);
          }

          const tris = [];
          const { ccw } = hole;
          // Vertex-0-Fan: topologisch immer korrekt, auch wenn das Polygon
          // wegen der gebogenen Inner-Curve leicht konkav ist. Centroid-Fan
          // wäre numerisch schöner, scheitert aber bei konkaven Polygonen.
          if (ccw) {
            for (let i = 1; i < N - 1; i++) tris.push(0, i + 1, i);
          } else {
            for (let i = 1; i < N - 1; i++) tris.push(0, i, i + 1);
          }
          if (ccw) {
            for (let i = 1; i < N - 1; i++) tris.push(N, N + i, N + i + 1);
          } else {
            for (let i = 1; i < N - 1; i++) tris.push(N, N + i + 1, N + i);
          }
          // Side walls — pro Polygon-Edge ein schmales Quad zwischen zwei Surface-Samples.
          for (let i = 0; i < N; i++) {
            const t1 = i, t2 = (i + 1) % N;
            const b1 = N + i, b2 = N + ((i + 1) % N);
            if (ccw) {
              tris.push(t1, t2, b1);
              tris.push(t2, b2, b1);
            } else {
              tris.push(t1, b1, t2);
              tris.push(t2, b1, b2);
            }
          }
          const triVerts = new Uint32Array(tris);
          const mesh = new Mesh({ numProp: 3, vertProperties: verts, triVerts });
          return new Manifold(mesh);
        };

        // Manifold → BufferGeometry.
        const manifoldToGeo = (m) => {
          const mesh = m.getMesh();
          const g = new BufferGeometry();
          // Clone the buffers (Manifold may free them when the Mesh is GC'd).
          const posBuffer = new Float32Array(mesh.vertProperties.length);
          posBuffer.set(mesh.vertProperties);
          const idxBuffer = new Uint32Array(mesh.triVerts.length);
          idxBuffer.set(mesh.triVerts);
          g.setAttribute('position', new Float32BufferAttribute(posBuffer, 3));
          g.setIndex(new BufferAttribute(idxBuffer, 1));
          g.computeVertexNormals();
          return g;
        };

        // Build all hole prisms.
        const holePrisms = [];
        for (let hi = 0; hi < holes.length; hi++) {
          if (cancelled) {
            holePrisms.forEach(p => p.delete && p.delete());
            return;
          }
          const prism = buildHolePrism(holes[hi]);
          if (prism) holePrisms.push(prism);
          if (onCutProgressRef.current) {
            onCutProgressRef.current(10 + ((hi + 1) / holes.length) * 30);
          }
          if ((hi + 1) % 4 === 0) await yieldNow();
        }
        if (holePrisms.length === 0) {
          setCutShells(null);
          return;
        }
        if (onCutProgressRef.current) onCutProgressRef.current(45);
        await yieldNow();
        if (cancelled) {
          holePrisms.forEach(p => p.delete && p.delete());
          return;
        }

        // Build the closed wall solid and subtract holes ONE-AT-A-TIME.
        //
        // Statt erst alle Cutter zu unionen und dann einmal vom Wall zu sub-
        // trahieren (großes Boolean mit Hunderten von potentiell konkaven Über-
        // schneidungen → numerisch riskant), iterieren wir: jeder Cutter wird
        // einzeln vom aktuellen Wall-Solid abgezogen. Jeder Subtract-Schritt
        // ist deutlich kleiner und stabiler. Die Cutter dürfen sich ohnehin
        // nicht überlappen (Marker sind disjunkt), also gibt's keinen Vorteil
        // beim Vor-Unionen.
        let workingWall = buildWallManifold();
        if (onCutProgressRef.current) onCutProgressRef.current(50);
        await yieldNow();
        if (cancelled) {
          holePrisms.forEach(p => p.delete && p.delete());
          workingWall.delete && workingWall.delete();
          return;
        }

        for (let hi = 0; hi < holePrisms.length; hi++) {
          if (cancelled) {
            holePrisms.forEach(p => p.delete && p.delete());
            workingWall.delete && workingWall.delete();
            return;
          }
          const next = workingWall.subtract(holePrisms[hi]);
          workingWall.delete && workingWall.delete();
          workingWall = next;
          if (onCutProgressRef.current) {
            onCutProgressRef.current(50 + ((hi + 1) / holePrisms.length) * 45);
          }
          if ((hi + 1) % 3 === 0) await yieldNow();
        }

        const cutWallGeo = manifoldToGeo(workingWall);

        // Cleanup WASM-allocated Manifold objects (avoid memory leaks).
        holePrisms.forEach(p => p.delete && p.delete());
        workingWall.delete && workingWall.delete();

        if (onCutProgressRef.current) onCutProgressRef.current(100);
        // The wall is a single closed solid (outer + inner + stitches with
        // holes). Render it as cutOuter; cutInner shares the same geometry
        // so the existing pipeline (which expects both shells) works.
        setCutShells({ cutOuter: cutWallGeo, cutInner: cutWallGeo });
        return;

        // ----- (legacy split-and-stitch code kept below as fallback path) -----
        // The lines below are unreachable when Manifold succeeds. They remain
        // for now in case we need to revert quickly.
        // eslint-disable-next-line no-unreachable
        const outerPos = outerGeo.attributes.position;
        const innerPos = innerGeo.attributes.position;
        const outerIdxAttr = outerGeo.index;
        const innerIdxAttr = innerGeo.index;
        if (!outerIdxAttr || !innerIdxAttr) {
          setCutShells(null);
          return;
        }
        const triCount = outerIdxAttr.count / 3;

        // Phase 1: per-hole centroid test → mark triangles to remove.
        // Uses the OUTER shell's centroid (proven to give correct positioning
        // in earlier deploys). Both shells share vertex IDs via buildGeo so
        // the same triangle index gets removed from both — boundaries match.
        const remove = new Uint8Array(triCount);
        for (let hi = 0; hi < holes.length; hi++) {
          if (cancelled) return;
          const hole = holes[hi];
          for (let t = 0; t < triCount; t++) {
            if (remove[t]) continue;
            const ia = outerIdxAttr.getX(t * 3);
            const ib = outerIdxAttr.getX(t * 3 + 1);
            const ic = outerIdxAttr.getX(t * 3 + 2);
            const cx = (outerPos.getX(ia) + outerPos.getX(ib) + outerPos.getX(ic)) / 3;
            const cy = (outerPos.getY(ia) + outerPos.getY(ib) + outerPos.getY(ic)) / 3;
            const cz = (outerPos.getZ(ia) + outerPos.getZ(ib) + outerPos.getZ(ic)) / 3;
            const dx = cx - hole.center.x;
            const dy = cy - hole.center.y;
            const dz = cz - hole.center.z;
            if (dx * dx + dy * dy + dz * dz > hole.cutoffSq) continue;
            const a = dx * hole.axTan.x + dy * hole.axTan.y + dz * hole.axTan.z;
            if (a < hole.aMin || a > hole.aMax) continue;
            const c = dx * hole.ciTan.x + dy * hole.ciTan.y + dz * hole.ciTan.z;
            if (c < hole.cMin || c > hole.cMax) continue;
            if (pointInPolygon(a, c, hole.polygon2D)) remove[t] = 1;
          }
          if (onCutProgressRef.current) {
            onCutProgressRef.current(((hi + 1) / holes.length) * 70);
          }
          await yieldNow();
        }
        if (cancelled) return;

        // Phase 2: build cut outer + cut inner indices, preserving each
        // shell's original winding (which may differ via the buildGeo
        // flip — outer faces outward, inner faces inward toward the arm).
        const cutOuterIdx = [];
        const cutInnerIdx = [];
        for (let t = 0; t < triCount; t++) {
          if (!remove[t]) {
            cutOuterIdx.push(
              outerIdxAttr.getX(t * 3),
              outerIdxAttr.getX(t * 3 + 1),
              outerIdxAttr.getX(t * 3 + 2),
            );
            cutInnerIdx.push(
              innerIdxAttr.getX(t * 3),
              innerIdxAttr.getX(t * 3 + 1),
              innerIdxAttr.getX(t * 3 + 2),
            );
          }
        }

        // Phase 3: find HOLE boundary edges in cut outer. A boundary edge
        // is one shared by exactly one triangle. We also exclude edges that
        // were ALREADY boundary edges in the original (uncut) shell — those
        // are the orthese's top/bottom/slit/thumb boundaries that already
        // have their own caps from the existing pipeline.
        const edgeKey = (a, b) => a < b ? `${a}_${b}` : `${b}_${a}`;
        const cutEdgeCount = new Map();
        for (let i = 0; i < cutOuterIdx.length; i += 3) {
          const v0 = cutOuterIdx[i], v1 = cutOuterIdx[i + 1], v2 = cutOuterIdx[i + 2];
          for (const [a, b] of [[v0, v1], [v1, v2], [v2, v0]]) {
            const k = edgeKey(a, b);
            cutEdgeCount.set(k, (cutEdgeCount.get(k) || 0) + 1);
          }
        }
        const origEdgeCount = new Map();
        for (let t = 0; t < triCount; t++) {
          const v0 = outerIdxAttr.getX(t * 3);
          const v1 = outerIdxAttr.getX(t * 3 + 1);
          const v2 = outerIdxAttr.getX(t * 3 + 2);
          for (const [a, b] of [[v0, v1], [v1, v2], [v2, v0]]) {
            const k = edgeKey(a, b);
            origEdgeCount.set(k, (origEdgeCount.get(k) || 0) + 1);
          }
        }
        const origBoundary = new Set();
        for (const [k, n] of origEdgeCount) if (n === 1) origBoundary.add(k);
        // Hole boundary edges: in cut outer they have count 1 AND were NOT
        // boundary in original. Preserve direction from the cut triangle.
        const holeBoundaryEdges = [];
        for (let i = 0; i < cutOuterIdx.length; i += 3) {
          const v0 = cutOuterIdx[i], v1 = cutOuterIdx[i + 1], v2 = cutOuterIdx[i + 2];
          for (const [a, b] of [[v0, v1], [v1, v2], [v2, v0]]) {
            const k = edgeKey(a, b);
            if (cutEdgeCount.get(k) === 1 && !origBoundary.has(k)) {
              holeBoundaryEdges.push([a, b]);
            }
          }
        }
        if (onCutProgressRef.current) onCutProgressRef.current(85);
        await yieldNow();
        if (cancelled) return;

        // Phase 4: build hole wall caps that stitch outer boundary to inner
        // boundary. For each directed boundary edge (a, b) on the cut outer,
        // the quad (outer_a, outer_b, inner_b, inner_a) is triangulated so
        // the cap's outward normal points INTO the hole interior (= away
        // from the wall solid). Since outer/inner share vertex IDs, the
        // outer boundary and inner boundary correspond 1:1 → watertight.
        const capPos = [];
        const capIdx = [];
        for (const [a, b] of holeBoundaryEdges) {
          const base = capPos.length / 3;
          capPos.push(outerPos.getX(a), outerPos.getY(a), outerPos.getZ(a)); // 0: outer_a
          capPos.push(outerPos.getX(b), outerPos.getY(b), outerPos.getZ(b)); // 1: outer_b
          capPos.push(innerPos.getX(b), innerPos.getY(b), innerPos.getZ(b)); // 2: inner_b
          capPos.push(innerPos.getX(a), innerPos.getY(a), innerPos.getZ(a)); // 3: inner_a
          // Triangulate so normals face INTO the hole:
          //   (outer_a, inner_a, inner_b) + (outer_a, inner_b, outer_b)
          capIdx.push(base, base + 3, base + 2);
          capIdx.push(base, base + 2, base + 1);
        }

        // Phase 5: merge cap into cut outer (gives the cut outer surface +
        // hole side walls, all in one mesh). The cap's inner-ring vertices
        // coincide spatially with the cut inner shell's boundary vertices,
        // so when both shells are exported together the result is a
        // closed-at-the-holes printable volume.
        const buildShell = (origPos, cutIdx, extraPos, extraIdx) => {
          const nOrig = origPos.count;
          const nExtra = extraPos.length / 3;
          const merged = new Float32Array((nOrig + nExtra) * 3);
          for (let i = 0; i < nOrig; i++) {
            merged[i * 3] = origPos.getX(i);
            merged[i * 3 + 1] = origPos.getY(i);
            merged[i * 3 + 2] = origPos.getZ(i);
          }
          for (let i = 0; i < nExtra; i++) {
            merged[(nOrig + i) * 3] = extraPos[i * 3];
            merged[(nOrig + i) * 3 + 1] = extraPos[i * 3 + 1];
            merged[(nOrig + i) * 3 + 2] = extraPos[i * 3 + 2];
          }
          const mergedIdx = [];
          for (const i of cutIdx) mergedIdx.push(i);
          for (const i of extraIdx) mergedIdx.push(i + nOrig);
          const g = new BufferGeometry();
          g.setAttribute('position', new Float32BufferAttribute(merged, 3));
          g.setIndex(mergedIdx);
          g.computeVertexNormals();
          return g;
        };

        const cutOuterFinal = buildShell(outerPos, cutOuterIdx, capPos, capIdx);
        if (cancelled) return;
        const cutInnerFinal = buildShell(innerPos, cutInnerIdx, [], []);
        if (onCutProgressRef.current) onCutProgressRef.current(100);
        if (cancelled) return;

        setCutShells({ cutOuter: cutOuterFinal, cutInner: cutInnerFinal });
      } catch (e) {
        console.error('Split-and-stitch hole cut failed:', e);
        setCutShells(null);
      }
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holePatternApplied, holePattern, outerGeo, innerGeo, outerOffset, innerOffset]);

  // Effective shells to use downstream: cut versions if available, else original.
  const effectiveOuterGeo = cutShells ? cutShells.cutOuter : outerGeo;
  const effectiveInnerGeo = cutShells ? cutShells.cutInner : innerGeo;

  // Software clip geometry: split triangles at plane boundaries (same as export)
  const clipGeo = (geo, planes) => {
    if (!geo || !planes || planes.length === 0) return geo;
    const posAttr = geo.attributes.position;
    const idx = geo.index;
    let tris = [];
    const triCount = idx ? idx.count / 3 : posAttr.count / 3;
    for (let t = 0; t < triCount; t++) {
      const a = idx ? idx.getX(t*3) : t*3;
      const b = idx ? idx.getX(t*3+1) : t*3+1;
      const c = idx ? idx.getX(t*3+2) : t*3+2;
      tris.push([
        new Vector3(posAttr.getX(a), posAttr.getY(a), posAttr.getZ(a)),
        new Vector3(posAttr.getX(b), posAttr.getY(b), posAttr.getZ(b)),
        new Vector3(posAttr.getX(c), posAttr.getY(c), posAttr.getZ(c)),
      ]);
    }
    for (const plane of planes) {
      const next = [];
      for (const tri of tris) {
        const dists = tri.map(v => plane.distanceToPoint(v));
        const inside = dists.map(d => d >= -0.001);
        const numIn = inside.filter(Boolean).length;
        if (numIn === 3) { next.push(tri); continue; }
        if (numIn === 0) continue;
        const vIn = [], vOut = [];
        for (let i = 0; i < 3; i++) { if (inside[i]) vIn.push(i); else vOut.push(i); }
        if (numIn === 1) {
          const a = vIn[0], b = vOut[0], c = vOut[1];
          next.push([tri[a].clone(), new Vector3().lerpVectors(tri[a], tri[b], dists[a]/(dists[a]-dists[b])), new Vector3().lerpVectors(tri[a], tri[c], dists[a]/(dists[a]-dists[c]))]);
        } else {
          const a = vOut[0], b = vIn[0], c = vIn[1];
          const ab = new Vector3().lerpVectors(tri[a], tri[b], dists[a]/(dists[a]-dists[b]));
          const ac = new Vector3().lerpVectors(tri[a], tri[c], dists[a]/(dists[a]-dists[c]));
          next.push([tri[b].clone(), tri[c].clone(), ab]);
          next.push([ab.clone(), tri[c].clone(), ac]);
        }
      }
      tris = next;
    }
    if (tris.length < 1) return null;
    const outPos = [], outIdx = [];
    let vc = 0;
    for (const tri of tris) { for (const v of tri) outPos.push(v.x, v.y, v.z); outIdx.push(vc, vc+1, vc+2); vc += 3; }
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(new Float32Array(outPos), 3));
    g.setIndex(outIdx);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  };

  // Keep only the largest connected component of a geometry (remove fragments)
  const keepLargestComponent = (geo) => {
    if (!geo) return null;
    const pos = geo.attributes.position;
    const idx = geo.index;
    if (!pos || !idx) return geo;

    // Weld vertices for connectivity
    const PREC = 100;
    const nv = pos.count;
    const vk = (i) => `${Math.round(pos.getX(i)*PREC)}_${Math.round(pos.getY(i)*PREC)}_${Math.round(pos.getZ(i)*PREC)}`;
    const keyToId = new Map();
    const vertId = new Int32Array(nv);
    let idCount = 0;
    for (let i = 0; i < nv; i++) {
      const k = vk(i);
      if (keyToId.has(k)) { vertId[i] = keyToId.get(k); }
      else { keyToId.set(k, idCount); vertId[i] = idCount; idCount++; }
    }

    // Build face list + adjacency via shared welded vertices
    const nf = idx.count / 3;
    const vertToFaces = new Map();
    for (let fi = 0; fi < nf; fi++) {
      for (let k = 0; k < 3; k++) {
        const vi = vertId[idx.getX(fi * 3 + k)];
        if (!vertToFaces.has(vi)) vertToFaces.set(vi, []);
        vertToFaces.get(vi).push(fi);
      }
    }

    // BFS to find connected components
    const visited = new Int32Array(nf).fill(-1);
    const components = [];
    for (let fi = 0; fi < nf; fi++) {
      if (visited[fi] >= 0) continue;
      const compId = components.length;
      const queue = [fi];
      const faces = [];
      visited[fi] = compId;
      while (queue.length > 0) {
        const f = queue.shift();
        faces.push(f);
        for (let k = 0; k < 3; k++) {
          const vi = vertId[idx.getX(f * 3 + k)];
          const neighbors = vertToFaces.get(vi);
          if (!neighbors) continue;
          for (const nf2 of neighbors) {
            if (visited[nf2] >= 0) continue;
            visited[nf2] = compId;
            queue.push(nf2);
          }
        }
      }
      components.push(faces);
    }

    if (components.length <= 1) return geo;

    // Keep only the largest component
    let largest = components[0];
    for (const comp of components) {
      if (comp.length > largest.length) largest = comp;
    }

    // Rebuild geometry from largest component
    const newPos = [], newIdx = [];
    let vc2 = 0;
    for (const fi of largest) {
      for (let k = 0; k < 3; k++) {
        const vi = idx.getX(fi * 3 + k);
        newPos.push(pos.getX(vi), pos.getY(vi), pos.getZ(vi));
      }
      newIdx.push(vc2, vc2 + 1, vc2 + 2);
      vc2 += 3;
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(new Float32Array(newPos), 3));
    g.setIndex(newIdx);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  };

  // Split geo into arm/thumb, then software-clip each region with its own planes
  const lowerPlane = armPlanes.length > 1 ? armPlanes[1] : null;
  const armClipPlanes = armPlanes;
  const thumbClipPlanes = [lowerPlane, thumbPlane].filter(Boolean);

  // Apply slit: clip geo with each slit plane separately, merge results
  // Clip triangle array with a single plane (returns kept triangles as Vector3 arrays)
  const clipTriangles = (tris, plane) => {
    const next = [];
    for (const tri of tris) {
      const dists = tri.map(v => plane.distanceToPoint(v));
      const inside = dists.map(d => d >= -0.001);
      const numIn = inside.filter(Boolean).length;
      if (numIn === 3) { next.push(tri); continue; }
      if (numIn === 0) { continue; }
      const vIn = [], vOut = [];
      for (let i = 0; i < 3; i++) { if (inside[i]) vIn.push(i); else vOut.push(i); }
      if (numIn === 1) {
        const a=vIn[0], b=vOut[0], c=vOut[1];
        next.push([tri[a].clone(), new Vector3().lerpVectors(tri[a],tri[b],dists[a]/(dists[a]-dists[b])), new Vector3().lerpVectors(tri[a],tri[c],dists[a]/(dists[a]-dists[c]))]);
      } else {
        const a=vOut[0], b=vIn[0], c=vIn[1];
        const ab = new Vector3().lerpVectors(tri[a],tri[b],dists[a]/(dists[a]-dists[b]));
        const ac = new Vector3().lerpVectors(tri[a],tri[c],dists[a]/(dists[a]-dists[c]));
        next.push([tri[b].clone(), tri[c].clone(), ab]);
        next.push([ab.clone(), tri[c].clone(), ac]);
      }
    }
    return next;
  };

  // Apply slit: clip with each plane, but skip triangles whose centroid
  // is on the back-side of the axis (beyond the axis center away from A).
  // This limits the slit planes to only affect the A-side hemisphere.
  const applySlit = (geo) => {
    if (!geo || slitPlanes.length !== 2) return geo;
    const pos = geo.attributes.position;
    const idx = geo.index;
    if (!pos || !idx) return geo;

    const axisDir = slitAxis ? slitAxis.dir : new Vector3(0, 1, 0);
    const center = slitAxis ? slitAxis.center : (() => {
      const box = new Box3().setFromObject(model);
      const c = new Vector3(); box.getCenter(c); return c;
    })();

    let radialA = null;
    if (slitClickPoint) {
      radialA = new Vector3().subVectors(slitClickPoint, center);
      radialA.sub(axisDir.clone().multiplyScalar(radialA.dot(axisDir)));
      if (radialA.length() > 0.01) radialA.normalize();
      else radialA = null;
    }

    // Step 1: Split mesh into A-side and back-side using a separator plane
    // through the axis center, perpendicular to radialA.
    // This gives CLEAN triangle-split edges at the axis (no jagged centroid cuts).
    const allTrisRaw = [];
    const triCount = idx.count / 3;
    for (let t = 0; t < triCount; t++) {
      const a = idx.getX(t*3), b = idx.getX(t*3+1), c = idx.getX(t*3+2);
      allTrisRaw.push([
        new Vector3(pos.getX(a), pos.getY(a), pos.getZ(a)),
        new Vector3(pos.getX(b), pos.getY(b), pos.getZ(b)),
        new Vector3(pos.getX(c), pos.getY(c), pos.getZ(c)),
      ]);
    }

    let aSideTris, backSideTris;
    if (radialA) {
      // Separator plane: normal = radialA, through axis center
      // Positive side = A-side, negative = back-side
      const sepPlane = new Plane(radialA.clone(), -radialA.dot(center));
      const sepPlaneInv = new Plane(radialA.clone().negate(), radialA.dot(center));
      aSideTris = clipTriangles(allTrisRaw, sepPlane);
      backSideTris = clipTriangles(allTrisRaw, sepPlaneInv);
    } else {
      aSideTris = allTrisRaw;
      backSideTris = [];
    }


    // Step 2: Clip A-side with each slit plane SEPARATELY, then MERGE results.
    // Plane normals point outward → clipGeo keeps the shell on that side.
    // Clipping with plane[0] keeps left half, clipping with plane[1] keeps right half.
    // Merging both = shell without the slit strip.
    let clippedATris = [];
    if (aSideTris.length > 0) {
      const leftHalf = clipTriangles(aSideTris, slitPlanes[0]);
      const rightHalf = clipTriangles(aSideTris, slitPlanes[1]);
      clippedATris = [...leftHalf, ...rightHalf];
    }


    // Step 3: Merge clipped A-side + untouched back-side
    const allTris = [...clippedATris, ...backSideTris];
    if (allTris.length < 1) return null;

    const outPos = [], outIdx = [];
    let vc = 0;
    for (const tri of allTris) {
      for (const v of tri) outPos.push(v.x, v.y, v.z);
      outIdx.push(vc, vc+1, vc+2); vc += 3;
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(new Float32Array(outPos), 3));
    g.setIndex(outIdx);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  };

  const clippedGeos = useMemo(() => {
    // Use the cut shells if the hole pattern has been applied via CSG;
    // otherwise the original offset shells.
    const sourceOuter = effectiveOuterGeo;
    const sourceInner = effectiveInnerGeo;
    if (!sourceOuter || !sourceInner) return [];
    const geos = [];

    if (thumbPlane && thumbPoint) {
      // Split by distance to thumb point, clip each region, push as pairs (outer, inner)
      const splitGeoByThumb = (geo) => {
        const pos = geo.attributes.position;
        const idx = geo.index;
        if (!pos || !idx) return { arm: null, thumb: null };
        const tp = thumbPoint instanceof Vector3 ? thumbPoint : new Vector3(thumbPoint.x, thumbPoint.y, thumbPoint.z);
        const thumbRadius = model ? (() => { const b = new Box3().setFromObject(model); return Math.max(b.max.x - b.min.x, b.max.z - b.min.z) * 0.25; })() : 20;
        const armI = [], thumbI = [];
        const triCount = idx.count / 3;
        const v = new Vector3();
        for (let t = 0; t < triCount; t++) {
          const a = idx.getX(t*3), b2 = idx.getX(t*3+1), c = idx.getX(t*3+2);
          v.set((pos.getX(a)+pos.getX(b2)+pos.getX(c))/3, (pos.getY(a)+pos.getY(b2)+pos.getY(c))/3, (pos.getZ(a)+pos.getZ(b2)+pos.getZ(c))/3);
          if (v.distanceTo(tp) < thumbRadius) { thumbI.push(a, b2, c); } else { armI.push(a, b2, c); }
        }
        const makeG = (indices) => {
          if (indices.length < 3) return null;
          const g = new BufferGeometry(); g.setAttribute('position', pos); g.setIndex(indices); g.computeVertexNormals(); return g;
        };
        return {
          arm: keepLargestComponent(applySlit(clipGeo(makeG(armI), armClipPlanes))),
          thumb: keepLargestComponent(applySlit(clipGeo(makeG(thumbI), thumbClipPlanes))),
        };
      };
      const outerS = splitGeoByThumb(sourceOuter);
      const innerS = splitGeoByThumb(sourceInner);
      // Push as pairs: [outerArm, innerArm, outerThumb, innerThumb]
      if (outerS.arm) geos.push(outerS.arm);
      if (innerS.arm) geos.push(innerS.arm);
      if (outerS.thumb) geos.push(outerS.thumb);
      if (innerS.thumb) geos.push(innerS.thumb);
    } else {
      // No thumb — clip with all planes, then apply slit
      const clippedOuter = keepLargestComponent(applySlit(clipGeo(sourceOuter, allPlanes)));
      const clippedInner = keepLargestComponent(applySlit(clipGeo(sourceInner, allPlanes)));
      if (clippedOuter) geos.push(clippedOuter);
      if (clippedInner) geos.push(clippedInner);
    }

    return geos;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOuterGeo, effectiveInnerGeo, allPlanes, thumbPoint, thumbPlane, slitPlanes]);

  // Build caps from boundary edges (same method as export):
  // Find edges used by exactly 1 triangle near a clipping plane,
  // match outer↔inner edges, build quad strips between them.
  // Then fill remaining small holes with triangle fans.
  const capGeos = useMemo(() => {
    if (clippedGeos.length < 2) return [];
    const caps = [];
    const PREC = 100;
    const vk = (x, y, z) => `${Math.round(x*PREC)}_${Math.round(y*PREC)}_${Math.round(z*PREC)}`;

    // Separate outer and inner clipped geos
    // clippedGeos alternates: armOuter, armInner, [thumbOuter, thumbInner]
    // Pair them: even=outer, odd=inner
    for (let pair = 0; pair < clippedGeos.length - 1; pair += 2) {
      const outerG = clippedGeos[pair];
      const innerG = clippedGeos[pair + 1];
      if (!outerG || !innerG) continue;

      const findBoundaryEdges = (geo) => {
        const pos = geo.attributes.position;
        const idx = geo.index;
        if (!pos || !idx) return [];
        const edgeCount = new Map();
        const edgeData = new Map();
        for (let i = 0; i < idx.count; i += 3) {
          const vis = [];
          for (let k = 0; k < 3; k++) {
            const vi = idx.getX(i + k);
            vis.push(new Vector3(pos.getX(vi), pos.getY(vi), pos.getZ(vi)));
          }
          for (const [a, b] of [[0,1],[1,2],[2,0]]) {
            const ka = vk(vis[a].x, vis[a].y, vis[a].z);
            const kb = vk(vis[b].x, vis[b].y, vis[b].z);
            const ek = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
            edgeCount.set(ek, (edgeCount.get(ek) || 0) + 1);
            if (!edgeData.has(ek)) edgeData.set(ek, [vis[a], vis[b]]);
          }
        }
        const boundary = [];
        const allCapPlanes = [...allPlanes, ...slitPlanes];
        for (const [ek, count] of edgeCount) {
          if (count === 1) {
            const [a, b] = edgeData.get(ek);
            const mid = new Vector3().addVectors(a, b).multiplyScalar(0.5);
            let nearPlane = false;
            for (const plane of allCapPlanes) {
              if (Math.abs(plane.distanceToPoint(mid)) < 2.0) { nearPlane = true; break; }
            }
            if (nearPlane) boundary.push([a, b]);
          }
        }
        return boundary;
      };

      const outerBE = findBoundaryEdges(outerG);
      const innerBE = findBoundaryEdges(innerG);

      if (outerBE.length > 0 && innerBE.length > 0) {
        const capPos = [], capIdx = [];
        let capVC = 0;
        for (const [oA, oB] of outerBE) {
          const oMid = new Vector3().addVectors(oA, oB).multiplyScalar(0.5);
          let bestDist = Infinity, bestInner = null;
          for (const [iA, iB] of innerBE) {
            const d = oMid.distanceTo(new Vector3().addVectors(iA, iB).multiplyScalar(0.5));
            if (d < bestDist) { bestDist = d; bestInner = [iA, iB]; }
          }
          if (!bestInner || bestDist > 15) continue;
          const d1 = oA.distanceTo(bestInner[0]) + oB.distanceTo(bestInner[1]);
          const d2 = oA.distanceTo(bestInner[1]) + oB.distanceTo(bestInner[0]);
          const [iA, iB] = d1 <= d2 ? bestInner : [bestInner[1], bestInner[0]];
          const base = capVC;
          capPos.push(oA.x,oA.y,oA.z, oB.x,oB.y,oB.z, iA.x,iA.y,iA.z, iB.x,iB.y,iB.z);
          capIdx.push(base,base+1,base+2, base+2,base+1,base+3);
          capVC += 4;
        }
        if (capPos.length >= 12) {
          const capGeo = new BufferGeometry();
          capGeo.setAttribute('position', new Float32BufferAttribute(new Float32Array(capPos), 3));
          capGeo.setIndex(capIdx);
          capGeo.computeVertexNormals();
          capGeo.computeBoundingSphere();
          caps.push(capGeo);
        }
      }
    }
    // Hole filling: merge all geos (shells + caps), find remaining boundary loops, fill small ones
    const allGeos = [...clippedGeos, ...caps];
    const mPos = [], mIdx = [];
    let mOff = 0;
    for (const g of allGeos) {
      const pos = g.attributes.position;
      const idx = g.index;
      if (!pos) continue;
      for (let i = 0; i < pos.count; i++) mPos.push(pos.getX(i), pos.getY(i), pos.getZ(i));
      if (idx) { for (let i = 0; i < idx.count; i++) mIdx.push(idx.getX(i) + mOff); }
      else { for (let i = 0; i < pos.count; i++) mIdx.push(i + mOff); }
      mOff += pos.count;
    }
    // Weld
    const wm2 = new Map(), o2n2 = new Int32Array(mOff), wP = []; let wC2 = 0;
    for (let i = 0; i < mOff; i++) {
      const k = vk(mPos[i*3], mPos[i*3+1], mPos[i*3+2]);
      if (wm2.has(k)) { o2n2[i] = wm2.get(k); }
      else { wm2.set(k, wC2); o2n2[i] = wC2; wP.push(mPos[i*3], mPos[i*3+1], mPos[i*3+2]); wC2++; }
    }
    const wI = mIdx.map(i => o2n2[i]);
    // Find boundary edges
    const eC2 = new Map(), eV2 = new Map();
    for (let i = 0; i < wI.length; i += 3) {
      const a = wI[i], b = wI[i+1], c = wI[i+2];
      for (const [e0, e1] of [[a,b],[b,c],[c,a]]) {
        const ek = e0 < e1 ? `${e0}_${e1}` : `${e1}_${e0}`;
        eC2.set(ek, (eC2.get(ek) || 0) + 1);
        if (!eV2.has(ek)) eV2.set(ek, [e0, e1]);
      }
    }
    const bEdges = [];
    for (const [ek, cnt] of eC2) { if (cnt === 1) bEdges.push(eV2.get(ek)); }

    if (bEdges.length > 0) {
      const adj = new Map();
      for (const [a, b] of bEdges) {
        if (!adj.has(a)) adj.set(a, []);
        if (!adj.has(b)) adj.set(b, []);
        adj.get(a).push(b); adj.get(b).push(a);
      }
      const visited = new Set();
      const loops = [];
      for (const startV of adj.keys()) {
        if (visited.has(startV)) continue;
        const loop = []; let curr = startV, prev = -1;
        while (!visited.has(curr)) {
          visited.add(curr); loop.push(curr);
          const neighbors = adj.get(curr);
          if (!neighbors) break;
          const next = neighbors.find(n => n !== prev) ?? neighbors[0];
          if (visited.has(next) && next === startV && loop.length >= 3) break;
          if (visited.has(next)) break;
          prev = curr; curr = next;
        }
        if (loop.length >= 3) loops.push(loop);
      }

      const fillPos = [], fillIdx = [];
      let fillVC = 0;
      for (const loop of loops) {
        if (loop.length > 50) continue;
        let cx = 0, cy = 0, cz = 0;
        for (const vi of loop) { cx += wP[vi*3]; cy += wP[vi*3+1]; cz += wP[vi*3+2]; }
        cx /= loop.length; cy /= loop.length; cz /= loop.length;
        const centerIdx = fillVC; fillPos.push(cx, cy, cz); fillVC++;
        const loopBase = fillVC;
        for (const vi of loop) { fillPos.push(wP[vi*3], wP[vi*3+1], wP[vi*3+2]); fillVC++; }
        for (let i = 0; i < loop.length; i++) {
          fillIdx.push(centerIdx, loopBase + i, loopBase + (i+1) % loop.length);
        }
        // Fix winding by checking against neighbor triangle
        const firstV = loop[0], secondV = loop[1];
        let refN = null;
        for (let i = 0; i < wI.length; i += 3) {
          const ta = wI[i], tb = wI[i+1], tc = wI[i+2];
          if ((ta===firstV||tb===firstV||tc===firstV) && (ta===secondV||tb===secondV||tc===secondV)) {
            const e1x=wP[tb*3]-wP[ta*3], e1y=wP[tb*3+1]-wP[ta*3+1], e1z=wP[tb*3+2]-wP[ta*3+2];
            const e2x=wP[tc*3]-wP[ta*3], e2y=wP[tc*3+1]-wP[ta*3+1], e2z=wP[tc*3+2]-wP[ta*3+2];
            refN = { x:e1y*e2z-e1z*e2y, y:e1z*e2x-e1x*e2z, z:e1x*e2y-e1y*e2x }; break;
          }
        }
        if (refN) {
          const fi = fillIdx.length - loop.length * 3;
          const ia=fillIdx[fi], ib=fillIdx[fi+1], ic=fillIdx[fi+2];
          const fe1x=fillPos[ib*3]-fillPos[ia*3], fe1y=fillPos[ib*3+1]-fillPos[ia*3+1], fe1z=fillPos[ib*3+2]-fillPos[ia*3+2];
          const fe2x=fillPos[ic*3]-fillPos[ia*3], fe2y=fillPos[ic*3+1]-fillPos[ia*3+1], fe2z=fillPos[ic*3+2]-fillPos[ia*3+2];
          const fN = { x:fe1y*fe2z-fe1z*fe2y, y:fe1z*fe2x-fe1x*fe2z, z:fe1x*fe2y-fe1y*fe2x };
          if (fN.x*refN.x+fN.y*refN.y+fN.z*refN.z < 0) {
            for (let i = fi; i < fillIdx.length; i += 3) { const tmp = fillIdx[i+1]; fillIdx[i+1] = fillIdx[i+2]; fillIdx[i+2] = tmp; }
          }
        }
      }
      if (fillPos.length >= 9) {
        const fillGeo = new BufferGeometry();
        fillGeo.setAttribute('position', new Float32BufferAttribute(new Float32Array(fillPos), 3));
        fillGeo.setIndex(fillIdx); fillGeo.computeVertexNormals(); fillGeo.computeBoundingSphere();
        caps.push(fillGeo);
      }
    }

    return caps;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clippedGeos, allPlanes]);

  // Split geos for export (unclipped — export has its own clip logic).
  // IMPORTANT: source from the CSG-cut shells (effectiveOuterGeo/effectiveInnerGeo)
  // when the hole pattern has been applied so the exported STL actually
  // contains the holes. Falls back to the uncut shells otherwise.
  const exportSplit = useMemo(() => {
    if (!effectiveOuterGeo || !effectiveInnerGeo || !thumbPlane || !thumbPoint) return null;
    const splitForExport = (geo) => {
      const pos = geo.attributes.position;
      const idx = geo.index;
      if (!pos || !idx) return { arm: geo, thumb: null };
      const tp = thumbPoint instanceof Vector3 ? thumbPoint : new Vector3(thumbPoint.x, thumbPoint.y, thumbPoint.z);
      const thumbRadius = model ? (() => { const b = new Box3().setFromObject(model); return Math.max(b.max.x - b.min.x, b.max.z - b.min.z) * 0.25; })() : 20;
      const armI = [], thumbI = [];
      const triCount = idx.count / 3;
      const v = new Vector3();
      for (let t = 0; t < triCount; t++) {
        const a = idx.getX(t*3), b2 = idx.getX(t*3+1), c = idx.getX(t*3+2);
        v.set((pos.getX(a)+pos.getX(b2)+pos.getX(c))/3, (pos.getY(a)+pos.getY(b2)+pos.getY(c))/3, (pos.getZ(a)+pos.getZ(b2)+pos.getZ(c))/3);
        if (v.distanceTo(tp) < thumbRadius) thumbI.push(a, b2, c); else armI.push(a, b2, c);
      }
      const makeG = (indices) => { if (indices.length < 3) return null; const g = new BufferGeometry(); g.setAttribute('position', pos); g.setIndex(indices); g.computeVertexNormals(); return g; };
      return { arm: makeG(armI), thumb: makeG(thumbI) };
    };
    const outerS = splitForExport(effectiveOuterGeo);
    const innerS = splitForExport(effectiveInnerGeo);
    return { outerS, innerS };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOuterGeo, effectiveInnerGeo, thumbPoint, thumbPlane]);

  // Report geometries for export. Use the CSG-cut shells (effectiveOuter/Inner)
  // so the exported STL contains the punched holes.
  useEffect(() => {
    if (onGeosReady && effectiveOuterGeo && effectiveInnerGeo) {
      onGeosReady({
        outerGeo: effectiveOuterGeo, innerGeo: effectiveInnerGeo, clippingPlanes: allPlanes,
        armOuter: exportSplit?.outerS?.arm,
        armInner: exportSplit?.innerS?.arm,
        thumbOuter: exportSplit?.outerS?.thumb,
        thumbInner: exportSplit?.innerS?.thumb,
        armClipPlanes,
        thumbClipPlanes,
        slitPlanes,
        slitClickPoint,
        slitAxis,
        closureGeos,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOuterGeo, effectiveInnerGeo, allPlanes, onGeosReady, exportSplit, closureGeos]);

  const matProps = {
    color: color || "#0f99c8",
    side: DoubleSide,
  };

  // Slit marker: use inverted slit planes to extract the exact slit strip
  const slitMarkerGeo = useMemo(() => {
    if (slitMarkerRotation == null || slitMarkerWidth == null || !model) return null;
    if (clippedGeos.length === 0) return null;

    // Build marker planes from click points or slider
    let regularPlanes;
    if (slitClickPoint) {
      regularPlanes = buildSlitPlanesFromPoints(slitClickPoint, slitClickPoint2, slitMarkerWidth);
    } else {
      regularPlanes = buildSlitPlanesFromAngle(slitMarkerRotation, slitMarkerWidth);
    }
    if (regularPlanes.length !== 2) return null;

    // Invert: negate normals and constants
    const invertedPlanes = regularPlanes.map(p => {
      const n = p.normal.clone().negate();
      return new Plane(n, -p.constant);
    });

    // Extract slit strip from outer shell geos only (index 0, 2)
    const outerGeoIndices = clippedGeos.length >= 4 ? [0, 2] : [0];
    const allPos = [], allIdx = [];
    let vc = 0;
    for (const gi of outerGeoIndices) {
      if (gi >= clippedGeos.length) continue;
      const strip = clipGeo(clippedGeos[gi], invertedPlanes);
      if (!strip) continue;
      const pos = strip.attributes.position;
      const idx = strip.index;
      if (!pos || !idx) continue;
      for (let i = 0; i < pos.count; i++) allPos.push(pos.getX(i), pos.getY(i), pos.getZ(i));
      for (let i = 0; i < idx.count; i++) allIdx.push(idx.getX(i) + vc);
      vc += pos.count;
    }

    if (allPos.length < 9) return null;

    // Filter: keep only triangles on the same side as point A (not the opposite side)
    if (!slitClickPoint) {
      // No click point — return unfiltered strip
      const g = new BufferGeometry();
      g.setAttribute('position', new Float32BufferAttribute(new Float32Array(allPos), 3));
      g.setIndex(allIdx);
      g.computeVertexNormals();
      g.computeBoundingSphere();
      return g;
    }
    const axisDir = slitAxis ? slitAxis.dir : new Vector3(0, 1, 0);
    const center = slitAxis ? slitAxis.center : new Vector3();
    const toA = new Vector3().subVectors(slitClickPoint, center);
    toA.sub(axisDir.clone().multiplyScalar(toA.dot(axisDir)));
    const radialA = toA.normalize(); // radial direction toward A

    const filteredPos = [], filteredIdx = [];
    let fvc = 0;
    for (let i = 0; i < allIdx.length; i += 3) {
      const a = allIdx[i], b = allIdx[i+1], c = allIdx[i+2];
      // Centroid
      const cx = (allPos[a*3] + allPos[b*3] + allPos[c*3]) / 3;
      const cy = (allPos[a*3+1] + allPos[b*3+1] + allPos[c*3+1]) / 3;
      const cz = (allPos[a*3+2] + allPos[b*3+2] + allPos[c*3+2]) / 3;
      // Radial from axis to centroid
      const toC = new Vector3(cx - center.x, cy - center.y, cz - center.z);
      toC.sub(axisDir.clone().multiplyScalar(toC.dot(axisDir)));
      // Keep if on the same side as A (dot product > 0)
      if (toC.dot(radialA) > 0) {
        filteredPos.push(allPos[a*3], allPos[a*3+1], allPos[a*3+2]);
        filteredPos.push(allPos[b*3], allPos[b*3+1], allPos[b*3+2]);
        filteredPos.push(allPos[c*3], allPos[c*3+1], allPos[c*3+2]);
        filteredIdx.push(fvc, fvc+1, fvc+2);
        fvc += 3;
      }
    }

    if (filteredPos.length < 9) return null;
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(new Float32Array(filteredPos), 3));
    g.setIndex(filteredIdx);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slitMarkerRotation, slitMarkerWidth, slitTilt, clippedGeos, model]);

  if (clippedGeos.length === 0) return null;

  return (
    <group userData={{ isShellGroup: true }}>
      {clippedGeos.map((geo, i) => (
        <mesh key={`shell-${i}`}>
          <primitive object={geo} attach="geometry" />
          <meshPhongMaterial {...matProps} />
        </mesh>
      ))}
      {capGeos.map((geo, i) => (
        <mesh key={`cap-${i}`}>
          <primitive object={geo} attach="geometry" />
          <meshPhongMaterial {...matProps} />
        </mesh>
      ))}
      {/* Closures (velcro strap holders) on both sides of the slit.
          Use the geometry's UUID as the key so that when closureCount changes
          (or any other recompute), React fully unmounts the stale Mesh+primitive
          nodes — otherwise <primitive> can keep dangling references to the
          previous BufferGeometry which leaves "ghost" connectors visible. */}
      {closureGeos.map((geo, i) => {
        const viz = geo.userData && geo.userData.gapViz;
        return (
          <React.Fragment key={geo.uuid}>
            <mesh>
              <primitive object={geo} attach="geometry" />
              <meshPhongMaterial color="#ff6600" emissive="#441100" side={DoubleSide} />
            </mesh>
            <mesh>
              <primitive object={geo} attach="geometry" />
              <meshBasicMaterial color="#000000" wireframe side={DoubleSide} />
            </mesh>
            {viz && (
              <>
                <mesh renderOrder={999}>
                  <primitive object={viz.outerDisk} attach="geometry" />
                  <meshBasicMaterial color="#ff3366" side={DoubleSide} transparent opacity={0.75} depthTest={false} />
                </mesh>
                <mesh renderOrder={999}>
                  <primitive object={viz.buckleDisk} attach="geometry" />
                  <meshBasicMaterial color="#22dd66" side={DoubleSide} transparent opacity={0.75} depthTest={false} />
                </mesh>
                {viz.verifyDisk && (
                  <mesh renderOrder={1001}>
                    <primitive object={viz.verifyDisk} attach="geometry" />
                    <meshBasicMaterial color="#3399ff" side={DoubleSide} transparent opacity={0.95} depthTest={false} />
                  </mesh>
                )}
                {viz.debugBar1 && (
                  <mesh renderOrder={1002}>
                    <primitive object={viz.debugBar1} attach="geometry" />
                    <meshBasicMaterial color="#ffaa00" side={DoubleSide} transparent opacity={0.9} depthTest={false} />
                  </mesh>
                )}
                {viz.debugY5 && (
                  <mesh renderOrder={1002}>
                    <primitive object={viz.debugY5} attach="geometry" />
                    <meshBasicMaterial color="#ff00ff" side={DoubleSide} transparent opacity={0.9} depthTest={false} />
                  </mesh>
                )}
                {viz.debugBar3 && (
                  <mesh renderOrder={1002}>
                    <primitive object={viz.debugBar3} attach="geometry" />
                    <meshBasicMaterial color="#ffff00" side={DoubleSide} transparent opacity={0.9} depthTest={false} />
                  </mesh>
                )}
                <line renderOrder={1000}>
                  <primitive object={viz.lineGeo} attach="geometry" />
                  <lineBasicMaterial color="#ffff00" linewidth={3} depthTest={false} />
                </line>
              </>
            )}
          </React.Fragment>
        );
      })}

      {/* Hole pattern preview — all marker lines rendered as thin tubes that
          follow the orthese outer surface (offset outward by outerOffset+0.5
          along the radial direction). Tubes are smaller in diameter than the
          orthese boundary cylinders. Standard depth-test means each marker
          is only visible on the side of the orthese facing the camera. */}
      {(() => {
        if (!holePattern || !slitAxis) return null;
        const offsetCenter = slitAxis.center;
        const offsetDir = slitAxis.dir.clone().normalize();
        const offsetDist = outerOffset + 0.5;
        // Hole tube radius: noticeably smaller than the orthese boundary tubes
        // (which are ~modelBounds.sizeX * 0.012 ≈ 1.2 mm on a typical arm).
        const tubeR = 0.35;
        const dotR = 0.7;
        return (
          <>
            {holePattern.centers.map((h) => (
              <MarkerDot
                key={`hole-${holesPerRow}-${holeSpacing}-${h.point.x.toFixed(2)}-${h.point.y.toFixed(2)}-${h.point.z.toFixed(2)}`}
                point={h.point}
                radius={dotR}
                color="#00e5ff"
                offsetCenter={offsetCenter}
                offsetDir={offsetDir}
                offsetDist={offsetDist}
                renderOrder={1502}
              />
            ))}
            {holePattern.ribs && holePattern.ribs.map((ribPts, ri) => (
              ribPts.length > 1 ? (
                <MarkerTube
                  key={`rib-${holesPerRow}-${holeSpacing}-${ri}-${ribPts.length}-${ribPts[0].x.toFixed(2)}`}
                  points={ribPts}
                  radius={tubeR}
                  color="#22dd66"
                  closed={false}
                  offsetCenter={offsetCenter}
                  offsetDir={offsetDir}
                  offsetDist={offsetDist}
                  renderOrder={1499}
                />
              ) : null
            ))}
            {holePattern.allowedRegions && holePattern.allowedRegions.map((region, ai) => (
              region.length > 1 ? (
                <MarkerTube
                  key={`region-${holesPerRow}-${holeSpacing}-${ai}-${region.length}-${region[0].x.toFixed(2)}`}
                  points={region}
                  radius={tubeR * 0.85}
                  color="#2196f3"
                  closed={false}
                  opacity={0.85}
                  offsetCenter={offsetCenter}
                  offsetDir={offsetDir}
                  offsetDist={offsetDist}
                  renderOrder={1498}
                />
              ) : null
            ))}
            {holePattern.polygons && holePattern.polygons.map((poly, pi) => (
              <MarkerTube
                key={`holepoly-${holesPerRow}-${holeSpacing}-${pi}-${poly[0].x.toFixed(2)}-${poly[0].y.toFixed(2)}-${poly[0].z.toFixed(2)}`}
                points={poly}
                radius={tubeR}
                color="#ffaa00"
                closed={false}
                offsetCenter={offsetCenter}
                offsetDir={offsetDir}
                offsetDist={offsetDist}
                renderOrder={1501}
              />
            ))}
          </>
        );
      })()}

      {/* Slit click markers: A=orange (position), B=purple (tilt) */}
      {slitClickPoint && (() => {
        const sz = model ? Math.max(new Box3().setFromObject(model).max.x - new Box3().setFromObject(model).min.x) * 0.015 : 1;
        return (
          <>
            <mesh position={[slitClickPoint.x, slitClickPoint.y, slitClickPoint.z]}>
              <sphereGeometry args={[sz, 16, 16]} />
              <meshStandardMaterial color="#f97316" emissive="#f97316" emissiveIntensity={0.5} />
            </mesh>
            {slitClickPoint2 && (
              <mesh position={[slitClickPoint2.x, slitClickPoint2.y, slitClickPoint2.z]}>
                <sphereGeometry args={[sz, 16, 16]} />
                <meshStandardMaterial color="#a855f7" emissive="#a855f7" emissiveIntensity={0.5} />
              </mesh>
            )}
          </>
        );
      })()}
      {/* Slit marker zone (red highlight on shell, only in Step 4) */}
      {slitMarkerGeo && (
        <mesh>
          <primitive object={slitMarkerGeo} attach="geometry" />
          <meshPhongMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.3} side={DoubleSide} transparent opacity={0.8} depthWrite={false} polygonOffset polygonOffsetFactor={-1} />
        </mesh>
      )}
    </group>
  );
});
ShellPreview.displayName = 'ShellPreview';

// ============================================================
// Main OrthesisViewer
// ============================================================
const OrthesisViewer = forwardRef(({
  file,
  fileType,
  textureFile,
  upperCutY,
  lowerCutY,
  upperTiltX = 0,
  upperTiltZ = 0,
  lowerTiltX = 0,
  lowerTiltZ = 0,
  upperPoint,
  upperNormal,
  lowerPoint,
  lowerNormal,
  thumbPoint,
  thumbNormal,
  thumbEnabled,
  thumbMode = 'free', // 'free' | 'fixed'
  thumbBase,  // orange marker - thumb base click point
  thumbTip,   // purple marker - thumb tip click point
  upperClickPoint,
  lowerClickPoint,
  onModelLoaded,
  onRingClick,
  onAlignClick,
  clickMode = null, // 'upper' | 'lower' | 'thumb' | 'align1' | 'align2' | null
  alignPoint1,
  alignPoint2,
  showRings = true,
  showShell = false,
  hideScan = false,
  skinOffset = 0.3,
  wallThickness = 2.5,
  slitRotation = null,
  slitWidth = null,
  slitTilt = null,
  slitMarkerRotation = null,
  slitMarkerWidth = null,
  slitClickPoint = null,
  slitClickPoint2 = null,
  closureCount = 0,
  holePatternEnabled = false,
  holesPerRow = 5,
  holeSpacing = 15,
  holePatternApplied = false,
  onCutProgress, // (progress: 0–100) — called from the CSG cut effect
  onPlanProgress, // (progress: 0–100) — called from the hole planning effect
}, ref) => {
  const { scene, camera, gl } = useThree();
  const modelRef = useRef(null);
  const controls = useRef();
  const [modelBounds, setModelBounds] = useState(null);
  const onModelLoadedRef = useRef(onModelLoaded);
  const onRingClickRef = useRef(onRingClick);
  const onAlignClickRef = useRef(onAlignClick);

  useEffect(() => { onModelLoadedRef.current = onModelLoaded; }, [onModelLoaded]);
  useEffect(() => { onRingClickRef.current = onRingClick; }, [onRingClick]);
  useEffect(() => { onAlignClickRef.current = onAlignClick; }, [onAlignClick]);

  const modelMaterial = useRef(new MeshPhongMaterial({ color: 0xf5e6d0, side: DoubleSide }));

  // Load velcro connector STL once
  const [velcroGeo, setVelcroGeo] = useState(null);
  useEffect(() => {
    new STLLoader().load('/verbinder_v2.stl', (geometry) => {
      // Center geometry at origin and store its bounding box for sizing
      geometry.computeBoundingBox();
      const bb = geometry.boundingBox;
      const center = new Vector3();
      bb.getCenter(center);
      geometry.translate(-center.x, -center.y, -center.z);
      geometry.computeVertexNormals();
      setVelcroGeo(geometry);
    });
  }, []);

  const adjustCameraToFitModel = useCallback(() => {
    if (!modelRef.current) return;
    const box = new Box3().setFromObject(modelRef.current);
    const center = new Vector3();
    const size = new Vector3();
    box.getCenter(center);
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = camera.fov * (Math.PI / 180);
    const dist = maxDim / (2 * Math.tan(fov / 2)) * 1.5;
    camera.position.set(center.x + dist * 0.7, center.y + dist * 0.3, center.z + dist * 0.7);
    camera.lookAt(center);
    camera.updateProjectionMatrix();
    if (controls.current) {
      controls.current.target.copy(center);
      controls.current.update();
      controls.current.saveState();
    }
  }, [camera]);

  // Helper: apply a quaternion rotation to all mesh vertices around a pivot
  const applyRotation = (quat, pivot) => {
    modelRef.current.traverse((child) => {
      if (child.isMesh && child.geometry) {
        const posAttr = child.geometry.attributes.position;
        const v = new Vector3();
        const invMatrix = new Matrix4().copy(child.matrixWorld).invert();
        for (let i = 0; i < posAttr.count; i++) {
          v.fromBufferAttribute(posAttr, i);
          v.applyMatrix4(child.matrixWorld);
          v.sub(pivot);
          v.applyQuaternion(quat);
          v.add(pivot);
          v.applyMatrix4(invMatrix);
          posAttr.setXYZ(i, v.x, v.y, v.z);
        }
        posAttr.needsUpdate = true;
        child.geometry.computeBoundingBox();
        child.geometry.computeBoundingSphere();
      }
    });
  };

  // Compute PCA principal axis of the mesh (longest axis)
  const computePrincipalAxis = () => {
    const vertices = [];
    modelRef.current.traverse((child) => {
      if (!child.isMesh || !child.geometry) return;
      const posAttr = child.geometry.attributes.position;
      const v = new Vector3();
      // Sample every Nth vertex for performance
      const step = Math.max(1, Math.floor(posAttr.count / 2000));
      for (let i = 0; i < posAttr.count; i += step) {
        v.fromBufferAttribute(posAttr, i);
        v.applyMatrix4(child.matrixWorld);
        vertices.push([v.x, v.y, v.z]);
      }
    });

    if (vertices.length < 10) return new Vector3(0, 1, 0);

    // Compute centroid
    let cx = 0, cy = 0, cz = 0;
    for (const [x, y, z] of vertices) { cx += x; cy += y; cz += z; }
    cx /= vertices.length; cy /= vertices.length; cz /= vertices.length;

    // Covariance matrix (3x3)
    let cxx = 0, cxy = 0, cxz = 0, cyy = 0, cyz = 0, czz = 0;
    for (const [x, y, z] of vertices) {
      const dx = x - cx, dy = y - cy, dz = z - cz;
      cxx += dx * dx; cxy += dx * dy; cxz += dx * dz;
      cyy += dy * dy; cyz += dy * dz; czz += dz * dz;
    }

    // Power iteration to find largest eigenvector
    let ev = new Vector3(0.5, 0.8, 0.3).normalize();
    for (let iter = 0; iter < 50; iter++) {
      const nx = cxx * ev.x + cxy * ev.y + cxz * ev.z;
      const ny = cxy * ev.x + cyy * ev.y + cyz * ev.z;
      const nz = cxz * ev.x + cyz * ev.y + czz * ev.z;
      ev.set(nx, ny, nz).normalize();
    }

    return ev;
  };

  // Align model: gross rotation from user points, then PCA fine-tuning
  // p1 = Handgelenk (top), p2 = Ellenbogen (bottom)
  // Axis goes from p2→p1 (bottom to top = Y-up)
  // Returns { p1: rotatedWrist, p2: rotatedElbow } so parent can update markers
  const alignModelToAxis = useCallback((p1, p2) => {
    if (!modelRef.current) return null;

    const wrist = p1 instanceof Vector3 ? p1.clone() : new Vector3(p1.x, p1.y, p1.z);
    const elbow = p2 instanceof Vector3 ? p2.clone() : new Vector3(p2.x, p2.y, p2.z);

    // Helper: rotate a point with the same transform applied to the mesh
    const rotatePoint = (point, quat, pivot) => {
      return point.clone().sub(pivot).applyQuaternion(quat).add(pivot);
    };

    // Step 1: Coarse rotation — elbow→wrist axis to Y-up
    const userAxis = new Vector3().subVectors(wrist, elbow).normalize();
    const targetAxis = new Vector3(0, 1, 0);
    const coarseQuat = new Quaternion().setFromUnitVectors(userAxis, targetAxis);
    const pivot = new Vector3().addVectors(elbow, wrist).multiplyScalar(0.5);

    applyRotation(coarseQuat, pivot);
    wrist.copy(rotatePoint(wrist, coarseQuat, pivot));
    elbow.copy(rotatePoint(elbow, coarseQuat, pivot));

    // Step 2: PCA fine-tuning — find actual principal axis after coarse rotation
    const principalAxis = computePrincipalAxis();

    // Ensure it points upward (same direction as Y+)
    if (principalAxis.y < 0) principalAxis.negate();

    // Only fine-tune if the deviation is small (< 30 degrees) — otherwise trust the user
    const angleDiff = principalAxis.angleTo(targetAxis);
    if (angleDiff < Math.PI / 6) {
      const fineQuat = new Quaternion().setFromUnitVectors(principalAxis, targetAxis);
      const box = new Box3().setFromObject(modelRef.current);
      const fineCenter = new Vector3();
      box.getCenter(fineCenter);
      applyRotation(fineQuat, fineCenter);
      wrist.copy(rotatePoint(wrist, fineQuat, fineCenter));
      elbow.copy(rotatePoint(elbow, fineQuat, fineCenter));
    }

    // Recalculate bounds and refit camera
    const box = new Box3().setFromObject(modelRef.current);
    const size = new Vector3();
    box.getSize(size);
    const newBounds = {
      minX: box.min.x, maxX: box.max.x,
      minY: box.min.y, maxY: box.max.y,
      minZ: box.min.z, maxZ: box.max.z,
      sizeX: size.x, sizeY: size.y, sizeZ: size.z,
      centerX: (box.min.x + box.max.x) / 2,
      centerY: (box.min.y + box.max.y) / 2,
      centerZ: (box.min.z + box.max.z) / 2,
    };
    setModelBounds(newBounds);
    setTimeout(() => {
      adjustCameraToFitModel();
      if (onModelLoadedRef.current) onModelLoadedRef.current(newBounds);
    }, 50);

    return { p1: { x: wrist.x, y: wrist.y, z: wrist.z }, p2: { x: elbow.x, y: elbow.y, z: elbow.z } };
  }, [adjustCameraToFitModel]);

  const shellGeosRef = useRef(null);

  useImperativeHandle(ref, () => ({
    resetCamera() { adjustCameraToFitModel(); },
    getModelBounds() { return modelBounds; },
    getModelScene() { return modelRef.current; },
    alignModel(p1, p2) { return alignModelToAxis(p1, p2); },
    getShellGeometries() { return shellGeosRef.current; },
    setShellGeometries(geos) { shellGeosRef.current = geos; },
  }));

  // Setup controls and lights
  useEffect(() => {
    controls.current = new OrbitControls(camera, gl.domElement);
    controls.current.enableDamping = true;
    controls.current.dampingFactor = 0.08;
    controls.current.rotateSpeed = 0.8;

    // Enable material clipping planes
    gl.localClippingEnabled = true;

    const ambientLight = new AmbientLight(0xffffff, 0.6);
    const dirLight1 = new DirectionalLight(0xffffff, 0.8);
    dirLight1.position.set(5, 10, 7);
    const dirLight2 = new DirectionalLight(0xffffff, 0.4);
    dirLight2.position.set(-5, -5, -5);
    scene.add(ambientLight, dirLight1, dirLight2);
    scene.background = new Color(0x1a1a2e);

    return () => {
      controls.current?.dispose();
      scene.remove(ambientLight, dirLight1, dirLight2);
    };
  }, [camera, gl, scene]);

  useFrame(() => { controls.current?.update(); });

  // Hide/show original scan model
  useEffect(() => {
    if (modelRef.current) {
      modelRef.current.visible = !hideScan;
    }
  }, [hideScan]);

  // Click handler — works for all ring types
  useEffect(() => {
    if (!clickMode || !modelRef.current) return;

    const raycaster = new Raycaster();
    const mouse = new Vector2();

    const handleClick = (event) => {
      const rect = gl.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const meshes = [];
      modelRef.current.traverse((child) => { if (child.isMesh) meshes.push(child); });
      const intersects = raycaster.intersectObjects(meshes);

      if (intersects.length > 0) {
        const hit = intersects[0];
        const point = hit.point.clone();

        // Camera view direction — the ring plane normal should be roughly
        // aligned with the view direction so the ring wraps AROUND the object
        // as seen from the current camera angle (not edge-on)
        const viewDir = new Vector3();
        camera.getWorldDirection(viewDir);

        if (clickMode === 'align1' || clickMode === 'align2') {
            if (onAlignClickRef.current) {
              onAlignClickRef.current(clickMode, point);
            }
            return;
          }

          if (clickMode === 'slitPlace' || clickMode === 'slitPlace2') {
            // Raycast against shell meshes (not scan) for correct surface point
            const shellMeshes = [];
            scene.traverse((child) => {
              if (child.isMesh && child.parent?.userData?.isShellGroup) shellMeshes.push(child);
            });

            const shellHits = shellMeshes.length > 0
              ? raycaster.intersectObjects(shellMeshes)
              : [];
            const shellPoint = shellHits.length > 0 ? shellHits[0].point.clone() : point;

            if (onRingClickRef.current) {
              onRingClickRef.current(clickMode, shellPoint, null);
            }
            return;
          }

          if (onRingClickRef.current) {
          if (clickMode === 'thumbBase' || clickMode === 'thumbTip' || clickMode === 'thumbRing') {
            // Thumb axis/ring points: just pass the surface click point
            onRingClickRef.current(clickMode, point, null);
          } else {
            // UPPER/LOWER: plane goes through the exact click point on the surface
            // Normal starts as Y-up, small tilt search for best fit
            const initialNormal = new Vector3(0, 1, 0);
            const result = findMinCircumferencePlane(modelRef.current, point, initialNormal, 0.3, 4);
            onRingClickRef.current(clickMode, point, result.normal);
          }
        }
      }
    };

    gl.domElement.addEventListener('click', handleClick);
    gl.domElement.style.cursor = 'crosshair';
    return () => {
      gl.domElement.removeEventListener('click', handleClick);
      gl.domElement.style.cursor = 'move';
    };
  }, [clickMode, camera, gl, modelBounds]);

  // Process loaded model
  const processLoadedModel = useCallback((model) => {
    modelRef.current = model;
    scene.add(model);
    const box = new Box3().setFromObject(model);
    const size = new Vector3();
    box.getSize(size);
    const bounds = {
      minX: box.min.x, maxX: box.max.x,
      minY: box.min.y, maxY: box.max.y,
      minZ: box.min.z, maxZ: box.max.z,
      sizeX: size.x, sizeY: size.y, sizeZ: size.z,
      centerX: (box.min.x + box.max.x) / 2,
      centerY: (box.min.y + box.max.y) / 2,
      centerZ: (box.min.z + box.max.z) / 2,
    };
    setModelBounds(bounds);
    setTimeout(() => { if (onModelLoadedRef.current) onModelLoadedRef.current(bounds); }, 0);
    setTimeout(() => { adjustCameraToFitModel(); }, 50);
  }, [scene, adjustCameraToFitModel]);

  // Load model
  useEffect(() => {
    if (!file) return;
    if (modelRef.current) { scene.remove(modelRef.current); modelRef.current = null; setModelBounds(null); }
    const url = URL.createObjectURL(file);
    let textureUrl = null;
    let disposed = false;
    const getMaterial = (texture) => texture ? new MeshPhongMaterial({ map: texture, side: DoubleSide }) : modelMaterial.current;

    if (fileType === 'stl') {
      new STLLoader().load(url, (geometry) => {
        if (disposed) return;
        processLoadedModel(new Mesh(geometry, getMaterial(null)));
        URL.revokeObjectURL(url);
      });
    } else {
      const loadObj = (material) => {
        new OBJLoader().load(url, (object) => {
          if (disposed) return;
          object.traverse((child) => { if (child.isMesh) child.material = material; });
          processLoadedModel(object);
          URL.revokeObjectURL(url);
        });
      };
      if (textureFile) {
        textureUrl = URL.createObjectURL(textureFile);
        new TextureLoader().load(textureUrl, (texture) => {
          if (disposed) return;
          loadObj(getMaterial(texture));
          URL.revokeObjectURL(textureUrl);
        });
      } else { loadObj(getMaterial(null)); }
    }
    return () => { disposed = true; URL.revokeObjectURL(url); if (textureUrl) URL.revokeObjectURL(textureUrl); };
  }, [file, fileType, textureFile, scene, processLoadedModel]);

  const planeSize = useMemo(() => modelBounds ? Math.max(modelBounds.sizeX, modelBounds.sizeZ) * 1.8 : 1, [modelBounds]);
  const center = useMemo(() => modelBounds ? { x: modelBounds.centerX, y: modelBounds.centerY, z: modelBounds.centerZ } : { x: 0, y: 0, z: 0 }, [modelBounds]);

  // Compute centered thumb axis: find cross-section center at base and tip
  // Centered ARM axis — same logic as thumb axis
  const centeredArmAxis = useMemo(() => {
    if (!alignPoint1 || !alignPoint2 || !modelRef.current) return null;

    const ap1 = alignPoint1 instanceof Vector3 ? alignPoint1 : new Vector3(alignPoint1.x, alignPoint1.y, alignPoint1.z);
    const ap2 = alignPoint2 instanceof Vector3 ? alignPoint2 : new Vector3(alignPoint2.x, alignPoint2.y, alignPoint2.z);

    const roughDir = new Vector3().subVectors(ap1, ap2).normalize(); // wrist→elbow = up

    const findCenter = (point) => {
      const plane = makePlane(point, roughDir);
      const loop = findLoopNearPoint(modelRef.current, plane, point);
      if (loop.length < 3) return point.clone();
      const cx = loop.reduce((s, p) => s + p.x, 0) / loop.length;
      const cy = loop.reduce((s, p) => s + p.y, 0) / loop.length;
      const cz = loop.reduce((s, p) => s + p.z, 0) / loop.length;
      return new Vector3(cx, cy, cz);
    };

    return { wrist: findCenter(ap1), elbow: findCenter(ap2) };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alignPoint1?.x, alignPoint1?.y, alignPoint1?.z, alignPoint2?.x, alignPoint2?.y, alignPoint2?.z, modelBounds]);

  const centeredThumbAxis = useMemo(() => {
    if (!thumbBase || !thumbTip || !modelRef.current) return null;

    const tb = thumbBase instanceof Vector3 ? thumbBase : new Vector3(thumbBase.x, thumbBase.y, thumbBase.z);
    const tt = thumbTip instanceof Vector3 ? thumbTip : new Vector3(thumbTip.x, thumbTip.y, thumbTip.z);

    // Rough axis direction from surface points
    const roughDir = new Vector3().subVectors(tt, tb).normalize();

    // For each endpoint, compute cross-section perpendicular to rough axis,
    // then find the centroid of that cross-section
    const findCenter = (point) => {
      const plane = makePlane(point, roughDir);
      const pts = computeCrossSection(modelRef.current, plane);
      if (pts.length < 3) return point.clone();

      // Find the loop closest to the click point
      const allLoops = [];
      // computeCrossSection returns ordered points of the biggest loop
      // Use findLoopNearPoint for better results
      const loop = findLoopNearPoint(modelRef.current, plane, point);
      if (loop.length < 3) return point.clone();

      // Compute centroid of the loop
      const cx = loop.reduce((s, p) => s + p.x, 0) / loop.length;
      const cy = loop.reduce((s, p) => s + p.y, 0) / loop.length;
      const cz = loop.reduce((s, p) => s + p.z, 0) / loop.length;
      return new Vector3(cx, cy, cz);
    };

    const centerBase = findCenter(tb);
    const centerTip = findCenter(tt);

    return { base: centerBase, tip: centerTip };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbBase?.x, thumbBase?.y, thumbBase?.z, thumbTip?.x, thumbTip?.y, thumbTip?.z]);

  if (!modelBounds) return null;

  const showUpperCut = upperCutY != null && upperCutY >= modelBounds.minY && upperCutY <= modelBounds.maxY;
  const showLowerCut = lowerCutY != null && lowerCutY >= modelBounds.minY && lowerCutY <= modelBounds.maxY;
  const showThumb = thumbEnabled && thumbPoint && thumbNormal;
  const tubeR = modelBounds.sizeX * 0.012;
  const markerSize = modelBounds.sizeX * 0.02;

  return (
    <>
      {/* Cut/thumb rings — only shown when showRings is true (not during alignment) */}
      {showRings && showUpperCut && (() => {
        const basePoint = upperPoint || { x: center.x, y: upperCutY, z: center.z };
        // If slider moved Y, update the point's Y
        const pt = { x: basePoint.x, y: upperCutY, z: basePoint.z };
        const adj = adjustPointNormal(pt, upperNormal || { x: 0, y: 1, z: 0 }, 0, upperTiltX, upperTiltZ);
        return <CrossSectionRing model={modelRef.current} planePoint={adj.point} planeNormal={adj.normal} color="#ef4444" tubeRadius={tubeR} />;
      })()}

      {/* Lower cut ring — through click point or center-Y fallback */}
      {showRings && showLowerCut && (() => {
        const basePoint = lowerPoint || { x: center.x, y: lowerCutY, z: center.z };
        const pt = { x: basePoint.x, y: lowerCutY, z: basePoint.z };
        const adj = adjustPointNormal(pt, lowerNormal || { x: 0, y: 1, z: 0 }, 0, lowerTiltX, lowerTiltZ);
        return <CrossSectionRing model={modelRef.current} planePoint={adj.point} planeNormal={adj.normal} color="#3b82f6" tubeRadius={tubeR} />;
      })()}

      {/* Thumb ring — auto-optimized normal for smallest circumference */}
      {showRings && showThumb && <ThumbRingOptimized model={modelRef.current} thumbPoint={thumbPoint} thumbNormal={thumbNormal} tubeRadius={tubeR * 1.2} />}

      {/* Click point markers — only during ring steps */}
      {showRings && upperClickPoint && (
        <mesh position={[upperClickPoint.x, upperClickPoint.y, upperClickPoint.z]}>
          <sphereGeometry args={[markerSize, 16, 16]} />
          <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.5} />
        </mesh>
      )}
      {showRings && lowerClickPoint && (
        <mesh position={[lowerClickPoint.x, lowerClickPoint.y, lowerClickPoint.z]}>
          <sphereGeometry args={[markerSize, 16, 16]} />
          <meshStandardMaterial color="#3b82f6" emissive="#3b82f6" emissiveIntensity={0.5} />
        </mesh>
      )}
      {/* Thumb surface click markers (orange=base, purple=tip) */}
      {thumbBase && (
        <mesh position={[thumbBase.x, thumbBase.y, thumbBase.z]}>
          <sphereGeometry args={[markerSize * 1.0, 16, 16]} />
          <meshStandardMaterial color="#f97316" emissive="#f97316" emissiveIntensity={0.5} />
        </mesh>
      )}
      {thumbTip && (
        <mesh position={[thumbTip.x, thumbTip.y, thumbTip.z]}>
          <sphereGeometry args={[markerSize * 1.0, 16, 16]} />
          <meshStandardMaterial color="#a855f7" emissive="#a855f7" emissiveIntensity={0.5} />
        </mesh>
      )}
      {/* Centered thumb axis — computed center points + extended line */}
      {centeredThumbAxis && (() => {
        const { base, tip } = centeredThumbAxis;
        const dx = tip.x - base.x;
        const dy = tip.y - base.y;
        const dz = tip.z - base.z;
        const len = Math.sqrt(dx*dx + dy*dy + dz*dz);
        if (len < 0.001) return null;
        const ext = 5.0; // extend far beyond both ends
        const p1x = base.x - dx * ext, p1y = base.y - dy * ext, p1z = base.z - dz * ext;
        const p2x = tip.x + dx * ext, p2y = tip.y + dy * ext, p2z = tip.z + dz * ext;
        return (
          <>
            {/* Center points (small green dots) */}
            <mesh position={[base.x, base.y, base.z]}>
              <sphereGeometry args={[markerSize * 0.6, 12, 12]} />
              <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={0.8} />
            </mesh>
            <mesh position={[tip.x, tip.y, tip.z]}>
              <sphereGeometry args={[markerSize * 0.6, 12, 12]} />
              <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={0.8} />
            </mesh>
            {/* Extended axis line through centers */}
            <line>
              <bufferGeometry>
                <bufferAttribute
                  attach="attributes-position"
                  array={new Float32Array([p1x, p1y, p1z, p2x, p2y, p2z])}
                  count={2}
                  itemSize={3}
                />
              </bufferGeometry>
              <lineBasicMaterial color="#22c55e" transparent opacity={0.4} />
            </line>
          </>
        );
      })()}

      {/* Shell preview — offset surface clipped at cut planes */}
      {showShell && modelRef.current && (
        <ShellPreview
          model={modelRef.current}
          upperCutY={upperCutY}
          lowerCutY={lowerCutY}
          upperPoint={upperPoint}
          upperNormal={upperNormal}
          upperTiltX={upperTiltX}
          upperTiltZ={upperTiltZ}
          lowerPoint={lowerPoint}
          lowerNormal={lowerNormal}
          lowerTiltX={lowerTiltX}
          lowerTiltZ={lowerTiltZ}
          skinOffset={skinOffset}
          wallThickness={wallThickness}
          thumbPoint={thumbEnabled ? thumbPoint : null}
          thumbNormal={thumbEnabled ? thumbNormal : null}
          thumbMode={thumbMode}
          slitRotation={slitRotation}
          slitWidth={slitWidth}
          slitTilt={slitTilt}
          slitMarkerRotation={slitMarkerRotation}
          slitMarkerWidth={slitMarkerWidth}
          slitClickPoint={slitClickPoint}
          slitClickPoint2={slitClickPoint2}
          closureCount={closureCount}
          holePatternEnabled={holePatternEnabled}
          holesPerRow={holesPerRow}
          holeSpacing={holeSpacing}
          holePatternApplied={holePatternApplied}
          onCutProgress={onCutProgress}
          onPlanProgress={onPlanProgress}
          velcroGeo={velcroGeo}
          armAxisCenter={centeredArmAxis ? new Vector3().addVectors(centeredArmAxis.wrist, centeredArmAxis.elbow).multiplyScalar(0.5) : null}
          armAxisDir={centeredArmAxis ? new Vector3().subVectors(centeredArmAxis.wrist, centeredArmAxis.elbow).normalize() : null}
          onGeosReady={(geos) => { shellGeosRef.current = geos; }}
        />
      )}

      {/* Centered arm axis — computed center points + extended line (yellow) */}
      {centeredArmAxis && (() => {
        const { wrist, elbow } = centeredArmAxis;
        const dx = wrist.x - elbow.x;
        const dy = wrist.y - elbow.y;
        const dz = wrist.z - elbow.z;
        const len = Math.sqrt(dx*dx + dy*dy + dz*dz);
        if (len < 0.001) return null;
        const ext = 3.0;
        const p1x = elbow.x - dx * ext, p1y = elbow.y - dy * ext, p1z = elbow.z - dz * ext;
        const p2x = wrist.x + dx * ext, p2y = wrist.y + dy * ext, p2z = wrist.z + dz * ext;
        return (
          <>
            <mesh position={[wrist.x, wrist.y, wrist.z]}>
              <sphereGeometry args={[markerSize * 0.6, 12, 12]} />
              <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.8} />
            </mesh>
            <mesh position={[elbow.x, elbow.y, elbow.z]}>
              <sphereGeometry args={[markerSize * 0.6, 12, 12]} />
              <meshStandardMaterial color="#8b5cf6" emissive="#8b5cf6" emissiveIntensity={0.8} />
            </mesh>
            <line key={`arm-ax-${p1x.toFixed(2)}-${p1y.toFixed(2)}-${p2x.toFixed(2)}-${p2y.toFixed(2)}`}>
              <bufferGeometry>
                <bufferAttribute
                  attach="attributes-position"
                  array={new Float32Array([p1x, p1y, p1z, p2x, p2y, p2z])}
                  count={2}
                  itemSize={3}
                />
              </bufferGeometry>
              <lineBasicMaterial color="#f59e0b" transparent opacity={0.4} />
            </line>
          </>
        );
      })()}

      {/* Alignment markers (surface click points) */}
      {alignPoint1 && (
        <mesh position={[alignPoint1.x, alignPoint1.y, alignPoint1.z]}>
          <sphereGeometry args={[markerSize * 1.5, 16, 16]} />
          <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.8} />
        </mesh>
      )}
      {alignPoint2 && (
        <mesh position={[alignPoint2.x, alignPoint2.y, alignPoint2.z]}>
          <sphereGeometry args={[markerSize * 1.5, 16, 16]} />
          <meshStandardMaterial color="#8b5cf6" emissive="#8b5cf6" emissiveIntensity={0.8} />
        </mesh>
      )}
    </>
  );
});

OrthesisViewer.displayName = 'OrthesisViewer';
export default OrthesisViewer;
